// Home page — the workspace "/" board (DESIGN-WL-HOME-WIDGETS-1,
// tickets/reference/white-label-redesign/HOME_WIDGETS_SPEC.md).
//
// A page head (the greeting line) above ONE CSS grid of widgets: fixed square
// cells, four sizes (S 1×1, M 2×1, L 2×2, XL 4×1), 4 / 3 / 2 / 1 columns by the
// board's own width, flowing the saved ORDER — no free placement, no holes.
// Every widget, the page's own and every module / app / AI widget, renders
// through the one frame (components/HomeWidgetFrame.js): a hue plate, the
// title, one square button, the body. App shortcuts sit in the same grid as
// one-cell app tiles for now (decision H1 C; the App strip is held).
//
// The board paints its final arrangement on the first frame: the geometry is
// pure CSS (_injectWidgetStyles), the layout / hidden map resolve
// synchronously from the account record, the unconfirmed-write outbox and the
// device cache (_reconcileHomeBoard), and widgets still loading hold a
// skeleton in their saved cell. In edit mode (Customize) a widget is dragged
// to a new place in the order, resized with the S · M · L · XL picker, or
// hidden; every such decision persists at once through the durable prefs
// write (the outbox + single-flight PUT, _savePrefsPatch).
import { applyMetadata } from '@symbo.ls/helmet'
import media from '@symbo-ls/brand/designSystem/media.js'
import {
  homeWidgetHue,
  homeWidgetSurfaceHue,
  homeWidgetColorsKey,
  readHomeWidgetColors,
  homeActivityMetric
} from '../functions/index.js'
// Bare shared-library import, not a deep workspacePrefix.js subpath — see
// app.js's identical fix note (tickets/catalog-fixall.md P0).
import _wsShared from '@symbo-ls/workspace-shared'
import { shellStripWorkspacePrefix } from '../functions/shellSharedDelegates.js'
// shared's registered findMe (the shell no longer re-registers it — see
// functions/index.js header).
const shellFindMe = (...args) => _wsShared.functions.findMe(...args)
// WS-GRID-PERSIST-1: the SYNCHRONOUS, per-tab workspace identity the home
// cache keys are scoped on. Pure regex parse of the path — no resolver, no
// await, and unlike the shared activeWorkspace pointer it cannot be
// contaminated by another tab.
const shellGetWorkspaceRawSegments = (...args) =>
  _wsShared.functions.getWorkspaceRawSegments(...args)
const shellGetActiveWorkspacePointer = (...args) =>
  _wsShared.functions.getActiveWorkspacePointer(...args)
const shellIsLoudDevRuntime = (...args) => _wsShared.functions.isLoudDevRuntime(...args)
const shellNavItemVisible = (...args) => _wsShared.functions.navItemVisible(...args)
const shellListOrgMembers = (...args) => _wsShared.functions.listOrgMembers(...args)
const invalidateOrgMembers = (...args) => _wsShared.functions.invalidateOrgMembers(...args)
// Local implementation, not a delegate off `_wsShared.pages` — see
// ./lazyPage.js's header (WS-SHARED-LAZYPAGE-HOLLOW-ROUTES-SWITCH-1).
import { lazySharedLib, lazySharedLibPage } from './lazyPage.js'
// The two poster-ink resolvers (components/HomePosterInk.js +
// HomePosterSoftInk.js). Imported as OBJECTS, exactly as HomePosterCaption
// reuses its sibling: the sites below override a child that a SHARED component
// already defined (EmptyProjectCard.Body.Title, the activity heatmap's Legend),
// where only a plain prop merges predictably — `extends` on such an override
// would race the component's own definition.
import { HomePosterInk } from '../components/HomePosterInk.js'
import { HomePosterSoftInk } from '../components/HomePosterSoftInk.js'
import { posterCardHue } from '../functions/posterCardHue.js'
// The board's geometry table, the v2 layout (order + size) and the frame's
// default-size rule — see HOME_WIDGETS_SPEC.md §1 / §6.
import {
  HOME_GRID_STEPS,
  HOME_GRID_CELL_CAP,
  HOME_BODY_LAYOUTS,
  homeWidgetSpan,
  homeBodyFit
} from '../functions/homeGridMetrics.js'
import {
  homeLayoutNormalize,
  homeLayoutFromLegacy,
  homeLayoutEnsure,
  homeLayoutMove,
  homeLayoutCardId,
  homeLayoutOrderBy,
  homeLayoutReplace,
  homeLayoutSetSize
} from '../functions/homeLayoutV2.js'
import {
  homeWidgetDefaultSize,
  homeWidgetIsApp,
  homeWidgetFrameSize,
  homeWidgetParams,
  homeWidgetRow
} from '../functions/homeWidgetFrame.js'
import {
  barSegments as _barSegments,
  PRIORITY_BUCKETS as _PRIORITY_BUCKETS,
  priorityMix as _priorityMix,
  workloadRows as _workloadRowsOf
} from '../functions/homeSummary.js'
import {
  customizeDismissDecision,
  CUSTOMIZE_EXIT_PATCH
} from '../functions/homeCustomizeDismiss.js'
import {
  resolveHomePanelSet,
  appDefaultWidgetIds,
  isWidgetRestorable
} from '../functions/homePanelSet.js'
import { persistableHiddenMap } from '../functions/homeHiddenPersist.js'
import { resolveSavedPrefsSource } from '../functions/homePrefsBootSource.js'
import { homeWidgetCatalog, homeDefaultVisibleWidgets } from '../functions/homeWidgetCatalog.js'
import { moduleWidgetGateAllows } from '../functions/moduleWidgetGate.js'
import {
  isAppModeWorkspace,
  appModeModuleRef,
  takeoverModuleRef,
  houseDefaultHiddenMap,
  panelSetHiddenMap
} from '../functions/homeDefaultWidgets.js'
import {
  aiWidgetsFor,
  aiWidgetIdsFor,
  aiWidgetOwnerLabel,
  setAiWidgetVisibility,
  deleteAiWidgetDef
} from '../functions/aiWidgets.js'
import { homePinnedHostRow } from '../functions/homePinnedItems.js'
import { installedAppRefs } from '../functions/workspaceConfigs.js'
import { untrackedState } from '../functions/untrackedState.js'
import {
  getModulePageDef,
  moduleImmersiveActive,
  moduleViewAsActive,
  moduleImmersiveLanding
} from '../functions/workspaceProjects.js'
import {
  rowToChannel,
  filterChannelsToActiveWorkspace,
  getActiveWorkspaceId
} from '@symbo-ls/workspace-chat/functions/chatUtils.js'

// Pages opened by the GridContainer widget actions into AppWindow.
// smbls' `extends:` resolves component NAMES, not route strings (see
// smbls/packages/element/src/extends.js:resolveExtend), so a literal
// `extends: '/chat'` returns null and the panel renders empty. We
// import the page modules directly and pass the resolved object as
// `extends:`, which `resolveExtend` accepts as-is (typeof === 'object').
//
// Hoisted here (ES `import` must live at module top level, but the block
// below that used to declare these alongside is now wrapped in a deferred
// factory — see _getAppWindowPages's own comment) — same imports, same
// names, just relocated so the factory body is pure function-scope code.
import { fetchTicketsForUser } from '@symbo-ls/workspace-tickets/functions/ticketLoaders.js'
import { saveTicket } from '@symbo-ls/workspace-tickets/functions/saveTicket.js'
import { createNote } from '@symbo-ls/workspace-documents/functions/createNote.js'
// FA001 — a sibling PAGE object is never imported into another page module:
// the emitted `_getAppWindowPages` would read a module-local binding that
// does not exist when the payload revives the function, and every
// `/announcements` + `/team-members` slot open threw a ReferenceError that
// DOMQL swallowed. Both routes are registered in this project's own
// `context.pages` (pages/index.js), so the AppWindow map reads them THROUGH
// the live context — see `_ctxPages` in the factory below.
// (WORKSPACE-SHELL-FRANK-GATE-BLOCKS-REPUBLISH-1)
import { _person } from '../functions/loadPrivateData.js'
import { fetchOrgNotifCount } from '../functions/notifications/fetchOrgNotifCount.js'
import { lazyPage } from './lazyPage.js'
// lazySharedLibPage is only exported from the canonical shared implementation
// (the local ./lazyPage.js re-exports lazyPage by name only). The home PageSlot
// resolves /documents + /documents/editor via _APP_WINDOW_PAGES (below) BEFORE
// context.pages, so these must ALSO merge the deferred documents context — a
// plain lazyPage would mount the page against an unregistered doc component set.
// Standalone sub-app pages opened in the AppWindow are lazy-loaded (dynamic
// import chunk) so their code stays out of the initial home bundle — see
// pages/lazyPage.js + LAZY_PAGE_PLAN.md. Same const names feed
// _APP_WINDOW_PAGES below unchanged.
// chat bundle-fold (2026-08-01): chat is now a lazy shared library — the home
// AppWindow PageSlot mount needs the context-fold-then-page-load shape, not a
// bare lazyPage (same conversion as tickets/meet below).
//
// The whole page-registry map is a MEMOIZED FACTORY, not module-scope
// consts — this module loads INSIDE the shared-barrel init cycle (same
// class as workspaceProjects.js/pageContext.js/functions/featureFlags.js's
// fix note), and building 19 lazySharedLib(Page) pages eagerly here — each
// one reading `_wsShared.pages.lazySharedLib(Page)` the instant its own
// `const` line runs — crashed the PRODUCTION BUILD ("Cannot read
// properties of undefined (reading 'lazySharedLibPage')", smbls push,
// tickets/execution-board.md §1). First call builds + caches the map;
// every call after returns the same cached object, so `_appPageFor`
// below stays referentially stable across renders.
let _appWindowPagesCache = null
const _getAppWindowPages = (root) => {
  if (_appWindowPagesCache) return _appWindowPagesCache

  // Shell pages this map exposes to the AppWindow slot, resolved from the
  // live context registry instead of a sibling import (FA001, see the note
  // beside the imports above).
  const _ctxPages = (root && root.context && root.context.pages) || {}

  const _chatPage = lazySharedLibPage(
    () => import('@symbo-ls/workspace-chat/context.js'),
    () => import('@symbo-ls/workspace-chat/pages/index.js').then((m) => m.chatHome),
    { title: (el) => el.call('pageTitle', 'workspace.page.chat') }
  )
  // Mail — the same lazy-shared-library shape as chat (the built-in email
  // client, architecture/MAIL.md): fold the mail context first, then mount
  // its home page in the AppWindow slot.
  const _mailPage = lazySharedLibPage(
    () => import('@symbo-ls/workspace-mail/context.js'),
    () => import('@symbo-ls/workspace-mail/pages/index.js').then((m) => m.mailHome),
    { title: (el) => el.call('pageTitle', 'workspace.page.mail') }
  )
  // Tickets is a deferred shared library (folded out of the eager sharedLibraries
  // merge in #16). Opening it in the home AppWindow PageSlot must fold its context
  // in FIRST — lazySharedLibPage, not lazyPage — else a cold AppWindow open (before
  // any direct /tickets router visit) mounts the board against an unregistered
  // tickets component set. Same _ticketsCtx the /tickets* router entries use.
  const _ticketsPage = lazySharedLibPage(
    () => import('@symbo-ls/workspace-tickets/context.js'),
    () => import('@symbo-ls/workspace-tickets/pages/index.js').then((m) => m.myView),
    { title: (el) => el.call('pageTitle', 'workspace.page.myTasks') }
  )
  // Direct import (not el.call) — same rationale as fetchTicketsForUser above:
  // the Received/Given quick-add must create a real ticket on a COLD home
  // load, before the deferred tickets shared library has necessarily folded
  // its functions barrel into context.functions.
  //
  // Same cold-boot rationale as saveTicket above — the Notes widget's quick-
  // new-note row must create a real note on a COLD home load too.
  // Calendar is a deferred shared library (folded out of the eager sharedLibraries
  // merge in #16). Same reasoning as _ticketsPage above — the home AppWindow open
  // must fold the calendar context first via lazySharedLibPage, not a plain
  // lazyPage that would mount CalMain/CalWeekView against an unregistered set.
  const _calendarPage = lazySharedLibPage(
    () => import('@symbo-ls/workspace-calendar/context.js'),
    () => import('@symbo-ls/workspace-calendar/pages/index.js').then((m) => m.calendarHome),
    { title: (el) => el.call('pageTitle', 'workspace.page.calendar') }
  )
  // Documents is a deferred shared library (out of the eager sharedLibraries
  // merge), so opening it in the home PageSlot must fold its context in first —
  // lazySharedLibPage, not lazyPage. Same loader the '/documents' router entry
  // uses (pages/index.js).
  const _documentsPage = lazySharedLibPage(
    () => import('@symbo-ls/workspace-documents'),
    () => import('@symbo-ls/workspace-documents/pages/index.js').then((m) => m.documentsHome),
    { title: (el) => el.call('pageTitle', 'workspace.page.notes') }
  )
  // Analytics is a deferred shared library (out of the eager sharedLibraries
  // merge), so opening it in the home PageSlot must fold its context in first —
  // lazySharedLib, not lazyPage. Its context.js carries a `.pages` map, so each
  // picks the matching lazyPage off lib.pages (same loaders the '/analytics' etc.
  // router entries use in pages/index.js). Mirrors documents' _documentsPage.
  const _analyticsCtx = () => import('@symbo-ls/workspace-analytics')
  const _analyticsPage = lazySharedLib(_analyticsCtx, (lib) => lib.pages['/workspace-analytics'], {
    title: (el) => el.call('pageTitle', 'workspace.page.workspaceAnalytics')
  })
  // Analytics sub-tab pages so the AnalyticsNavbar (Workspace/Map/Logs) tabs
  // can swap the PageSlot content in-place instead of navigating away.
  const _projectAnalyticsPage = lazySharedLib(_analyticsCtx, (lib) => lib.pages['/analytics'], {
    title: (el) => el.call('pageTitle', 'workspace.page.analytics')
  })
  const _analyticsMapPage = lazySharedLib(_analyticsCtx, (lib) => lib.pages['/analytics/map'], {
    title: (el) => el.call('pageTitle', 'workspace.page.analyticsMap')
  })
  const _analyticsSessionsPage = lazySharedLib(
    _analyticsCtx,
    (lib) => lib.pages['/analytics/sessions'],
    { title: (el) => el.call('pageTitle', 'workspace.page.analyticsSessions') }
  )
  // PageSlot family-host CHROME (pseudo-pages, not navigable routes): the
  // slot's family host resolves these through `_defFor` and mounts ONE per
  // family — the header/controls DOM survives every intra-family route
  // swap. Registered like any lazy analytics page so the chrome lives in
  // the analytics chunk and folds its context.
  const _analyticsChromeProjects = lazySharedLib(
    _analyticsCtx,
    (lib) => lib.pages['/analytics/__slot-chrome'],
    {}
  )
  const _analyticsChromeWorkspace = lazySharedLib(
    _analyticsCtx,
    (lib) => lib.pages['/workspace-analytics/__slot-chrome'],
    {}
  )
  // Logs slot pages — ALL sections, so LogsSidebar clicks swap the slot
  // in place. Only /users was registered before, which made every other
  // section a full-page navigation that tore the PageSlot down.
  const _analyticsLogsPage = (route, title) =>
    lazySharedLib(_analyticsCtx, (lib) => lib.pages[route], { title })
  const _analyticsLogsUsersPage = _analyticsLogsPage('/analytics/logs/users', (el) =>
    el.call('pageTitle', 'workspace.page.logsUsers')
  )
  const _analyticsLogsSessionsPage = _analyticsLogsPage('/analytics/logs/sessions', (el) =>
    el.call('pageTitle', 'workspace.page.logsSessions')
  )
  // Two args, not three: the extra alias-path argument used to land in the
  // `title` slot, so the pane title read as the RAW PATH (measured live
  // 2026-09-13). The alias route below maps to this same page.
  const _analyticsLogsSessionPage = _analyticsLogsPage('/analytics/logs/session', (el) =>
    el.call('pageTitle', 'workspace.page.logsSession')
  )
  // Bare logs root — redirects in-slot to /users (analytics logs/main.js).
  const _analyticsLogsRootPage = _analyticsLogsPage('/workspace-analytics/logs', (el) =>
    el.call('pageTitle', 'workspace.page.logs')
  )
  // PROJECTS-family per-session detail + bugs / events views
  // (WS-ANALYTICS-FAMILY-SPLIT-UTC-TOTALS-1): a project session drills into
  // its own family's detail, never the workspace logs chrome. Existing
  // polyglot titles — no new i18n keys.
  const _analyticsSessionPage = _analyticsLogsPage('/analytics/session', (el) =>
    el.call('pageTitle', 'workspace.page.logsSession')
  )
  // Their OWN titles — inside the home PageSlot the pane/document title
  // comes from THIS wrapper, not the package's metadata.title, so reusing
  // the Logs keys titled the projects views "… / Logs / …" (measured live
  // on dev, build 3748c22d).
  const _analyticsBugsPage = _analyticsLogsPage('/analytics/bugs', (el) =>
    el.call('pageTitle', 'workspace.page.analyticsBugs')
  )
  const _analyticsEventsPage = _analyticsLogsPage('/analytics/events', (el) =>
    el.call('pageTitle', 'workspace.page.analyticsEvents')
  )
  // Unique visitors (WS-ANALYTICS-UTM-CAMPAIGNS-UI-1 addendum) — the logs
  // Users module on the projects family, its own title.
  const _analyticsVisitorsPage = _analyticsLogsPage('/analytics/visitors', (el) =>
    el.call('pageTitle', 'workspace.page.analyticsVisitors')
  )
  const _analyticsLogsEventsPage = _analyticsLogsPage('/analytics/logs/events', (el) =>
    el.call('pageTitle', 'workspace.page.logsEvents')
  )
  const _analyticsLogsBugsPage = _analyticsLogsPage('/analytics/logs/bugs', (el) =>
    el.call('pageTitle', 'workspace.page.logsBugs')
  )
  const _analyticsLogsNetworkPage = _analyticsLogsPage('/analytics/logs/network', (el) =>
    el.call('pageTitle', 'workspace.page.logsNetwork')
  )
  const _analyticsLogsVerbosePage = _analyticsLogsPage('/analytics/logs/verbose', (el) =>
    el.call('pageTitle', 'workspace.page.logsVerbose')
  )
  // teamAndAgents: the full panzoom board for the `/team-and-agents` route
  // (openRoute below opens it in the AppWindow PageSlot). No longer embedded
  // inline in the home AgentsWidget (2026-08-12 — that widget now renders a
  // deterministic split-count numeral body instead; see AgentsWidget below).
  const _agentsPage = lazyPage(
    () => import('@symbo-ls/workspace-canvas/pages/agents.js').then((m) => m.agents),
    { title: (el) => el.call('pageTitle', 'workspace.page.agents') }
  )
  // Remaining search-launcher destinations (2026-06-08 per Nika "all apps
  // should be opened in PageSlot") — registered here so every app in the
  // search dropdown opens inside the home AppWindow instead of routing away.
  // /studio (the dashboard project-picker surface) opens in the home PageSlot.
  // lazySharedLibPage (not lazyPage): the dashboard embeds the studios board
  // (`StudiosEmbed: {}`) and resolves studios-only components, so studios' 19
  // self-registered components (its default export, #11 Stage 2) must fold into
  // the live context BEFORE the dashboard mounts. Same shape the '/studio' router
  // entry uses (canvas/pages/index.js). Both the studios chunk (context) and the
  // dashboard chunk load on first /studio open; the eager home bundle carries
  // neither. See LAZY_PAGE_PLAN.md.
  const _studioPage = lazySharedLibPage(
    () =>
      Promise.all([
        import('@symbo-ls/workspace-canvas/pages/studios.js'),
        import('@symbo-ls/workspace-canvas/functions/index.js')
      ]).then(([m, f]) => ({ components: m.studiosContext.components, functions: { ...f } })),
    () => import('@symbo-ls/workspace-canvas/pages/dashboard.js').then((m) => m.dashboard),
    { title: (el) => el.call('polyglot', 'workspace.page.dashboard') }
  )
  // meet+chat bundle-fold (2026-08-01): meet is now a lazy shared library (see
  // pages/index.js's `_meetCtx` for the full rationale) — the home AppWindow
  // PageSlot mount needs the same context-fold-then-page-load shape as the
  // router-level entry, not a bare lazyPage.
  const _meetPage = lazySharedLibPage(
    () => import('@symbo-ls/workspace-meet/context.js'),
    () => import('@symbo-ls/workspace-meet/pages/index.js').then((m) => m.meetHome),
    { title: (el) => el.call('pageTitle', 'workspace.page.meet') }
  )
  // Admin opens inside the home AppWindow (PageSlot) too, like every other app
  // (per Nika "admin button in user dropdown should open admin in PageSlot")
  // rather than full-routing to /admin. Lazy so the admin bundle stays out of
  // the initial home chunk.
  const _adminPage = lazyPage(
    // Import the page's SOURCE file, not the identity-index barrel — the barrel
    // statically imports all 16 admin pages, so a barrel chunk re-bundles the
    // whole admin surface where this slot needs one page (bundle-split T1;
    // pages/index.js route entries import the same source files, so chunks
    // dedupe).
    () => import('./admin/admin.js').then((m) => m.admin),
    { title: (el) => el.call('pageTitle', 'workspace.page.admin') }
  )
  // Reminders opens inside the home AppWindow (PageSlot) too, like every other
  // non-canvas app — lazy so the reminders bundle stays out of the initial
  // home chunk (mirrors _adminPage).
  const _remindersPage = lazyPage(() => import('./reminders.js').then((m) => m.reminders), {
    title: (el) => el.call('pageTitle', 'workspace.page.reminders')
  })
  // History — the built-in activity timeline, in the PageSlot like the two
  // above (lazy, so its bundle stays out of the initial home chunk).
  const _historyPage = lazyPage(() => import('./history.js').then((m) => m.history), {
    title: (el) => el.call('pageTitle', 'workspace.page.history')
  })
  const _systemHealthPage = lazyPage(() => import('./system.js').then((m) => m.system), {
    title: (el) => el.call('pageTitle', 'workspace.page.systemHealth')
  })
  // NOTE — the GreenIG insurance pages (/policies, /claims, /customers,
  // /transactions) are NOT statically imported here. They live in the PUBLISHED
  // v2 module (silverbreeze-labs/greenig-app) and register into
  // root.workspaceProject.pages when that workspace activates. Route resolution
  // goes through `_appPageFor(path, root)` (below), which consults the project's
  // pages before the shared `_APP_WINDOW_PAGES` — so these routes resolve ONLY in
  // the GreenIG workspace.
  // The document editor opens inside the home AppWindow too (2026-06-08 per
  // Nika "DocumentsWidget items should open in the PageSlot") — the widget
  // tiles set `#doc=<id>` + selectedPage instead of routing away.
  const _documentsEditorPage = lazySharedLibPage(
    () => import('@symbo-ls/workspace-documents'),
    () => import('@symbo-ls/workspace-documents/pages/editor.js').then((m) => m.editor),
    { title: (el) => el.call('pageTitle', 'workspace.page.editor') }
  )
  // Project preview inside the PageSlot (2026-06-10 per Nika "open preview
  // should open workspace/preview in PageSlot"). Preview is a deferred sub-app
  // (folded out of the eager shell chunk in #16 — see workspace/context.js), so
  // opening it here must fold its context in FIRST — lazySharedLibPage, not
  // lazyPage — else a cold open (before any /preview router visit) mounts the
  // preview page against an unregistered Scene/PreviewNavbar/renderProject set.
  // Same context loader the '/preview' router entry uses (pages/index.js). The
  // pageLoader wrapper caps the standalone page's 100vw/100vh viewport sizing to
  // the sheet, and clears the preview's root-state footprint on unmount —
  // `root.data` is ALSO the canvas editor's project key, so a stale preview write
  // must not leak into the editor. Target project rides root._previewParams (+
  // ?owner/&key/&env for reload-restore); the page's own onCreate hook fetches.
  const _previewPage = lazySharedLibPage(
    () => import('@symbo-ls/workspace-preview/context.js'),
    () =>
      import('@symbo-ls/workspace-preview/pages/main.js').then((m) => ({
        ...m.main,
        width: '100%',
        height: '100%',
        onRemove: (el, s) => {
          // The page's own removal first (the site builder's release,
          // preview pages/main.js) — this wrapper replaces its onRemove.
          try {
            if (typeof m.main.onRemove === 'function') m.main.onRemove(el, s)
          } catch (err) {
            console.warn('[AppWindow] preview release failed:', err)
          }
          try {
            const r = s?.root || s
            r?.update?.({
              data: null,
              fetchError: null,
              usedFallback: false,
              resolvedEnv: null,
              requestedEnv: null,
              _previewParams: null
            })
          } catch (err) {
            console.warn('[AppWindow] preview cleanup failed:', err)
          }
        }
      })),
    { title: (el) => el.call('pageTitle', 'workspace.page.preview') }
  )

  const _APP_WINDOW_PAGES = {
    '/chat': _chatPage,
    '/mail': _mailPage,
    '/tickets': _ticketsPage,
    '/tickets/my': _ticketsPage,
    '/tasks': _ticketsPage,
    '/tasks/my': _ticketsPage,
    '/calendar': _calendarPage,
    '/reminders': _remindersPage,
    '/history': _historyPage,
    '/notes': _documentsPage,
    '/notes/editor': _documentsEditorPage,
    // Legacy slot values (stored prefs / old ?page= links) keep resolving.
    '/documents': _documentsPage,
    '/documents/editor': _documentsEditorPage,
    // Merged analytics surface (2026-08-08): /analytics = Projects family
    // (overview + map + logs), /workspace-analytics = Workspace usage.
    // Registered so the AnalyticsNavbar tabs + the AnalyticsHeader view
    // chips + LogsSidebar all stay inside the PageSlot.
    '/analytics/__slot-chrome': _analyticsChromeProjects,
    '/workspace-analytics/__slot-chrome': _analyticsChromeWorkspace,
    '/analytics': _projectAnalyticsPage,
    '/analytics/map': _analyticsMapPage,
    '/analytics/sessions': _analyticsSessionsPage,
    '/analytics/session': _analyticsSessionPage,
    '/analytics/visitors': _analyticsVisitorsPage,
    '/analytics/bugs': _analyticsBugsPage,
    '/analytics/events': _analyticsEventsPage,
    '/analytics/logs/users': _analyticsLogsUsersPage,
    '/analytics/logs/sessions': _analyticsLogsSessionsPage,
    '/analytics/logs/session': _analyticsLogsSessionPage,
    '/workspace-analytics/logs/session': _analyticsLogsSessionPage,
    '/analytics/logs/events': _analyticsLogsEventsPage,
    '/analytics/logs/bugs': _analyticsLogsBugsPage,
    '/analytics/logs/network': _analyticsLogsNetworkPage,
    '/analytics/logs/verbose': _analyticsLogsVerbosePage,
    '/workspace-analytics': _analyticsPage,
    // Legacy slot values (stored prefs / old ?page= links) keep resolving.
    '/workspace-analytics/map': _analyticsMapPage,
    // Every workspace logs section the LogsSidebar navigates to
    // (`/workspace-analytics/logs/<key>`) — registered HERE with an
    // object-form `{ title }` so the pane title resolves; the router
    // registry's entries (pages/index.js) carry function-form metadata,
    // which PageSlot's `_slotEntryMeta` cannot read, so a slot opened on
    // one of these showed the raw path as its title (measured live
    // 2026-09-13 on /workspace-analytics/logs/sessions).
    '/workspace-analytics/logs': _analyticsLogsRootPage,
    '/workspace-analytics/logs/users': _analyticsLogsUsersPage,
    '/workspace-analytics/logs/sessions': _analyticsLogsSessionsPage,
    '/workspace-analytics/logs/events': _analyticsLogsEventsPage,
    '/workspace-analytics/logs/bugs': _analyticsLogsBugsPage,
    '/workspace-analytics/logs/network': _analyticsLogsNetworkPage,
    '/workspace-analytics/logs/verbose': _analyticsLogsVerbosePage,
    // Through the live context (FA001, like /announcements below): the router
    // page is a string-extend of the canvas-registered `teamAndAgents`.
    '/team-and-agents': _ctxPages['/team-and-agents'],
    '/agents': _agentsPage,
    '/my-agents': _agentsPage, // back-compat alias → /agents
    '/studio': _studioPage,
    '/preview': _previewPage,
    '/meet': _meetPage,
    '/announcements': _ctxPages['/announcements'],
    '/team-members': _ctxPages['/people'] || _ctxPages['/team-members'],
    '/system-status': _systemHealthPage,
    '/admin': _adminPage
    // (Workspace-project pages — /policies, /claims, /customers, /transactions —
    // are NOT here; they resolve via root.workspaceProject.pages, see _appPageFor.)
    // (Removed 2026-06-10: the dead '/_event-detail' synthetic route. Home
    // event cards open CalEventDetailModal via el.call('openEventDetailModal')
    // — the @symbo.ls/windows path, same as the calendar cells — so nothing
    // ever set selectedPage to '/_event-detail'. The route + its URL guards
    // below were unreachable.)
  }
  // Only cache once the context was actually reachable — a first call made
  // before the root carries its context must not freeze the two
  // context-resolved entries as `undefined`.
  if (root && root.context) _appWindowPagesCache = _APP_WINDOW_PAGES
  return _APP_WINDOW_PAGES
}

// Resolve a route → page definition. The active workspace PROJECT's pages take
// precedence (so project-only routes like /policies resolve ONLY in the workspace
// whose project registered them), then the shared shell map, then — with the
// context passed (`root._slotResolve`) — the rest of the shell's registry. Pass
// `root` so the project pages are visible; without root it falls back to the
// shared map.
const _appPageFor = (path, root, ctx) => {
  const projPages = root && root.workspaceProject && root.workspaceProject.pages
  // Module pages resolve from the loader's LIVE wrapped map first — the
  // stash copy in root state has been through root.update's deep clone,
  // which strips the module design-system context off the def (the def
  // mounts, but every module token then resolves against the SHELL config:
  // "navy" paints the CSS keyword, "paper" drops). getModulePageDef is
  // null whenever no module is active, so non-module workspaces are
  // untouched.
  const liveGet =
    root && typeof root._modulePagesLiveGet === 'function' ? root._modulePagesLiveGet() : null
  // WS-APP-PAGE-SLOT-KEEPS-MODULE-CTX-1 — the live map above holds the
  // TAKEOVER module's pages only. An INSTALLED app's (loadWorkspaceApps)
  // wrapped defs live in the shell context's `pages` bag alone, so a pane
  // opened from the home (`?page=/socials`, the navbar tab) fell through to
  // the stash copy below and mounted the app against the SHELL design system
  // and the SHELL state root (measured live 2026-09-23, system/socials-app:
  // the pane's designSystem === the shell's, `s.root.shell` undefined; the
  // same app opened from /studio carried its own). Only a def that CARRIES a
  // module context is taken from the bag — a plain shell page keeps the
  // precedence below exactly as it was.
  // WS-APP-OPEN-SLOT-PLACEHOLDER-1 — so is an installed app's OPENING
  // placeholder (before its setup): rendering it is what sets the app up, and
  // it then takes the real page itself. The stash copy mounted the app's raw
  // page against the shell design system and never set the app up. The loader
  // marks its placeholder with this registered symbol (workspaceProjects.js
  // _appOpeningPage; non-enumerable, so no clone or spread carries it).
  const ctxPage = path && ctx && ctx.pages ? ctx.pages[path] : null
  const ctxOpening = !!ctxPage && !!ctxPage[Symbol.for('symbols.workspace.appOpening')]
  // LAST, every other route of the shell's registry: the page the PageSlot
  // mounts for it anyway (canvas PageSlot `_defFor`, its `context.pages`
  // tier). Without this tier the home OPENED routes it could not NAME: the
  // loader's ?page= restore (workspaceProjects.js _restorePageParam) and the
  // pane both took `/apps` from the bag, while the URL sync (PageSlot
  // `_writeSlotUrl` / `_syncSlotFromUrl`, which ask only this resolver) called
  // it unresolvable — so `?page=/apps` left the address bar the moment the
  // pane opened, and a reload or a Back to that entry lost the pane (dev
  // 2026-10-24, the PM close-audit note on WS-SHELL-APP-ROUTES-NO-MAIN-
  // LANDMARK-1). Last, so a plain bag page never changes the precedence above.
  return (
    (path && liveGet && liveGet[path]) ||
    (path && getModulePageDef(path)) ||
    (ctxPage && (ctxPage.context || ctxOpening) ? ctxPage : null) ||
    (path && projPages && projPages[path]) ||
    _getAppWindowPages(root)[path] ||
    ctxPage ||
    undefined
  )
}

// Test seam for the resolver precedence (__tests__/slotResolveInstalledAppContext.test.js).
export const __appPageForTests = _appPageFor

// The first name the greeting addresses. A member with no name yields '' — the
// greeting then reads WITHOUT a name. It used to fall back to the English word
// "there", so a Georgian board read "დილა მშვიდობისა, there".
const _shellFirstName = (name) =>
  String(name || '')
    .trim()
    .split(/\s+/)[0] || ''

// The bundle key of the greeting's lead-in for an hour of the day. With a name
// the lead-in carries its own separator ("Good morning, ") and the name follows
// in its own span; without one the greeting is the lead-in alone. Literal keys
// only — a key built by concatenation is invisible to grep and to the key gates.
//
// THE HOUR LAW (HOME_SIGNALS_CONTRACT §4; Nika 2026-09-28: "Good evening" at
// 02:20 is wrong; night pick B): 05:00–11:59 morning, 12:00–17:59 afternoon,
// 18:00–21:59 evening, 22:00–04:59 the night line — "Hello, {name}" /
// "გამარჯობა, {name}", never "Good evening" after 22:00 or before 05:00.
const _greetLeadKey = (hour, hasName) => {
  if (hour >= 5 && hour < 12)
    return hasName ? 'workspace.home.greet.morning' : 'workspace.home.greet.morningNoName'
  if (hour >= 12 && hour < 18)
    return hasName ? 'workspace.home.greet.afternoon' : 'workspace.home.greet.afternoonNoName'
  if (hour >= 18 && hour < 22)
    return hasName ? 'workspace.home.greet.evening' : 'workspace.home.greet.eveningNoName'
  return hasName ? 'workspace.home.greet.night' : 'workspace.home.greet.nightNoName'
}

// (The greeting's one-sentence summary — _greetingFacts / _greetingSummaryRuns
// and their readiness and today's-events helpers — became the SIGNALS of
// HOME_SIGNALS_CONTRACT v1, 2026-09-28: functions/homeSignals.js collects them
// from the built-in apps and every installed app, and the sub-line and the
// summary render from functions/homeSummaryCompose.js.)

// The ?page=<route> restore-param name the home reads on load. The shell PageSlot
// owns writing/clearing it while a slot is open; this constant stays here because
// the home onCreate + workspaceProjects.js restore sites look it up by this name.
const APP_WINDOW_URL_PARAM = 'page'

// (_greetingFor removed 2026-09-24 — never called; the Greeting card's
// GreetLead reads workspace.home.greet.* for the same three day parts.)

// (_dayCockpit + _cockpitBody removed 2026-06-06 — the last cockpit
// tiles, Open tickets + Unread, left the home grid per Nika.)

// Open tickets ASSIGNED TO the current user. Used by the "Received
// tasks" widget — the count + a short list of the most-recent rows
// so the user sees what's on their plate without opening /tickets.
// "Open" excludes the canonical resolved/cancelled state labels.
const _DONE_TICKET_STATES = new Set(['done', 'closed', 'resolved', 'cancelled', 'archived'])
const _isOpenTicket = (t) =>
  !_DONE_TICKET_STATES.has(String(t?.state || t?.status || '').toLowerCase())
const _ticketEmail = (t, field) =>
  String(
    t?.[field] || t?.[field === 'assigneeEmail' ? 'assignee_email' : 'reporter_email'] || ''
  ).toLowerCase()

// Widget data source — root._userTickets is a DEDICATED key written only
// by _ensureHomeTickets (assigned ∪ reported). The widgets originally read
// the shared board cache root.tickets, but several async boot writers race
// over it (loadPrivateData's workspace-change handler wipes it to [] after
// auth resolves), so the widgets randomly showed "You're clear" depending
// on whether the wipe landed before or after the cold-home load. Fall back
// to the board cache only while the dedicated key hasn't loaded yet.
const _widgetTickets = (root) => {
  if (Array.isArray(root?._userTickets) && root._userTickets.length) return root._userTickets
  return Array.isArray(root?.tickets) ? root.tickets : []
}

// FALSE-EMPTY-HOME-RECEIVED-GIVEN-LOADING-1 — the ONE place a widget turns
// a ticket set into a number, so the Received / Given render sites carry no
// raw `.length` a load-state pin cannot see. The number alone never decides
// a body: `homeTicketListState` does (functions/homeLoadState.js).
const _ticketCount = (rows) => rows.length

const _receivedTickets = (root) => {
  const me = (root?.currentUserEmail || '').toLowerCase()
  if (!me) return []
  return _widgetTickets(root).filter(
    (t) => _isOpenTicket(t) && _ticketEmail(t, 'assigneeEmail') === me
  )
}

// Open tickets the current user CREATED (and assigned to someone
// else). The "Given tasks" widget surfaces these so a project lead
// can glance at what they've delegated.
const _givenTickets = (root) => {
  const me = (root?.currentUserEmail || '').toLowerCase()
  if (!me) return []
  return _widgetTickets(root).filter((t) => {
    if (!_isOpenTicket(t)) return false
    const reporter = _ticketEmail(t, 'reporterEmail')
    if (reporter !== me) return false
    const assignee = _ticketEmail(t, 'assigneeEmail')
    // Skip self-assigned rows so the count reads as "delegated work".
    return assignee && assignee !== me
  })
}

// S8 (NIKA BATCH 2026-08-16) — TeamPulseWidget's bucket grouping. Nika:
// "I like how our board-status py script looks — we need a few new builtin
// widgets for workspace default that show similar summaries." Takes the
// PROPERTIES that make that script readable (tickets/data/board-status.py
// bar()), not its ASCII: a proportional bar over LIVE work only, fixed-width
// count cells, a legend naming who can act. Four buckets, not the model's
// full 8-state enum — a home widget summarizes, it doesn't replace the
// board.
// `label` carries a BUNDLE KEY; the legend's own render site resolves it.
const _PULSE_BUCKETS = [
  { key: 'todo', label: 'workspace.pulse.todo', hue: 'blue' },
  { key: 'active', label: 'workspace.pulse.active', hue: 'orange' },
  { key: 'review', label: 'workspace.pulse.review', hue: 'purple' },
  { key: 'blocked', label: 'workspace.pulse.blocked', hue: 'red' }
]
const _pulseBucket = (t) => {
  const key = String(t?.state || t?.columnKey || '').toLowerCase()
  if (key === 'blocked') return 'blocked'
  if (key === 'ready_to_review' || key === 'ready_to_test' || key === 'review') return 'review'
  if (key === 'in_progress' || key === 'progress') return 'active'
  if (key === 'done' || key === 'released' || key === 'cancelled' || key === 'closed') return null
  return 'todo'
}
// Counts over every OPEN ticket in the workspace (not just "assigned to
// me" — that distinction is what separates this from Received/Given).
const _workspacePulse = (root) => {
  const counts = { todo: 0, active: 0, review: 0, blocked: 0 }
  for (const t of _widgetTickets(root)) {
    if (!_isOpenTicket(t)) continue
    const b = _pulseBucket(t)
    if (b) counts[b] += 1
  }
  return counts
}
// Largest-remainder allocation over `total` proportional segments — same
// algorithm as board-status.py's own `bar()`, so a bucket with ANY tickets
// never rounds down to a zero-width segment (a 1-in-40 bucket disappearing
// would misreport "nothing blocked" when one ticket genuinely is).
// Bucket-agnostic (S8 second pass): the allocator, the priority buckets and
// the workload grouping all live in `functions/homeSummary.js` as pure,
// directly-testable functions. The page keeps the DATA PLUMBING (which
// tickets are loaded, which count as open); the module keeps the
// arithmetic, so "never round a non-empty bucket to zero" has exactly one
// implementation shared by all three summary widgets.
const _pulseBarSegments = (counts, totalUnits) => _barSegments(_PULSE_BUCKETS, counts, totalUnits)

// Open rows the summary widgets share — one filter, three widgets.
const _openWidgetTickets = (root) => _widgetTickets(root).filter(_isOpenTicket)
const _workspacePriorityMix = (root) => _priorityMix(_openWidgetTickets(root))
const _workloadRows = (root) =>
  _workloadRowsOf(_openWidgetTickets(root), (t) => _ticketEmail(t, 'assigneeEmail'))

// Cold-home loader for the Received / Given widgets. Those widgets read
// `root.tickets`, which is the board cache — only populated after the
// user has visited a /tickets board this session. On a cold home load it
// is empty, so both widgets rendered "Nothing assigned" / "Nothing
// delegated" even when the user has open work. Fetch the user's assigned
// + reported tickets once here. `currentUserEmail` is set asynchronously
// by auth, so wait briefly for it; skip entirely if the board cache is
// already populated (don't clobber a full board the user just loaded).
// ⚠️ Takes the ELEMENT as well as root (FALSE-ZERO-HOME-NON-AGENT-KEYS-1):
// `fetchTicketsForUser` resolves the SDK through `el.call('getSDK')`, and
// this loader used to hand it `root` — so every Home call threw
// "el.call is not a function", the loader swallowed it into `[]`, and
// `_userTickets` was never written on any cold Home load. Measured live on
// `/w/system/hq` 2026-08-21 the moment the error key existed to show it.
const _ensureHomeTickets = async (el, root, { force = false } = {}) => {
  if (!el || !root) return
  if (root._homeTicketsLoaded && !force) return
  root._homeTicketsLoaded = true
  // Wait for BOTH the auth email AND the workspace scope. The tickets list
  // resolves its REQUIRED workspaceId from the SDK's ambient context
  // (mirrored from root.workspace by the auth subscription) — on a cold
  // first boot (e.g. right after the demo startDemo reload) the fetch used
  // to fire before the scope landed, 400 with workspace_id_required, and the
  // one-shot gate swallowed the empty result: "Received tasks" showed
  // "You're clear" even though tickets WERE assigned, until a manual reload.
  let email = root.currentUserEmail || ''
  let wsId = root.workspace?.id || null
  if (!email || !wsId) {
    await new Promise((resolve) => {
      let n = 0
      const iv = setInterval(() => {
        email = root.currentUserEmail || ''
        wsId = root.workspace?.id || null
        if ((email && wsId) || ++n > 30) {
          clearInterval(iv)
          resolve()
        }
      }, 200)
    })
  }
  if (!email) {
    root._homeTicketsLoaded = false
    return
  }
  let rows
  try {
    rows = await fetchTicketsForUser(el, email, 100, { throwOnError: true })
  } catch (err) {
    // FALSE-ZERO-HOME-NON-AGENT-KEYS-1 — record the failure instead of
    // swallowing it: `_homeTicketsError` is what lets the Temo caption
    // render "Couldn’t load tickets" rather than a confident "Standing by".
    // The gate re-opens so the next onUpdate / Refresh retries.
    root._homeTicketsLoaded = false
    root.update({
      _homeTicketsError: (err && err.message) || el.call('polyglot', 'workspace.home.error.tickets')
    })
    return
  }
  if (!rows.length && !wsId) {
    // Nothing came back while the workspace scope was still unresolved —
    // that's the 400-race, not a real empty state. Re-open the gate so the
    // next onUpdate retries once the scope lands, and write NOTHING:
    // `_userTickets` stays `null` ("not read yet"), never `[]`.
    root._homeTicketsLoaded = false
    return
  }
  // Always write — `[]` with a resolved scope is a real, verified zero
  // (FALSE-ZERO-HOME-NON-AGENT-KEYS-1), not a dropped read.
  const patch = { _userTickets: rows, _homeTicketsError: null }
  // #2323 — also seed myTickets so the home AgentsWidget status shows a
  // non-zero count on a cold home load. ONLY the assigned-to-me subset though: myTickets
  // feeds My View's buckets, which assume assigned-only — the earlier
  // version pushed the full assigned∪reported union in, so tickets you
  // DELEGATED to others briefly appeared in your own buckets. And only when
  // myTickets is still empty, so a fresher page-owned fetch is never
  // clobbered.
  if (!(Array.isArray(root.myTickets) && root.myTickets.length)) {
    const me = email.toLowerCase()
    patch.myTickets = rows.filter((t) =>
      String(t.assigneeEmail || t.assignee_email || '')
        .toLowerCase()
        .includes(me)
    )
  }
  root.update(patch)
}

// PRESENCE-ZERO-1 (tickets/opus.md) — the "Active right now" widget (+ its
// AppAnalyticsWidget mirror + the Watcher agent status) must report WORKSPACE
// presence ("Who's on the workspace today"), not Projects/site-visitor
// analytics. `loadAnalyticsData` (the generic Projects-family loader
// analytics/functions/loadAnalyticsData.js) resolves an EMPTY project scope
// on Home (no `_analyticsProjectKey`/`_analyticsParams` are ever set here) to
// `excludeProjectId: 'workspace'` — the 2026-08-11 surface-division filter
// that deliberately hides the shell's own dogfood telemetry from the Projects
// dashboard's "All projects" view. That is exactly backwards for a widget
// whose only traffic IS the shell's own telemetry: it pinned this widget at
// 0/0 in every internal/dogfood session, while /workspace-analytics
// (analytics/pages/main.js `_loadWorkspaceMetrics`) queries the SAME
// `analyzed.now`/`activeUsers` rollups scoped `projectId: 'workspace'` and
// reported the true, non-zero count — two surfaces, same underlying
// analyzed-session collection, disagreeing purely on `projectId` filter
// form (verified live: GET /core/analyzed/now?projectId=workspace →
// usersNow:1 vs ?excludeProjectId=workspace → usersNow:0, same moment).
// Fixed by scoping identically to /workspace-analytics and writing to a
// DEDICATED key (`_homeWorkspaceActivity`, not the shared `root._analytics`
// the Projects page also writes) so the two surfaces' scopes can never
// collide again.
const _loadHomeWorkspaceActivity = (el, root) => {
  if (!el?.sdk?.execute || !root) return
  el.sdk
    .execute('analyzed', 'now', { filter: { projectId: 'workspace' } })
    .then((res) => {
      // FALSE-ZERO-HOME-NON-AGENT-KEYS-1 — an ABSENT field is `null`, never
      // a well-formed 0 (`Number(undefined) || 0` was the data-layer half of
      // the false zero); a missing payload is a failed read, not a quiet
      // workspace. Always write, so the widget can leave its skeleton.
      if (!res) {
        root.update({
          _homeWorkspaceActivityError: el.call(
            'polyglot',
            'workspace.home.error.workspaceActivityEmpty'
          )
        })
        return
      }
      root.update({
        _homeWorkspaceActivity: {
          usersNow: homeActivityMetric(res.usersNow ?? res.users_now),
          usersToday: homeActivityMetric(res.usersToday ?? res.users_today)
        },
        _homeWorkspaceActivityError: null
      })
    })
    .catch((err) => {
      console.warn('[home] workspace-activity load failed:', err && err.message)
      root.update({
        _homeWorkspaceActivityError:
          (err && err.message) || el.call('polyglot', 'workspace.home.error.workspaceActivity')
      })
    })
}

// FALSE-ZERO-1 (residual, 2026-08-20) — Home's OWN agent-roster producer.
//
// Measured live on `/w/system/hq`: `root._agents` stayed `undefined`
// forever. The tri-state contract (`shared/state.js:142-147`) is written by
// exactly two producers, and NEITHER runs on Home — the shared one lives in
// the Simone mention composer (`shared/_simone/Chat.js` ensureAgentRoster,
// mounts only when the picker opens) and the other on the /team-and-agents
// canvas page. So every Home consumer sat permanently in the `loading`
// branch: the already-fixed "AI agents" numeral rendered a permanent `–`,
// and the unfixed ones rendered a confident wrong number instead.
//
// A consumer cannot fix that. A surface that must distinguish
// loading / loaded / failed needs a producer that actually runs on it, so
// Home gets one — the same one-shot `_agentsLoaded` gate the shared
// producer uses (so the two can never double-fetch), the same SDK call, and
// the same row shape the contract owner writes, so Home, Simone and the
// canvas roster can never disagree about what an agent is.
const _loadHomeAgentRoster = (el, root) => {
  if (!root || root._agentsLoaded) return
  const sdk = el && el.sdk
  const orgId = root.jwtClaims && root.jwtClaims.workspace_org_id
  if (!sdk || typeof sdk.listAgents !== 'function' || !orgId) return
  root._agentsLoaded = true
  Promise.resolve(sdk.listAgents(orgId))
    .then((raw) => {
      const list = Array.isArray(raw) ? raw : (raw && raw.data) || []
      const agents = list
        .map((u) => ({
          id: String(u.username || u.id || u._id || '').toLowerCase(),
          name: u.name || u.username || el.call('polyglot', 'workspace.agents.fallbackName'),
          kind: 'agent',
          avatar: u.avatar || null,
          userId: u.id || u._id || null,
          email: u.email || null,
          // the per-language names of a server-seeded agent (the contract's
          // shape, shared/_simone/mention.js agentsFromMongo): the chat's
          // @-mention picker shows "პირადი ასისტენტი" in a Georgian UI
          nameI18n: (u.metadata && u.metadata.nameI18n) || null
        }))
        .filter((a) => a.userId)
      // Always write — `[]` is a real, verified zero, not a dropped read.
      root.update({ _agents: agents, _agentsError: null })
    })
    .catch((err) => {
      // Record the failure instead of swallowing it: `_agentsError` is what
      // lets a consumer render `!` rather than a confident `0`.
      root._agentsLoaded = false
      root.update({
        _agentsError: (err && err.message) || el.call('polyglot', 'workspace.home.error.agents')
      })
    })
}

// Re-run every home dashboard data loader in place — the cold-load prefetch in
// onCreate AND the "Refresh" context-menu action both route through here so the
// two can never drift. Analytics (SWR, 30s freshness), the Received / Given /
// ticket widgets, the chat-channel sidebar list, and the Projects widget each
// refetch from the server and repaint reactively; nothing reloads the page.
// `force` bypasses _ensureHomeTickets's one-shot gate — the menu passes
// force:true; the cold onCreate path leaves it self-gating (force:false).
const _refreshHomeData = (el, root, { force = false } = {}) => {
  if (!el || !root) return
  _loadHomeWorkspaceActivity(el, root)
  // Roster feeds AppTeamWidget's combined count, the AgentsWidget share bar
  // and its avatar row. `force` re-arms the one-shot gate so the
  // context-menu Refresh actually refetches it (FALSE-ZERO-1).
  if (force) root._agentsLoaded = false
  _loadHomeAgentRoster(el, root)
  // Context-menu "Refresh" (force) refetches EVERYTHING the widgets read —
  // the old pass covered analytics/tickets/channels/projects but left the
  // roster + notification badge stale, which read as "refresh does nothing"
  // on people-facing widgets (2026-07-25 per Nika "fix refresh
  // functionality").
  if (force && el.sdk) {
    invalidateOrgMembers()
    shellListOrgMembers(el.sdk, root.workspace?.id ? String(root.workspace.id) : null)
      .then((rows) => {
        if (!rows) return
        const all = rows.map(_person)
        root.update({ peopleAll: all, people: all, peopleLoaded: true, peopleError: null })
      })
      .catch((err) => {
        console.warn('[home] roster refresh failed:', err && err.message)
        root.update({
          peopleError: (err && err.message) || el.call('polyglot', 'workspace.home.error.members')
        })
      })
    fetchOrgNotifCount(root).catch((err) =>
      console.warn('[home] notif refresh failed:', err && err.message)
    )
  }
  // Populate root._userTickets for the Received / Given widgets (see
  // _ensureHomeTickets). Fire-and-forget; it self-gates unless forced.
  if (force) root._homeTicketsLoaded = false
  _ensureHomeTickets(el, root, { force }).catch((err) =>
    console.warn('[home] tickets load failed:', err && err.message)
  )
  if (el.sdk?.execute) {
    el.sdk
      .execute('workspaceProject.chat', 'list', {})
      .then((res) => {
        const rows = Array.isArray(res) ? res : res?.data || []
        // Full rowToChannel shape + workspace scoping. The old minimal
        // mapping ({id,name,kind,is_private}) STRIPPED workspaceId, which
        // made filterChannelsToActiveWorkspace fail open downstream — after
        // a home load, every cross-workspace channel from the server's
        // membership union showed in the chat sidebar and Messenger list
        // (the "Symbols chats visible in aihouse" leak, 2026-07-12).
        root.update({
          channels: filterChannelsToActiveWorkspace(
            (Array.isArray(rows) ? rows : []).map(rowToChannel),
            getActiveWorkspaceId(root)
          )
        })
      })
      .catch((err) => console.warn('[home] channels load failed:', err && err.message))
  }
  const orgId = root?.jwtClaims?.workspace_org_id || (root?.orgs && root.orgs[0]?.id)
  const orgs = root?.jwtClaims?.orgs || root?.orgs || []
  const orgSlug = orgs.find((o) => o.id === orgId)?.slug
  if (orgSlug && el.sdk?.getProjects) {
    // THE APP SPLIT (docs/APP_SPLIT.md): the widget below keeps websites only;
    // the AI-app and App Store ids tell a workspace app the list cannot.
    try {
      el.call('loadProjectKindSignals')
    } catch (_) {}
    el.sdk
      .getProjects({ owner: orgSlug, limit: 500 })
      .then((res) => {
        const raw = Array.isArray(res) ? res : res?.data || res?.items || []
        root.update({ _projectsForActiveOrg: raw })
      })
      .catch((err) => console.warn('[home] getProjects failed:', err && err.message))
  }
}

// Compact column/state label for the ticket-row pill below — a tiny LOCAL
// map (not a cross-package el.call into the tickets library's own
// formatStateLabel) so Received/Given render correctly on a COLD home load,
// before the deferred tickets shared library has necessarily folded its
// functions barrel into context.functions. Same defensive reasoning as
// fetchTicketsForUser/saveTicket being direct ESM imports above. Ink-only
// (design-direction.md §6): the pill is a neutral wash + this label text,
// never a second accent hue riding on top of the widget's own poster.
// Values are BUNDLE KEYS — the pill's render site resolves them, and the
// suffix-derived fallback in `_ticketPillLabel` passes through polyglot
// verbatim because polyglot returns a non-key unchanged.
const _TICKET_STATE_LABEL = {
  not_started: 'workspace.ticketState.backlog',
  backlog: 'workspace.ticketState.backlog',
  unsynced: 'workspace.ticketState.backlog',
  draft: 'workspace.ticketState.draft',
  todo: 'workspace.ticketState.todo',
  in_progress: 'workspace.ticketState.inProgress',
  blocked: 'workspace.ticketState.blocked',
  awaiting_approval: 'workspace.ticketState.awaitingApproval',
  ready_to_review: 'workspace.ticketState.inReview',
  review: 'workspace.ticketState.inReview',
  ready_to_test: 'workspace.ticketState.readyToTest',
  done: 'workspace.ticketState.done',
  released: 'workspace.ticketState.released',
  cancelled: 'workspace.ticketState.cancelled',
  closed: 'workspace.ticketState.cancelled'
}
const _ticketPillLabel = (t) => {
  const key = String(t?.state || t?.columnKey || '').toLowerCase()
  if (!key) return ''
  if (_TICKET_STATE_LABEL[key]) return _TICKET_STATE_LABEL[key]
  return key.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())
}

// Render a single ticket row inside Received / Given widgets. Each
// child is a raw DOMQL definition (no childExtends:'AppListItem') so
// onClick can write `selectedTicket` instead of routing — clicking a
// row sets the AppWindow PageSlot to the matching tickets sub-route
// (so TicketsDetailModal mounts at the page root) AND seeds
// selectedTicket on the same tick, so the modal pops with that row's
// body without the user having to scroll-and-pick from a list.
//
// `counterpartEmail` is the person shown (avatar + name) — for Received
// that's the reporter (who assigned this to me), for Given that's the
// assignee (who I assigned this to): in both cases the OTHER party, since
// showing the viewer's own avatar on every row would be zero information.
// `hue` is the caller's own `homeWidgetSurfaceHue(root, 'received'|'given')`
// result — passed in (not recomputed here) so the row's ink stays the CARD's
// ink rather than the adaptive 'title'/'caption' tokens the row used before:
// those flip with the shell theme while a picked card colour does not, which
// is the exact near-invisible-in-dark-theme bug class already fixed for
// CityMemberRow/UpcomingEventCard (2026-08-12).
// ⚠️ `hue` is NULLABLE since 2026-09-02 (the board's default is one neutral
// solid; a colour is the user's opt-in), so every read below carries the
// adaptive-neutral fallback the row used before posters existed. The callers
// build this inside a `children:` factory, so it is already re-resolved on
// every root update — no extra reactivity is needed here.
const _ticketRowChild = (t, surfacePage, counterpartEmail, hue) => ({
  scope: {
    t: t,
    hue: hue
  },

  tag: 'button',
  type: 'button',
  flow: 'x',
  gap: 'A',
  align: 'center',
  round: 'radiusCard',
  padding: 'Z A',
  textAlign: 'left',
  cursor: 'pointer',
  color: (hue && hue.ink) || 'title',
  background: 'transparent',
  border: 'none',
  width: '100%',
  transition: '160ms defaultBezier background',
  ':hover': { background: (hue && hue.wash) || 'hover' },

  onClick: (e, el) => {
    e.preventDefault()
    // Show the shell-root TicketsDetailModal by setting its stateKey
    // carrier directly (2026-06-08 perf fix) — NOT through
    // @symbo.ls/windows openModal, which re-prepared the whole
    // ~4,000-line component on every open and froze the UI 15–40s. The
    // modal is mounted once at the shell root (workspace/app.js) and
    // shows reactively on selectedTicket. `surfacePage` is unused here;
    // it stays in the factory args for forward-compat.
    const root = el.state?.root || el.getRoot?.()?.state?.root || el.getRoot?.()?.state
    root?.update?.({ selectedTicket: t })
  },

  Avatar: {
    avatarType: 'initials',
    boxSize: 'C C',
    flexShrink: '0',
    src: (el, s) => el.call('avatarUrl', counterpartEmail || t.title)
  },

  Body: {
    flow: 'y',
    gap: 'V',
    flex: '1',
    minWidth: '0',

    TitleRow: {
      flow: 'x',
      align: 'center',
      gap: 'V',

      Title: {
        tag: 'span',
        flex: '1',
        minWidth: '0',
        fontSize: 'A',
        fontWeight: '700',
        color: (hue && hue.ink) || 'title',
        // Primary text wraps — never an ellipsis (the home quality law).
        overflowWrap: 'anywhere',
        text: (el) => el.scope.t.title || el.call('polyglot', 'workspace.ticket.untitled')
      },

      // Column pill — ink-wash + the column/state label text, never a
      // second accent hue on this poster (see _ticketPillLabel above).
      Pill: {
        hide: !_ticketPillLabel(t),
        tag: 'span',
        flexShrink: '0',
        fontSize: 'Y2',
        fontWeight: '700',
        lineHeight: '1',
        padding: 'V Y',
        round: 'radiusPill',
        color: (hue && hue.ink) || 'title',
        background: (hue && hue.fill) || 'overlay',
        whiteSpace: 'nowrap',
        text: (el) => el.call('polyglot', _ticketPillLabel(el.scope.t))
      }
    },

    Caption: {
      tag: 'p',
      margin: '0',
      fontSize: 'Y',
      lineHeight: '1.4',
      color: (hue && hue.inkSoft) || 'caption',
      overflowWrap: 'anywhere',
      text: counterpartEmail ? counterpartEmail.split('@')[0] : ''
    }
  }
})

// Shared quick-add row for Received/Given — 'received' self-assigns so the
// new row appears in THAT SAME widget immediately (a personal quick task);
// 'given' has no assignee yet (a delegated task is meaningless without one)
// so it opens the real ticket detail modal right after create, reusing the
// existing, proven detail-modal dispatch instead of inventing a bespoke
// inline assignee picker.
// `hueId` is the WIDGET ID this row lives in ('received' / 'given'), not a
// resolved hue object: unlike `_ticketRowChild` above this row is built ONCE,
// at page-definition time, so a resolved object baked here could never
// repaint when the user picks or clears the widget's colour (2026-09-02).
// Every paint prop below resolves it per render instead.
const _taskQuickAddRow = (kind, hueId) => ({
  scope: { kind: kind, hueId: hueId },
  flow: 'x',
  gap: 'V',
  align: 'center',
  flexShrink: '0',

  Input: {
    flex: '1',
    minWidth: '0',
    minHeight: 'controlHCompact',
    round: 'radiusControl',
    fontSize: 'Z1',
    padding: '0 Z1',
    background: (el, s) => (_cardHue(el, s, el.scope.hueId) || {}).fill || 'overlay',
    border: 'none',
    color: (el, s) => (_cardHue(el, s, el.scope.hueId) || {}).ink || 'title',
    aria: {
      label: (el) =>
        el.call(
          'polyglot',
          el.scope.kind === 'given'
            ? 'workspace.home.quickAdd.delegateAria'
            : 'workspace.home.quickAdd.addAria'
        )
    },
    placeholder: (el) =>
      el.call(
        'polyglot',
        el.scope.kind === 'given'
          ? 'workspace.home.quickAdd.delegatePlaceholder'
          : 'workspace.home.quickAdd.addPlaceholder'
      ),
    value: (el, s) =>
      s.root?.[el.scope.kind === 'given' ? '_givenQuickAdd' : '_receivedQuickAdd'] || '',
    onClick: (e) => e.stopPropagation(),
    onInput: (e, el, s) =>
      s.root.update({
        [el.scope.kind === 'given' ? '_givenQuickAdd' : '_receivedQuickAdd']: e.target.value
      }),
    onKeydown: (e, el, s) => {
      e.stopPropagation()
      if (e.key !== 'Enter') return
      e.preventDefault()
      _submitTaskQuickAdd(el, s.root, el.scope.kind)
    }
  },
  AddBtn: {
    tag: 'button',
    type: 'button',
    class: 'gs-no-drag',
    flexShrink: '0',
    background: (el, s) => (_cardHue(el, s, el.scope.hueId) || {}).ink || 'title',
    // The label sits ON the ink fill, so it must be the ink's counterpart:
    // the colour's own bg on a picked colour, `title-reversed` on the
    // neutral card where the fill is `title`.
    color: (el, s) => (_cardHue(el, s, el.scope.hueId) || {}).bg || 'title-reversed',
    border: 'none',
    minHeight: 'controlHCompact',
    round: 'radiusPill',
    padding: '0 Z',
    fontSize: 'Z1',
    fontWeight: '700',
    cursor: 'pointer',
    transition: '120ms defaultBezier opacity',
    ':hover': { opacity: '.85' },
    ':disabled': { opacity: '.4', cursor: 'not-allowed' },
    disabled: (el, s) =>
      !(s.root?.[el.scope.kind === 'given' ? '_givenQuickAdd' : '_receivedQuickAdd'] || '').trim(),
    text:
      kind === 'given'
        ? '{{ workspace.home.quickAdd.delegate | polyglot }}'
        : '{{ workspace.home.quickAdd.add | polyglot }}',
    onClick: (e, el, s) => {
      e.stopPropagation()
      _submitTaskQuickAdd(el, s.root, el.scope.kind)
    }
  }
})

// Orchestrates the quick-add: read the draft, clear it, create via the
// registered `saveTicket` (imported directly above — same cold-boot
// rationale as fetchTicketsForUser), then append the real row straight into
// `root._userTickets` (the widgets' OWN dedicated cache — saveTicket/
// _applyServerTicketPatch only ever touch root.tickets/selectedTicket/
// myTickets, never this cache) so the new task is visible immediately with
// no extra refetch. 'given' rows carry no assignee yet, so they open the
// real ticket detail modal right after create — completing the delegation
// there reuses the exact same, already-proven UI the row click above does.
const _submitTaskQuickAdd = async (el, root, kind) => {
  if (!root) return
  const key = kind === 'given' ? '_givenQuickAdd' : '_receivedQuickAdd'
  const title = (root[key] || '').trim()
  if (!title) return
  const me = root.currentUserEmail || ''
  root.update({ [key]: '' })
  const payload = { title, columnKey: 'backlog', reporterEmail: me }
  if (kind === 'received') payload.assigneeEmail = me
  try {
    const id = await saveTicket.call(el, payload)
    if (!id) throw new Error('create returned no id')
    const created = {
      ...payload,
      id,
      key: id,
      state: 'not_started',
      assigneeEmail: payload.assigneeEmail || null
    }
    root.update({ _userTickets: [...(root._userTickets || []), created] })
    if (kind === 'given') root.update({ selectedTicket: created })
  } catch (err) {
    console.warn('[home] quick-add task failed:', err && err.message)
    root.update({ [key]: title })
    el.call('openNotification', {
      type: 'error',
      title: el.call('polyglot', 'workspace.home.quickAdd.taskFailed'),
      message: (err && err.message) || el.call('polyglot', 'workspace.home.tryAgain')
    })
  }
}

// Canonical row shape for the Home dashboard's two Notes-widget fetches
// (AppDocumentsWidget + DocumentsWidget below). Mirrors `documentsFetch`'s
// shape (`documents/functions/index.js`) field-for-field — SAME `from: 'docs'`
// query, so both widgets share that fetch's cache key/`~/documents` root-state
// slot whenever `/notes` mounts inline in the Home AppWindow PageSlot.
// `updatedAt` stays the RAW server ISO string deliberately (DATE-FMT-1): the
// bug was two DIFFERENT per-callsite transforms racing to write that shared
// slot, one of which pre-formatted the date and one of which didn't — a
// single shared mapper removes the shape drift, and every render site reads
// this raw value through the shared `shortDate` formatter (DocumentsList's
// Caption below) instead of re-formatting it at fetch time.
//
// `title` is the RAW title — '' for an untitled document, never a placeholder
// word. The row is DATA: the home tile writes `#note=<id>`, the documents editor
// seeds `editorDoc.title` from it and saveNote sends that title on update, and the
// documents package's `documentsFetch` maps the same slot — an English "Untitled"
// here could be written back to the record, and read as English on a Georgian board.
// The word is translated where the row is RENDERED (the tile's Title below;
// documents' own lists resolve `documents.notes.untitledShort` the same way).
const _mapDocRow = (r) => {
  const rawFolder = r.folderPath || r.folder || ''
  return {
    id: r.docId || r._id || r.id,
    title: r.title || '',
    folder: rawFolder === '/' ? '' : rawFolder.replace(/^\//, ''),
    type: r.type || 'note',
    source: r.source || 'member',
    updatedAt: r.updatedAt || '',
    author: r.author || ''
  }
}

// Notes widget quick-add (board §2 "notes/assets: chip grid (exists) +
// quick-new-note input row"). Unlike _submitTaskQuickAdd above, createNote
// (imported directly above, same cold-boot rationale as saveTicket) ALREADY
// appends the new row to root.documents/root.notes and sets selectedNoteId
// internally (functions/createNote.js) — so this orchestrator must NOT
// also append, or the note would render twice.
const _submitNoteQuickAdd = async (el, root) => {
  if (!root) return
  const title = (root._noteDraft || '').trim()
  if (!title) return
  root.update({ _noteDraft: '' })
  try {
    const id = await createNote.call(el, { title })
    if (!id) throw new Error('create returned no note')
  } catch (err) {
    console.warn('[home] quick-add note failed:', err && err.message)
    root.update({ _noteDraft: title })
    el.call('openNotification', {
      type: 'error',
      title: el.call('polyglot', 'workspace.home.quickAdd.noteFailed'),
      message: (err && err.message) || el.call('polyglot', 'workspace.home.tryAgain')
    })
  }
}

// Real-team-only birthday list — the `workspaceProject.birthdays`
// feed includes seeded placeholder names (e.g. Samuel L. Jackson,
// Frank Zappa) that aren't actual team members. Filter to only
// entries that match someone in `root.people` (by email if present,
// otherwise name) so the widget shows real milestones only.
const _teamBirthdays = (root) => {
  const all = Array.isArray(root?.birthdays) ? root.birthdays : []
  if (!all.length) return []
  const people = Array.isArray(root?.people) ? root.people : []
  if (!people.length) return []
  const emails = new Set(people.map((p) => (p.email || '').toLowerCase()).filter(Boolean))
  const names = new Set(people.map((p) => (p.name || '').trim().toLowerCase()).filter(Boolean))
  return all.filter((b) => {
    const e = (b?.email || '').toLowerCase()
    if (e && emails.has(e)) return true
    const n = (b?.name || '').trim().toLowerCase()
    return n && names.has(n)
  })
}

// Projects under the user's currently active workspace.
// THE APP SPLIT (docs/APP_SPLIT.md, WS-STUDIO-SPLIT-WORKSPACE-APPS-FROM-SITES):
// the home Projects widget is the WEBSITES widget — a workspace app (a module,
// an AI-built app) opens from the launcher and never shows as a project card.
const _activeWorkspaceProjects = (root) => {
  const all = Array.isArray(root?._projectsForActiveOrg) ? root._projectsForActiveOrg : []
  const activeWsId = root?.jwtClaims?.active_workspace_id || root?.workspace?.id
  const scoped = !activeWsId
    ? all
    : all.filter((p) => {
        const wsId = p?.workspace?.id || p?.workspace?._id || p?.workspace
        return String(wsId) === String(activeWsId)
      })
  const fns = _wsShared.functions
  return fns.splitProjectsByKind(scoped, fns.projectKindContext(root)).websites
}

// Rolling 7-day window from `now`, filtered to where the signed-in user
// is an attendee. Deduped by title+start+location since the upstream
// feed returns duplicate rows with different ids for the same booking.
const _weekEvents = (s) => {
  const me = s.root.currentUserEmail
  const now = Date.now()
  const endMs = now + 7 * 24 * 60 * 60 * 1000
  const seen = new Set()
  return (s.root.intranetEvents || [])
    .filter((e) => {
      const t = e.startAt ? new Date(e.startAt).getTime() : 0
      if (t < now || t >= endMs) return false
      if (Array.isArray(e.attendees) && !e.attendees.includes(me)) return false
      const key = `${e.title || ''}|${e.startAt || ''}|${e.location || ''}`
      if (seen.has(key)) return false
      seen.add(key)
      return true
    })
    .sort((x, y) => new Date(x.startAt).getTime() - new Date(y.startAt).getTime())
}

// Timezone CLUSTERS for the Team-timezones widget (2026-08-13 — replaces the
// flat per-member row list, 2026-08-12's fix for the even-older per-CITY
// tile bucketing). Groups by each member's real IANA `timezone` field
// (root.people[].timezone — the same field CalProfileModal / PersonPopoverCard
// / PersonCard already read for the identical purpose), NOT the free-text
// `location` string the two older versions keyed on — two members can spell
// the same city two ways, but an IANA zone id is an exact match key, and
// it's what the live-clock math (`_zoneTime`, components/IntranetRows.js)
// needs anyway. Sorted west→east by live UTC offset so the list reads like
// a world clock; capped
// at 8 zones (the widget body scrolls past that — card.Body sets
// `overflow: auto`).
let _tzGroupsCache = { peopleRef: null, me: null, value: [] }
const _teamTimezoneGroups = (s) => {
  const people = s.root.people
  const me = s.root.currentUserEmail
  if (_tzGroupsCache.peopleRef === people && _tzGroupsCache.me === me) {
    return _tzGroupsCache.value
  }
  const byTz = new Map()
  for (const p of people || []) {
    if (!p || p.email === me) continue
    const tz = p.timezone || null
    const key = tz || '__no-timezone__'
    let group = byTz.get(key)
    if (!group) {
      group = { timezone: tz, members: [] }
      byTz.set(key, group)
    }
    group.members.push(p)
  }
  const value = Array.from(byTz.values())
    .map((g) => ({ ...g, offsetMin: _tzOffsetMinutes(g.timezone) }))
    .sort((a, b) => a.offsetMin - b.offsetMin || b.members.length - a.members.length)
    .slice(0, 8)
  _tzGroupsCache = { peopleRef: people, me, value }
  return value
}

// Robust IANA→UTC-offset-minutes via the round-trip locale-string trick
// (format "now" INTO the zone, re-parse as a Date, diff against true UTC) —
// avoids depending on `timeZoneName: 'shortOffset'` text output for the SORT
// key (that format is still used, display-only, by the shared
// `el.call('zoneLabel', tz)` — shared/functions/zoneLabel.js, TZ-LABELS-1 —
// which components/IntranetRows.js's TzZone reads). Unresolvable zones (or
// the "no timezone set"
// bucket) sort last.
const _tzOffsetMinutes = (timezone) => {
  if (!timezone) return 9999
  try {
    const now = Date.now()
    const utcMs = now + new Date().getTimezoneOffset() * 60000
    const tzMs = new Date(new Date(now).toLocaleString('en-US', { timeZone: timezone })).getTime()
    return Math.round((tzMs - utcMs) / 60000)
  } catch {
    return 9999
  }
}

// Zone-label / live-time text and the TzCluster/TzAvatarItem presentational
// templates live in components/IntranetRows.js, next to their sibling
// Team-timezones components (AvatarBundle, AvatarBundleItem, CityMemberRow)
// — registered (not inlined via `childExtends: {}`) per frank's FA808, which
// flags an inline childExtends object as not independently re-extractable.

// (HOME_WORK / HOME_TEAM / HOME_INSIGHTS moved 2026-06-06 → SEARCH_APPS
// in workspace/functions/searchApps.js. The AppsWidget card that
// rendered them was removed from the home grid; the same list now
// renders as a dropdown when the global search gains focus, and items
// open inside the AppWindow below via el.call('openAppPage').)

// (`_homeAgents` — the headline Temo/Watcher/Caesar cards with their ticket
// and presence captions — was removed 2026-08-21 (FALSE-ZERO-HOME-NON-AGENT-
// KEYS-1): it had no caller since the hardcoded "youragents" widget left the
// grid (see the widget-id history below). Its two Family-D reads
// (`root.tickets.length`, `root.myTickets.length`) and the Watcher presence
// caption were therefore never rendered; the live Family-D defect was the
// `_ensureHomeTickets` loader above, which handed `root` to a function that
// needs an element and so never wrote `_userTickets` on Home.)

// ── THE CARD SURFACE (2026-09-26, DESIGN-WL-HOME-WIDGETS-1) ─────────────────
// Every home card is ONE neutral solid, painted by the frame
// (components/HomeWidgetFrame.js). A widget's colour — its family hue, or the
// user's "Add color" pick (context menu, `root._homeWidgetColors`) — tints its
// icon plate and its one visual only (the accent law, HOME_WIDGETS_SPEC.md
// §3), never the card and never the text a body paints.
//
//   `_NEUTRAL_CARD_BG` — the neutral card: the brand `card` theme fill
//   (design brief 4.2 item 1: an OPAQUE raised fill, gray3 in dark and gray15
//   in light), behind the `--home-card-bg` re-tint channel (a workspace
//   record such as GreenIG's green glass, or a module's chrome.card —
//   functions/workspaceProjects.js applyModuleCardVar). It replaced a
//   translucent white wash, `rgba(255, 255, 255, .06)` dark / `.62` light,
//   that composited to #505056 on the dark stage, where `inactive` ink read
//   2.80:1. `--theme-card-background` is the design system's own variable
//   for that theme and flips with `data-theme`, so ONE inline value serves
//   both schemes. __tests__/homeCardSurfaceTheme.test.js renders it.
const _NEUTRAL_CARD_BG = 'var(--home-card-bg, var(--theme-card-background))'

// The hue a card's BODY paints with: none. Under the accent law above a
// widget's colour lands on its plate and its visual (the frame's
// `--home-widget-hue`), and every body ink is the neutral twin on the neutral
// card — in both themes, whatever the user picked. The body call sites keep
// asking; the one answer is null.
const _cardHue = () => null

// ── Poster steps on ANY home card ────────────────────────────────────────
// Widget bodies written for the always-hued posters painted literal `black`
// / `white` inks and their alpha steps (`.06`/`.08` hover and plates,
// `.12` fills, `.35` rings, a `yellow` check). Every card is neutral by
// default since 2026-09-02, so each literal read in ONE theme only — black
// ink on the dark card, white segments on the light one. `_posterStep`
// returns the user-coloured card's own step (the `{ bg, ink, inkSoft,
// inkMeta, wash, fill }` entry, functions/homeWidgetHue.js `_hueEntry`),
// else the neutral card's twin, so a body never has to know whether its
// card is coloured. The twins are the entry's own grammar on the `title`
// ink: `wash` = ink .08 and `fill` = ink .12 (the entry's alphas), the
// soft and meta inks the adaptive `caption` / `inactive` tokens, `bg` the
// card fill itself. ONE card lookup, shared with HomePosterInk /
// HomePosterSoftInk (`posterCardHue`), so the three can never disagree.
const _NEUTRAL_STEP = {
  bg: _NEUTRAL_CARD_BG,
  ink: 'title',
  inkSoft: 'caption',
  inkMeta: 'inactive',
  wash: 'title.08',
  fill: 'title.12'
}
const _posterStep = (step) => (el) => {
  const h = posterCardHue(el)
  return h ? h[step] : _NEUTRAL_STEP[step]
}

// ── SPEC GEOMETRY REGISTRY (WS-HOME-RESET-LAYOUT-INPLACE-1) ──────
// Every widget's DECLARED default cell (x, y, w, h — the 12-column geometry
// its spec or manifest carries), captured at the layer that declares it: the
// static plane through `_widget(spec, body)`, the module plane through
// `HomeModuleWidget`'s `s.spec`. It is the source of the DEFAULT board — a
// fresh board and "Reset layout" — converted to an order + sizes by the same
// homeLayoutFromLegacy a saved 12-column board goes through
// (_defaultLayoutEntries). Declaration-time capture has no ordering hazard and
// touches no DOM.
const _SPEC_GEOMETRY = new Map()

// TENANT DIMENSION (module-memo census). A STATIC widget's id is a constant
// declared in this file — it has no tenant dimension, so it keys on the bare
// id. A MODULE widget's id is tenant DATA out of a manifest, and two
// workspaces can legitimately ship the same id with different geometry, so
// those entries carry the workspace scope in the key. Nothing is ever torn
// down: a stale entry can only be READ through `_specLayoutFor`, which walks
// the ACTIVE workspace's catalog and asks for the scoped key first.
const _specGeometryKey = (id, root) => (root ? `${_wsScope(root)}::${id}` : id)

// Record one widget's DECLARED cell. w/h fall back to the module plane's
// defaults (4 × 3). Returns the spec so the call can sit inline.
const _recordSpecGeometry = (spec, root) => {
  if (!spec || !spec.id) return spec
  _SPEC_GEOMETRY.set(_specGeometryKey(spec.id, root), {
    x: spec.x != null ? Number(spec.x) : null,
    y: spec.y != null ? Number(spec.y) : null,
    w: Number(spec.w) || null,
    h: Number(spec.h) || null
  })
  return spec
}

// The declared cells of the ACTIVE workspace's catalog, 12-column shaped.
// Catalog-scoped, so a foreign workspace's module widgets can never leak into
// a reset. Sparse-safe: a widget declared without x/y flows after the placed
// ones instead of being pinned to (0, 0).
const _specLayoutFor = (root, catalog = homeCatalogFor(root)) => {
  const out = []
  for (const w of catalog) {
    // Scoped first (a module widget of THIS workspace), then the bare id (a
    // static widget declared in this file). Never the other workspace's entry.
    const geo = _SPEC_GEOMETRY.get(_specGeometryKey(w.id, root)) || _SPEC_GEOMETRY.get(w.id)
    if (!geo) continue
    const entry = { id: w.id }
    if (geo.x != null) entry.x = geo.x
    if (geo.y != null) entry.y = geo.y
    if (geo.w) entry.w = geo.w
    if (geo.h) entry.h = geo.h
    out.push(entry)
  }
  return out
}

// ── Widget factory ───────────────────────────────────────────────
// One page-declared widget: the shared frame (components/HomeWidgetFrame.js)
// over a state row built from its spec, plus the widget's own body. Module /
// app / AI widgets render through the SAME frame (HomeModuleWidget below), so
// the two planes can no longer drift apart.
//
// spec: { id, title, icon, openRoute?, openCall?, action?, hue?, size?,
//         minSize?, x?, y?, w?, h? }
//   - `app-*` ids render as one-cell app tiles in the grid (decision H1 C,
//     the interim until the App strip ships).
//   - x / y / w / h are the widget's DEFAULT place on a fresh board — read
//     through the spec-geometry registry above and converted by the same
//     homeLayoutFromLegacy a saved 12-column board goes through.
//   - action: `{ icon, label, route? | set? }` — the one square button;
//     without one the frame offers ↗ Open when `openRoute` is set.
const _STATIC_WIDGET_ROWS = new Map()
const _staticWidgetRow = (spec) => ({
  id: spec.id,
  title: spec.title || '',
  icon: spec.icon || 'star',
  kind: String(spec.id).indexOf('app-') === 0 ? 'app' : 'widget',
  size: spec.size || null,
  minSize: spec.minSize || null,
  hue: spec.hue || null,
  openRoute: spec.openRoute || null,
  openCall: spec.openCall || null,
  action: spec.action || null,
  help: spec.help || null,
  spanAt: spec.spanAt || null,
  spec: { x: spec.x, y: spec.y, w: spec.w, h: spec.h }
})
const _widget = (spec, body) => {
  // Runtime source for this widget's DEFAULT cell — see _SPEC_GEOMETRY.
  _recordSpecGeometry(spec)
  const row = _staticWidgetRow(spec)
  _STATIC_WIDGET_ROWS.set(spec.id, row)
  return {
    extends: 'HomeWidgetFrame',
    state: row,
    // Not BUILT while the board keeps it off (qa, unreal home 2026-10-02:
    // the activity widget + heatmap were built on every load, then hidden -
    // 0.6-0.9 s of the home build). Same rule as `hide:` below; restoring it
    // from the customize drawer flips this and builds it then.
    if: (el, s) => !el.call('homeWidgetFrameHidden', s) && !_moduleShadows(s.root, s.id),
    // A module that ships a widget under a house id replaces the page's own
    // copy while it is installed — one owner per id on the board.
    hide: (el, s) => el.call('homeWidgetFrameHidden', s) || _moduleShadows(s.root, s.id),
    Body: body || {}
  }
}

// ── App shortcuts — the App strip (HOME_WIDGETS_SPEC §1, decision H1 A) ──
// The built-in app shortcuts are never grid items: they render in the ONE App
// strip between the greeting and the grid (components/HomeAppStrip.js). Each
// keeps its id, its hide pref and its place in the layout ORDER — x / y are
// its default place in a fresh board's reading order (the strip reads the app
// entries' relative order out of root._homeLayout); every shortcut is one
// 2 × 2 legacy cell (an S entry) there.
const _APP_SHORTCUTS = [
  {
    id: 'app-studio',
    x: 4,
    y: 0,
    title: 'workspace.widget.appStudio.title',
    icon: 'layers',
    openRoute: '/studio'
  },
  {
    id: 'app-tickets',
    x: 6,
    y: 0,
    title: 'workspace.widget.appTickets.title',
    icon: 'check',
    openRoute: '/tasks'
  },
  {
    id: 'app-chat',
    x: 8,
    y: 0,
    title: 'workspace.widget.appChat.title',
    icon: 'chat',
    openRoute: '/chat'
  },
  {
    id: 'app-calendar',
    x: 10,
    y: 0,
    title: 'workspace.widget.appCalendar.title',
    icon: 'date outline',
    openRoute: '/calendar'
  },
  {
    id: 'app-reminders',
    x: 4,
    y: 6,
    title: 'workspace.widget.appReminders.title',
    icon: 'clockOutline',
    openRoute: '/reminders'
  },
  {
    id: 'app-meet',
    x: 4,
    y: 2,
    title: 'workspace.widget.appMeet.title',
    icon: 'video',
    openRoute: '/meet'
  },
  {
    id: 'app-broadcast',
    x: 6,
    y: 2,
    title: 'workspace.widget.appBroadcast.title',
    icon: 'bell',
    openRoute: '/announcements'
  },
  {
    id: 'app-documents',
    x: 8,
    y: 2,
    title: 'workspace.widget.appDocuments.title',
    icon: 'document',
    openRoute: '/notes'
  },
  {
    id: 'app-agents',
    x: 10,
    y: 2,
    title: 'workspace.widget.appAgents.title',
    icon: 'star',
    openRoute: '/agents'
  },
  {
    id: 'app-team',
    x: 4,
    y: 4,
    title: 'workspace.widget.appTeam.title',
    icon: 'people',
    openRoute: '/team-and-agents'
  },
  {
    id: 'app-analytics',
    x: 6,
    y: 4,
    title: 'workspace.widget.appAnalytics.title',
    icon: 'analytics',
    openRoute: '/workspace-analytics'
  },
  {
    id: 'app-people',
    x: 8,
    y: 4,
    title: 'workspace.widget.appPeople.title',
    icon: 'user',
    openRoute: '/team-members'
  },
  {
    id: 'app-infra',
    x: 10,
    y: 6,
    title: 'workspace.widget.appInfra.title',
    icon: 'server',
    openRoute: '/infra'
  },
  {
    id: 'app-health',
    x: 10,
    y: 4,
    title: 'workspace.widget.appHealth.title',
    icon: 'eye',
    openRoute: '/system-status'
  }
].map((spec) => {
  const full = { ...spec, w: 2, h: 2 }
  _recordSpecGeometry(full)
  const row = _staticWidgetRow(full)
  _STATIC_WIDGET_ROWS.set(full.id, row)
  return row
})

// The App strip's items: the shortcuts not hidden (an installed module that
// ships the same id owns it) and any module widget that ships as an icon only,
// in the saved layout ORDER. "All apps" closes the row (HomeAppStrip).
const _appStripRows = (root) => {
  const hidden = (root && root._homeHiddenWidgets) || {}
  const layout = _currentHomeLayout(root)
  const place = (id) => {
    const at = layout.findIndex((e) => e && e.id === id)
    return at < 0 ? layout.length : at
  }
  const rows = []
  for (const row of _APP_SHORTCUTS) {
    if (hidden[row.id] === true || _moduleShadows(root, row.id)) continue
    rows.push({ ...row, key: row.id })
  }
  for (const w of _allModuleWidgetsFor(root)) {
    if (!homeWidgetIsApp(w) || hidden[w.id] === true) continue
    rows.push({ ...w, kind: 'app', key: w.id })
  }
  return rows.sort((a, b) => place(a.id) - place(b.id))
}

// THE SETUP WIZARD's apps step (functions/onboardingWizard.js): every app the
// App strip can show — the house shortcuts and the installed apps' icon
// widgets, the same two sources as `_appStripRows` — each with whether it
// shows now. A hidden app is offered only when a click could bring it back
// (`_widgetRestorable`, the drawer's own test), in the saved layout ORDER.
const _onboardingAppRows = (root) => {
  const hidden = (root && root._homeHiddenWidgets) || {}
  const layout = _currentHomeLayout(root)
  const place = (id) => {
    const at = layout.findIndex((e) => e && e.id === id)
    return at < 0 ? layout.length : at
  }
  const rows = []
  const offer = (id, row) => {
    const shown = hidden[id] !== true
    if (!shown && !_widgetRestorable(id, root)) return
    rows.push({ id, title: row.title, icon: row.icon, openRoute: row.openRoute, shown })
  }
  for (const row of _APP_SHORTCUTS) {
    if (!_moduleShadows(root, row.id)) offer(row.id, row)
  }
  for (const w of _allModuleWidgetsFor(root)) {
    if (homeWidgetIsApp(w)) offer(w.id, w)
  }
  return rows.sort((a, b) => place(a.id) - place(b.id))
}

// The wizard's picks in ONE write — `{ [id]: true | false }`, shown or
// hidden. The same halves as the board's own hide (`_persistWidgetHide` +
// `_showWidgetHidden`) and restore (`_restoreWidget`): the user's choices are
// recorded, the hidden map flips once, the durable subset persists once —
// never a write per tile. No motion: the wizard covers the board.
const _setAppsShown = (root, picks, sdk) => {
  if (!root || typeof root.update !== 'function' || !picks || typeof picks !== 'object') {
    return false
  }
  const cur = { ...(root._homeHiddenWidgets || {}) }
  let layout = _currentHomeLayout(root)
  let moved = false
  let changed = false
  for (const id of Object.keys(picks)) {
    if (picks[id] === true && cur[id] === true && _widgetRestorable(id, root)) {
      const next = homeLayoutEnsure(layout, [{ id, size: _defaultWidgetSize(root, id) }])
      if (next !== layout) {
        layout = next
        moved = true
      }
      cur[id] = false
      _recordUserHidden(root, id, false)
      changed = true
    } else if (picks[id] === false && cur[id] !== true) {
      cur[id] = true
      _recordUserHidden(root, id, true)
      changed = true
    }
  }
  if (!changed) return false
  root.update(
    moved ? { _homeHiddenWidgets: cur, _homeLayout: layout } : { _homeHiddenWidgets: cur }
  )
  _saveHiddenWidgets(root, _durableHiddenMap(root), sdk)
  if (moved) _persistHomeLayout(root, sdk)
  return true
}

// The Dock's and the Rail's items — ONE factory body for both (the strip
// re-reads its rows when the hidden map or the saved order changes).
const _appStripChildren = (root) => {
  void root._homeHiddenWidgets
  void root._homeLayout
  return _appStripRows(root)
}

// Does the Dock (style 'dock') or the Rail (style 'rail') step aside? The
// person's look turns the strip off, or picks the other style (the Rail's own
// rule) — AND a strip with no app left to show steps aside too: its capsule
// would hold "All apps" alone, one lone launcher pill on the page head
// (measured on dev Vault 2026-10-03, an app-mode workspace whose default
// hides every house shortcut: a 57 × 57 capsule with one grid glyph). The
// header's search launcher and the Apps menu stay. The look half is the
// components' own gate (functions/homeLook.js homeAppStripLookHidden), read
// here too because a mount-site function REPLACES the component's (functions
// never merge).
const _appStripHidden = (el, root, style) =>
  !!el.call('homeAppStripLookHidden', root, style) || _appStripChildren(root).length === 0

// A TAB OTHER THAN THE DASHBOARD HOLDS THE HOME (functions/homeView.js — Main,
// Chat, Present, Build): the dashboard's own rows — the App strip, its Rail,
// the boards row — step aside for it. A host without the tabs (no homeViewOf)
// reads as the dashboard.
const _tabHoldsHome = (el, root) => {
  const tab = el.call('homeViewOf', root)
  return typeof tab === 'string' && tab !== 'dashboard'
}

// The Personalise panel's app list: the same rows, each with its neighbours'
// ids for the arrow buttons (components/HomePersonalize.js).
const _personalizeAppRows = (root) => {
  const rows = _appStripChildren(root)
  return rows.map((row, i) => ({
    ...row,
    key: row.id,
    prevId: i > 0 ? rows[i - 1].id : null,
    nextId: i < rows.length - 1 ? rows[i + 1].id : null
  }))
}

// ── Module widget host ───────────────────────────────────────────
// Module / app / AI widgets render through the SAME frame as the page's own
// widgets (components/HomeWidgetFrame.js), over their state row: the manifest
// entry as the module loaders stashed it ({ id, title, icon, body, moduleId,
// spec, size?, hue?, action?, help? } — see _allModuleWidgetsFor).
//
// Body content mounts via el.set() keyed by moduleId/id: a string body extends
// a module component (validated by the manifest normalizer and in context by
// stash time — the stash update that renders this host happens AFTER the
// injection), an object body mounts inline.
const _mountModuleWidgetBody = (el, s) => {
  // The module context is looked up BEFORE the guard and its PRESENCE is part
  // of the signature. The board paints at shell boot, and
  // `_ensureModuleDsContext` builds the module context lazily — so a body can
  // mount against the SHELL context first. With a `moduleId/id` signature that
  // body was never mounted again once the context landed: it kept the shell's
  // tokens and echoed the module's raw translation keys (aihouse home widgets,
  // 2026-09-23). Now the signature changes exactly once, 0 → 1, so the body
  // re-mounts exactly once. It cannot loop: the signature is written before
  // `el.set`, so the update that `set` triggers re-enters with an equal
  // signature and returns. An AI-created body never reads the module context,
  // so its signature stays context-free.
  // CSS-hidden hosts still create children. Wait for restoration before the
  // first body mount, and before the lazy module-context build below, so a
  // hidden widget neither runs its data hooks nor builds a context at boot.
  if (s.root?._homeHiddenWidgets?.[s.id]) return
  // A pinned app widget (an AI row of kind 'widget' resolved to its module)
  // mounts through the module path below, under its module's context.
  const pinnedApp = !!(s.aiGenerated && s.kind === 'widget' && s.moduleId && s.body)
  const modCtx = s.aiGenerated && !pinnedApp ? null : s.root?._moduleDsCtxGet?.(s.moduleId)
  // A PINNED app widget's params are part of its identity: update_widget and
  // "Change with AI" rewrite them in place, and the body reads them once, at
  // mount (frame contract v1 `scope.params`). Without them in the signature
  // the card kept drawing the old params until a reload (dev 2026-10-04: a
  // testnet card renamed "(Mainnet)" still listed testnet nodes).
  const paramsSig = pinnedApp ? `/${JSON.stringify(homeWidgetParams(s))}` : ''
  const sig = `${s.moduleId || ''}/${s.id || ''}/${modCtx ? 1 : 0}${paramsSig}`
  if (el.__moduleBodySig === sig) return
  el.__moduleBodySig = sig
  const body = s.body
  try {
    // AI-CREATED plane (tickets/opus.md): the body is DATA — a fixed-vocabulary
    // block list — not a module component key and not a component definition.
    // It mounts the ONE reviewed interpreter (components/WidgetRenderer.js),
    // which reads the def off this host's own state (body / dataRecipe /
    // viewerCapability). Spreading the body as an element definition here —
    // what the module branch below does — would treat `blocks` as a child key
    // and, worse, would make an author-supplied object into a component
    // definition. It is never evaluated, never extended by name, never eval'd.
    if (s.aiGenerated && !pinnedApp) {
      // A RECORDS row (kind 'records', WIDGET-TOOLS-RECORDS-SHAPE-1) is one app
      // collection read live for THIS viewer: it mounts the records renderer
      // the board and the thread share — AssistantRecordsBlock reads `records`
      // off this host's state and asks ai.boards.records under the viewer's
      // grants. A viewer the server already refused keeps WidgetRenderer,
      // which draws that notice instead of a read that would only be refused.
      if (
        s.kind === 'records' &&
        s.records &&
        typeof s.records === 'object' &&
        s.viewerCapability &&
        s.viewerCapability.allowed === true
      ) {
        el.set({ extends: 'AssistantRecordsBlock' })
        return
      }
      // A pinned BLOCK carries its values (homePinnedHostRow `blockData`):
      // seeded into the AI-widget data bag BEFORE the interpreter mounts, so
      // its first run reads them (no recipe → loadAiWidgetData fetches nothing).
      if (s.kind === 'block' && s.blockData && s.root && typeof s.root.update === 'function') {
        const bag = s.root._aiWidgetData || {}
        s.root.update(
          {
            _aiWidgetData: {
              ...bag,
              [s.id]: {
                sig: 'pinned',
                loading: false,
                settled: true,
                denied: false,
                error: null,
                data: s.blockData
              }
            }
          },
          { preventFetch: true }
        )
      }
      el.set({ extends: 'WidgetRenderer' })
      return
    }
    const def = typeof body === 'string' ? { extends: body } : { ...(body || {}) }
    // Thread the module's OWN design-system context into the widget body —
    // the host is a shell-page child, so without this every module token
    // inside a home widget resolved against the SHELL config and silently
    // dropped ("widgets lost styles", bellforge 2026-08-12). Same
    // non-enumerable-context contract as the wrapped module pages
    // (workspaceProjects.js _wrapModulePages); getter closure because the
    // loader chunk's module context never survives root.update clones.
    // Passed as a plain property on the set() payload — same contract as
    // PageSlot's host.set (a non-enumerable prop would be dropped by any
    // spread/clone inside set). The def object is ephemeral (never cached,
    // never ridden onto root state), so enumerability costs nothing here.
    // WS-MODULE-STATE-SCOPE-1: the module context also carries the module's
    // STATE scope now (a plugin on `modCtx`), so a widget body reads and writes
    // the SAME module-local plane the module's pages do — with no def change,
    // which matters here because this def is `{ extends: <moduleComponent> }`
    // and any def key the shell added would shadow the component's own.
    // (`modCtx` is resolved above, before the signature guard.)
    if (modCtx) {
      def.context = modCtx
      // WS-MODULE-CTX-DROPS-APP-GLOBALSCOPE-1 (live leg) — same scope-root
      // parity as _wrapModulePages: the widget host is a shell-page child, so
      // without a def-level `scope` the body's `__scope` reads resolve the
      // shell chain, never the app's own globalScope bag. Merge order matches
      // the page wrap: the body's own scope keys win over the bag.
      if (modCtx.globalScope) {
        def.scope =
          typeof def.scope === 'object' && def.scope !== null
            ? Object.assign({}, modCtx.globalScope, def.scope)
            : modCtx.globalScope
      }
    }
    // FRAME CONTRACT v1: the body reads `el.scope.params` (the schema's
    // defaults + this instance's values — functions/homeWidgetFrame.js
    // homeWidgetParams), `el.scope.widgetId` and `el.scope.size` (the size at
    // mount; the live size is the card's data-widget-size). A COPY of whatever
    // scope the body had: the app's shared globalScope bag is never written
    // (every widget of the app shares it).
    def.scope = Object.assign({}, def.scope || null, {
      params: homeWidgetParams(s),
      widgetId: s.id,
      size: homeWidgetFrameSize(s)
    })
    el.set(def)
  } catch (err) {
    console.warn('[home] module widget body mount failed:', s.id, err)
  }
}

// NOTE: the module plane no longer resolves its own hue pair. Both plates a
// module widget can show — the header tile and the icon-mode squircle — now
// extend the shared `AppHueTile`, which does the whole resolution itself
// (house id/route → the widget's own manifest `hue`/`spec.iconBg` → the
// adaptive overlayStrong/title pair). The former `_moduleWidgetHue` helper
// paired the hue's ink with its 8% `wash` over a NEUTRAL card, which is the
// one combination the poster palette is not art-directed for — see the
// IconTile note below.

const HomeModuleWidget = {
  extends: 'HomeWidgetFrame',
  // Defer the whole frame, including its controls/effects, until first shown.
  // Keep it mounted thereafter so hiding/restoring preserves body state.
  if: (el, s) => {
    if (!el.call('homeWidgetFrameHidden', s)) el.__homeWidgetMounted = true
    return !!el.__homeWidgetMounted
  },
  // The launcher's duplicate check prefers a module host over a page widget
  // with the same id (see _findItemById).
  attr: { 'data-module-widget': 'true' },
  // The widget's DEFAULT cell for a reset or a fresh board (_SPEC_GEOMETRY).
  onRender: (el, s) => {
    const spec = s.spec || {}
    _recordSpecGeometry({ id: s.id, x: spec.x, y: spec.y, w: spec.w || 4, h: spec.h || 3 }, s.root)
  },
  Body: {
    // Restoring a hidden widget re-runs the mount below (see its guard).
    subscribeTo: ['_homeHiddenWidgets'],
    onCreate: (el, s) => _mountModuleWidgetBody(el, s),
    onUpdate: (el, s) => _mountModuleWidgetBody(el, s)
  }
}

// A saved cell whose widget has not arrived yet (its module is still loading):
// the frame's geometry with skeletons in it, so the board paints its final
// arrangement at once and the late widget lands in place (zero shift).
// Known gap: a manifest `spanAt` is not on the client before its module, so
// at a step the manifest overrides the skeleton holds the size's plain span
// and the card grows on arrival (WS-HOME-BOARD-SHEET-RAW-DOM-AND-PENDING-SPANAT-1).
const HomeWidgetPending = {
  extends: 'HomeWidgetFrame',
  attr: { 'aria-busy': 'true' },
  // No resize while the widget is still on its way: its minimum size is not
  // known yet, and the handle would let a skeleton go below it.
  HomeWidgetResizeHandle: { hide: true },
  Header: {
    HomeWidgetPlate: { Icon: { hide: true } },
    TitleRow: {
      Title: { hide: true },
      SkeletonRow: { width: '50%' }
    }
  },
  Body: {
    Skeleton: { height: 'C', width: '40%', round: 'radiusControl' },
    SkeletonRow: { marginTop: 'auto', width: '30%' }
  }
}

// ── mail-inbox widget resolvers (architecture/MAIL.md §3.6) ────────────────
// The unified-inbox list slot. Mail keys one slot per (account, folder) pair
// and `all` is the unified inbox — the literal is mail's own
// `mailListKey(null, 'inbox')`, restated here rather than imported because
// this file must not pull the LAZY mail bundle into the home page's own chunk.
// mail/__tests__ pins the two against each other.
const MAIL_INBOX_LIST_KEY = 'all|inbox'

// Newest-first already: mail's stream reducer sorts every slot on write
// (`_sortRows`, by `lastMessageAt` descending), so this slices rather than
// re-sorting a list the producer has already ordered.
const _mailInboxRows = (s) => {
  const slot = (s?.root?.mailThreads || {})[MAIL_INBOX_LIST_KEY] || {}
  return Array.isArray(slot.rows) ? slot.rows.slice(0, 5) : []
}

// The SAME number the header envelope's dot and the navbar badge read — one
// producer (mail's stream reducer), so the card and the badge can never
// disagree about how much unread mail there is.
const _mailUnread = (s) => Number(s?.root?.navUnreadCounts?.mail || 0)

// Catalog of every widget that lives on the home page — THE registry,
// functions/homeWidgetCatalog.js (`homeWidgetCatalog`, with the notes on
// `subtitle` and the literal-key rule). The admin panel pages read the same
// list, so a widget added there is offered to the admins who set the
// workspace defaults too.
const HOME_CATALOG = homeWidgetCatalog()

// The catalog for the ACTIVE workspace: the static house set + the active
// MODULE's widgets (root.workspaceProject.widgets ∪ root.workspaceApps[]
// .widgets — stashed by the loaders from each manifest; ids validated by the
// normalizer /^[a-z0-9-]+$/). Module widgets surface in the customize drawer /
// title lookups / visibility resolvers ONLY while their module is live — a
// module-less workspace sees the house set alone, so no client widget is ever
// offered outside its workspace. (The legacy v1 inline plane is gone — WS6
// migration, 2026-07-30.)
const homeCatalogFor = (root, moduleWidgets = _allModuleWidgetsFor(root)) => {
  // A house row may name the ROUTE that gates it (`gateRoute`). The predicate
  // is `navItemVisible` — the same one the navbar, the launcher and the header
  // envelope hide on — so one flag flip at /flags moves every surface of an
  // app together, this widget included. A row without `gateRoute` is
  // ungated, which is every other house widget.
  //
  // It gates the CATALOG, not just the render, because the catalog is what the
  // customize drawer offers and what the hidden-map resolver loops: filtering
  // here means a workspace with the app off is never offered the card, never
  // reveals it from a saved board, and never reserves its grid cell.
  const house = HOME_CATALOG.filter((w) => !w.gateRoute || shellNavItemVisible(root, w.gateRoute))
  if (!moduleWidgets.length) return house
  return [
    ...house,
    // `subtitle` rides along so an installed app's widget is as tellable
    // apart in the Customize drawer as a house row is — the drawer reads the
    // catalog row, not the render spec.
    ...moduleWidgets.map((w) => ({
      id: w.id,
      title: w.title || w.id,
      icon: w.icon || 'star',
      subtitle: w.subtitle || null
    }))
  ]
}

// EVERY module-declared widget for the active workspace, across BOTH module
// planes, each annotated with its owning module id (the body-mount signature
// key):
//   1. the singular takeover module — root.workspaceProject.widgets (stashed
//      by loadWorkspaceProject from the manifest), and
//   2. the ADDITIVE apps — root.workspaceApps[].widgets (stashed per app by
//      loadWorkspaceApps at functions/workspaceProjects.js:1118).
// Plane 2 was a dangling producer until 2026-07-25: the loader collected each
// installed app's manifest widgets onto the stash, but every home resolver
// (catalog, project-scope gate, render host, resync signal) read ONLY plane 1
// — so a workspace running additive apps (settings.workspaceApps) could pin
// e.g. 'bi-revenue' in home_default_panels and nothing would render: the id
// had no catalog entry, produced no host element, and the reveal loop never
// visited it. One resolver, consumed everywhere, keeps the two planes in
// lock-step from now on. First-listed wins on a duplicate widget id (matches
// the loader's inject-skip-existing collision policy).
const _allModuleWidgetsFor = (root) => {
  const out = []
  const seen = new Set()
  const push = (widgets, moduleId) => {
    if (!Array.isArray(widgets)) return
    for (const w of widgets) {
      if (!w || !w.id || seen.has(w.id)) continue
      // Optional per-widget visibility gate — `visibleFn` names a MODULE
      // function (resolved in its owning app's scope) called as
      // fn(widgetId, root). The module decides with its own model (e.g.
      // bellforge personas, Nika 2026-08-16 "home widgets should be
      // personalized to each user persona"); the shell stays
      // persona-blind. FAIL-OPEN when the fn hasn't injected yet (boot
      // race) — a widget flashing then hiding beats one lost forever.
      // functions/moduleWidgetGate.js: the gate's `this.call` reaches the
      // shell bridges (moduleRole / moduleCan) like a real el.call, and the
      // evaluation reads root.workspace.access first, so this factory
      // re-runs when the member's access lands (G-H).
      if (!moduleWidgetGateAllows(root, w, moduleId, true)) continue
      seen.add(w.id)
      // `key` rides along so the ModuleWidgets children factory reconciles by
      // widget id instead of array position (rawChild.key — the childrenAs:
      // 'state' contract, element/create.js key resolution). Positional keys
      // meant an order change or shrink remapped widget BODIES across
      // surviving hosts while their gs-ids stayed put — the double-registered
      // cms/crm widgets from the BOOT-RESHUFFLE trace.
      out.push(homeWidgetRow(w, moduleId))
    }
  }
  const wp = root?.workspaceProject
  push(wp?.widgets, wp?.id)
  const apps = Array.isArray(root?.workspaceApps) ? root.workspaceApps : []
  for (const app of apps) push(app?.widgets, app?.id)
  // PLANE 3 — AI-CREATED widgets (tickets/opus.md "Per-workspace AI-CREATED
  // widgets"): per-workspace user content with no module behind it, fetched by
  // loadPrivateData's dashboard-prefs chain into root.workspaceAiWidgets and
  // filtered to the LIVE workspace by aiWidgetsFor (rows carry their own
  // workspaceId — a mid-switch response can never paint the next tenant's
  // board). Unioned LAST and through the SAME `push`, so:
  //   * every existing consumer (catalog, gate bookkeeping, render host,
  //     resync signal, Customize drawer) picks them up with no second path —
  //     the whole point of this being one resolver;
  //   * a house or module widget ALWAYS wins an id collision (`seen`), and the
  //     server's `ai-` id namespace means one cannot be constructed anyway.
  // `moduleId: null` marks the plane for the body mount + subtitle below: an
  // AI widget's body is DATA drawn by WidgetRenderer, never a module component
  // key.
  // A PINNED board item (kind 'widget' / 'block', functions/homePinnedItems.js)
  // is drawn as the board drew it: a widget row resolves to the installed
  // app's own widget (read off the module rows above) with the pinned params,
  // a block row carries the one reviewed block definition and its values.
  // `push` stamps the moduleId it is given, so each row goes with its own: a
  // resolved app widget carries its app's, every other AI row null.
  for (const w of aiWidgetsFor(root)) {
    const host = homePinnedHostRow(w, out)
    push([host], (host && host.moduleId) || null)
  }
  return out
}

// The human name of the app that OWNS a module widget — its own declared
// navbar label (crm → 'CRM', bi → 'Analytics'), which is what the shell's nav
// already calls it. Empty string when the owning app can't be resolved (the
// takeover-module plane, or a stash that hasn't landed) so callers can treat
// "no label" as "say nothing" rather than printing an opaque project id.
const _moduleAppLabel = (root, moduleId) => {
  if (!moduleId) return ''
  const apps = Array.isArray(root?.workspaceApps) ? root.workspaceApps : []
  const app = apps.find((a) => a && a.id === moduleId)
  const nav = Array.isArray(app?.navbar) ? app.navbar : []
  const entry = nav.find((n) => n && typeof n.label === 'string' && n.label)
  return (entry && entry.label) || ''
}

// A module widget's header subtitle.
//
// Two INSTALLED apps can legitimately ship same-titled widgets, and on
// natali/ws_EEYlHGTvZ8 two of them also compute the same number from the same
// workspace records: crm's `crm-pipeline` and bi's `bi-pipeline` both render
// "Pipeline — GEL 102,700 open pipeline". The board therefore showed what read
// as ONE widget drawn twice. It is not: every gs-id on that board is unique
// (verified live) and `_allModuleWidgetsFor` already dedupes by widget id via
// `seen`, so there is nothing to de-duplicate — the resolved set was correct
// and the RENDER was ambiguous. Deduping ids here would have deleted a real,
// separately-installed app's widget.
//
// So: name the owner instead, and only when it actually disambiguates —
// a widget whose title is unique on the visible board stays uncluttered. The
// app's OWN declared `spec.subtitle` always wins. Generic by construction: no
// widget id, app id or title is named here, and the ambiguity is recomputed
// from live state, so it appears and disappears with install/uninstall and
// with the user's own show/hide choices.
const _moduleWidgetSubtitle = (s) => {
  const own = s.spec?.subtitle
  if (own) return own
  // AI-CREATED plane: there is no owning app to name, and "who made this" is
  // the most useful thing this line can carry — a member seeing a widget on
  // their board that no app installed should be able to tell at a glance that
  // the assistant built it, and for whom. Unconditional (not
  // ambiguity-gated like the module case below): provenance is the point.
  const ai = aiWidgetOwnerLabel(s)
  if (ai) return ai
  const root = s.root
  const hidden = root?._homeHiddenWidgets || {}
  const title = s.title || s.id
  let sharing = 0
  for (const w of _allModuleWidgetsFor(root)) {
    if (hidden[w.id]) continue
    if ((w.title || w.id) === title) sharing++
  }
  return sharing > 1 ? _moduleAppLabel(root, s.moduleId) : ''
}

// The widgets a fresh home shows — the registry's own list
// (functions/homeWidgetCatalog.js `homeDefaultVisibleWidgets`); every other
// catalog row is opt-in via the Customize drawer.
const _DEFAULT_VISIBLE_WIDGETS = homeDefaultVisibleWidgets()
const _DEFAULT_HIDDEN_WIDGETS = HOME_CATALOG.map((w) => w.id).filter(
  (id) => !_DEFAULT_VISIBLE_WIDGETS.includes(id)
)

// The GLOBAL default hidden map for the house rows of THIS workspace — the
// branch both visibility resolvers reach when the workspace names no
// `home_default_panels` of its own and the session is not a demo.
//
// On a general workspace it is `_DEFAULT_HIDDEN_WIDGETS` mapped to `true`,
// unchanged. On an APP-MODE workspace (`settings.workspaceModule.installMode
// === 'app'`) every house BOARD card is hidden instead: that workspace exists
// to run one installed app, so its default board is the MODULE's declared
// widgets and nothing else (Nika, 2026-09-10). The page FRAME — the greeting
// with its summary and the Dock's house apps (catalog `place`) — keeps its
// defaults there too (Nika 2026-10-03, "Greeting + house dock"). `_foldAppWidgetDefaults` runs immediately
// after this at both call sites and turns the installed app's own default
// widgets back on, so the board is never empty by construction.
//
// This is a DEFAULT, deliberately — never a write. The house rows all stay in
// the Customize drawer (`_drawerHiddenRows` lists exactly what this map hides),
// and adding one back is an ordinary gesture that persists, because a stored
// `false` then DISAGREES with this base and `persistableHiddenMap` keeps it.
// The decision itself lives in functions/homeDefaultWidgets.js so a test can
// pin it without a browser.
const _globalDefaultHidden = (root) =>
  houseDefaultHiddenMap({
    houseIds: HOME_CATALOG.map((w) => w.id),
    defaultHiddenIds: _DEFAULT_HIDDEN_WIDGETS,
    appMode: isAppModeWorkspace(root),
    frameIds: HOME_CATALOG.filter((w) => w.place).map((w) => w.id)
  })

// Curated showcase set for demo sessions (root.isDemo === true).
// Widgets included are always populated in the seeded demo sandbox:
//   greeting   — always present (user profile)
//   received   — demo user's assigned tickets (seeded server-side)
//   youragents — hardcoded AI-agent cards (s1m0ne, Temo, Caesar, Watcher, +
//                "Create agent"), the primary AI-showcase surface
//   assets     — the 2 seeded demo documents
//   projects   — the seeded "Acme Landing" studio project (server af078c7e
//                seeds it at demo assignment)
//   events     — "Coming up" — the seeded calendar events, re-dated to the
//                claim week with the visitor as invitee (same server commit)
//   next-up    — "Next up" — the same seeded events, the next one first
//   analytics  — "Active right now" live counter
// Dropped vs previous set:
//   agents — Team & agents' numeral body (2026-08-12) reads live headcount;
//             with no demo team/agent data it would show "0" on both sides,
//             a hollow-looking card same as the excluded teamtime/broadcast
//             below — left out of the curated demo set for the same reason,
//             not because of the old embedded-canvas empty-body bug.
//   given  — demo user reports all tickets but delegates none, so the card
//             always shows "You haven't delegated any open tasks".
// Excluded: teamtime / broadcast (empty in a fresh demo and would render
// hollow cards, defeating the showcase purpose).
const _DEMO_VISIBLE_WIDGETS = [
  'greeting',
  'next-up',
  'received',
  'youragents',
  'assets',
  'projects',
  'events',
  'analytics'
]

// Dashboard-preference schema version. Bumping this abandons every user's
// saved panel set on next load (a one-time reset-everyone to the current
// default) — see the version gate in resolveSavedPrefsSource
// (functions/homePrefsBootSource.js) and the stamp in _savePrefsPatch. v2 = the greeting-only default rollout (2026-06-16).
const CURRENT_DASHBOARD_V = 2

// Widget ids that belong to a workspace MODULE. They may render ONLY when the active
// workspace's SERVER `home_default_panels` lists them — the user's dashboard prefs
// (hidden_widgets) must NEVER un-hide them. hidden_widgets IS scoped per user ×
// workspace (the server's home_dashboard_prefs row — the /sb proxy pins BOTH
// workspace_id and user_id, see server workers/workspace-project/src/surfaces/sb.js
// USER_SCOPED_TABLES — and the localStorage cache key is workspace-scoped too), so
// cross-WORKSPACE leaks aren't the threat. The gate protects against STALE prefs
// within a workspace (a `false` flag persisted while a module was live must not
// reveal its widgets after the module is removed/swapped) and against restore paths
// (drawer click / drag-drop) revealing a module widget the panel set doesn't grant.
// DERIVED per read: every ACTIVE module-declared widget id (takeover module +
// additive apps — _allModuleWidgetsFor). (The legacy v1 inline id list is
// gone — WS6 migration, 2026-07-30; an id nothing renders simply never shows.)
const _projectScopedIdsFor = (root) => {
  const ids = new Set()
  for (const w of _allModuleWidgetsFor(root)) ids.add(w.id)
  return ids
}

// The ADDITIVE-install widget sources — every stash whose widgets belong to
// an app INSTALLED on this workspace (`settings.workspaceApps[]`, plus the
// legacy pre-plural single app: `settings.workspaceModule` carrying
// `installMode: 'app'`, whose manifest stash lands on root.workspaceProject).
// Real full-takeover modules (no installMode flag — greenig/bellforge class)
// are NOT app-plane: their widgets stay governed by the workspace's
// authoritative `home_default_panels` alone, exactly as before.
const _appPlaneWidgetSources = (root) => {
  const sources = Array.isArray(root?.workspaceApps) ? [...root.workspaceApps] : []
  // `appModeModuleRef` reads the RECORD bag (`root.workspace.settings`), which
  // is the only plane the singular install ref ever lands on. This used to read
  // `root.workspaceSettings.workspaceModule` — the RESOLVED CONFIG bag, which
  // never carries that key (measured live on Vault, 2026-09-10), so this branch
  // was dead and a workspace holding ONLY the singular ref contributed no app
  // widgets at all. That is load-bearing now: `houseDefaultHiddenMap` stands
  // the house rows down in app mode and this fold is what puts the module's own
  // rows back, so a dead branch here would leave such a board EMPTY.
  const ref = appModeModuleRef(root)
  const wp = root?.workspaceProject
  // …and only when the PLURAL list does not already carry the same app. On a
  // workspace holding both refs for one app (Vault holds exactly that: the
  // singular `workspaceModule` AND a `workspaceApps[0]` entry, both
  // `vault/vaultos-app`) the loaded stash is the same widgets twice, and
  // `appDefaultWidgetIds` unions its sources — so a duplicate could only ever
  // widen the default set if the two copies disagreed. Skipping the duplicate
  // keeps every already-plural workspace byte-for-byte as it was.
  const lc = (v) => (v == null ? '' : String(v).toLowerCase())
  const refId = ref ? `${lc(ref.owner)}/${lc(ref.key)}` : ''
  const alreadyPlural = sources.some((s) => lc(s && s.appId) === refId)
  if (wp && ref && !alreadyPlural) {
    sources.push({
      widgets: wp.widgets,
      home_default_panels:
        (wp.config && wp.config.home_default_panels) ||
        (wp.configDefaults && wp.configDefaults.home_default_panels) ||
        null
    })
  }
  return sources
}

// EVERY widget id declared by an installed app (default-on or not) — the ids
// the project gate treats as grantable on THIS workspace, so the Customize
// drawer can reveal an app's opt-in widgets (e.g. bi-stock) without the
// workspace hand-pinning home_default_panels.
const _appPlaneWidgetIdsFor = (root) => {
  const ids = new Set()
  for (const src of _appPlaneWidgetSources(root)) {
    for (const w of Array.isArray(src?.widgets) ? src.widgets : []) {
      if (w && w.id) ids.add(w.id)
    }
  }
  return ids
}

// The installed apps' DEFAULT-ON widget ids (functions/homePanelSet.js rule:
// the app's declared home_default_panels ∩ its own widget ids, or all its
// widgets when it declares no panels). Derived live from the stashes, so
// install/uninstall converge with zero per-workspace writes.
const _appDefaultWidgetIdsFor = (root) => appDefaultWidgetIds(_appPlaneWidgetSources(root))

// The ONE restore-grant read, root-bound (the decision itself is pure —
// functions/homePanelSet.js `isWidgetRestorable`). Both consumers of the grant
// — the drawer's candidate list and every restore path — call THIS, so the
// list can never offer a widget the click would refuse, and neither can drift
// from `_enforceProjectGate`'s exemption set again
// (WS-HOME-DRAWER-CANNOT-RESTORE-HIDDEN-MODULE-WIDGET-1).
const _widgetRestorable = (id, root) =>
  isWidgetRestorable({
    id,
    scopedIds: _projectScopedIdsFor(root),
    appPlaneIds: _appPlaneWidgetIdsFor(root),
    aiIds: aiWidgetIdsFor(root),
    panels: _resolveWsPanels(root)
  })

// Fold the installed apps' widget defaults into a BASE hidden map: every
// app-declared widget gets an EXPLICIT entry — `false` (visible) for the
// app's defaults or anything the workspace's own panel set names, `true`
// (opt-in via Customize) for the rest. Explicit entries matter because these
// ids are NOT in HOME_CATALOG (`_DEFAULT_HIDDEN_WIDGETS` never names them):
// without one, an id the loosened project gate no longer force-hides would
// read `undefined` → visible, turning every opt-in widget default-on. The
// caller merges the user's saved prefs ON TOP, so an explicit hide (or an
// explicit reveal of an opt-in widget) sticks. Mutates + returns, matching
// _enforceProjectGate's contract.
const _foldAppWidgetDefaults = (baseHidden, wsVisible, root) => {
  const defaults = _appDefaultWidgetIdsFor(root)
  const allowed = Array.isArray(wsVisible) ? wsVisible : null
  for (const id of _appPlaneWidgetIdsFor(root)) {
    baseHidden[id] = !(defaults.has(id) || (allowed && allowed.includes(id)))
  }
  // AI-CREATED widgets are default-ON for whoever the server listed them to
  // (their creator, plus every member for an explicitly workspace-shared one).
  // Same derived-not-written rule the app plane uses above — no per-user prefs
  // write on create, so the widget appears the moment its row lands and
  // disappears the moment it is deleted. An explicit user hide still sticks:
  // the caller merges saved prefs ON TOP of this base map.
  for (const id of aiWidgetIdsFor(root)) baseHidden[id] = false
  return baseHidden
}

// Force every module-scoped widget hidden unless the active workspace's panel set
// (server home_default_panels) explicitly includes it. Applied AFTER the user-prefs
// merge, so prefs can reorder/hide generic widgets but can never reveal another
// workspace's module widgets. Mutates + returns the hidden map.
//
// APP-PLANE EXEMPTION (2026-08-13, the generic appstore widget contract): a
// widget declared by an app INSTALLED on this workspace (_appPlaneWidgetIdsFor
// — derived from the server-stored `settings.workspaceApps` refs, so still a
// server-granted surface, never a client fabrication) is grantable HERE by
// construction and passes the gate. Its default visibility is decided by
// _foldAppWidgetDefaults in the base map + the user's prefs on top — before
// this exemption a panel-less workspace force-hid every installed app's
// widgets AND blocked the Customize drawer from ever revealing them (13 apps
// installed on natali/ws_EEYlHGTvZ8, zero widgets renderable). Takeover-module
// widgets keep the strict panels-only rule — the cross-workspace leak this
// gate exists to prevent is unchanged, since an app's ids are exempt only
// while that app is on THIS workspace's install list.
// AI-PLANE EXEMPTION (tickets/opus.md "Per-workspace AI-CREATED widgets"): an
// AI-created widget is per-user content, not an admin-granted module surface —
// `home_default_panels` stays what it has always been, the WORKSPACE ADMIN's
// default board for module widgets. Its authorization is the server's own list
// scoping instead (a personal widget is only ever returned to its creator; a
// shared one to members, each row carrying the CALLER's capability), which is
// strictly stronger than a client-side id gate: the row does not exist in the
// response at all for someone who may not see it. Gating these on
// home_default_panels would mean a member could not have a widget of their own
// without an admin editing workspace settings — the opposite of the ticket's
// personal-by-default rule.
const _enforceProjectGate = (hidden, wsVisible, root) => {
  const allowed = Array.isArray(wsVisible) ? wsVisible : null
  const appIds = _appPlaneWidgetIdsFor(root)
  const aiIds = aiWidgetIdsFor(root)
  for (const id of _projectScopedIdsFor(root)) {
    if (appIds.has(id) || aiIds.has(id)) continue
    if (!allowed || !allowed.includes(id)) hidden[id] = true
  }
  return hidden
}

// A workspace's `home_default_panels` is authoritative for its dashboard — but a
// DEGENERATE set that names nothing beyond the always-present greeting (e.g. a
// workspace whose project module was removed, collapsing its panel list back to
// `['greeting']`) would, taken as authoritative, hide every other widget and
// strand the home on greeting-only. Treat such a set as unset (return null) so
// the caller falls through to the global default (the app-icon + data widgets).
// A real project panel set always names its module widgets too — kept as-is even
// if those mount async (the resync reveals them once their nodes exist) — so this
// only rescues the genuinely-empty case and never regresses a project dashboard.
// Last-known home_default_panels per workspace — the boot-paint fallback for
// _resolveWsPanels below. Keyed by the CONTENT workspace (root.workspace — the
// URL-resolved doc), NOT the auth-claim scope: panels are a property of the
// workspace whose dashboard is being RENDERED, and a claim-keyed entry leaked
// one workspace's panel gate onto another's dashboard whenever claim and URL
// diverged (bellforge's [greeting, bf-overview] hid every generic widget on
// /w/nikoloza). Falls back to the boot-read scope only while the workspace
// doc hasn't resolved.
const _wsPanelsCacheKey = (root) =>
  `symbols.home.wsPanels.v1.${root?.workspace?.id || _wsScopeForBootRead(root)}`

// Resolve the workspace's own panel set, and say whether the answer is LIVE
// (from settings) or a cached paint-hint. The distinction matters because a
// live set is AUTHORITATIVE — it overrides the user's own show/hide choices —
// and a hint must never be allowed to do that. See _resolveBootHidden.
//
// The cache is an ENVELOPE (`{ ws, panels }`), not a bare array, because a bare
// array had no way to record which workspace it described. `root.workspaceSettings`
// is a GLOBAL root key that lags a workspace switch, while the cache key is the
// NEW `root.workspace.id` — so every switch wrote the PREVIOUS workspace's panel
// list under the NEXT workspace's key. Live-confirmed damage: three unrelated
// workspaces (Symbols Intranet included) all caching the same
// `["greeting","bf-overview"]`, a bellforge-only set. Since a panel set is
// authoritative, that stale entry hid every widget whose id wasn't in it, and
// the boot then persisted that into the user's own hidden_widgets — turning a
// transient race into permanent, cross-workspace data loss.
//
// Reading the envelope also self-heals: legacy bare-array entries no longer
// parse as valid, so the poisoned ones are ignored (and overwritten) on sight.
//
// The envelope also carries `governsHouse` — whether that panel set governs
// the house DEFAULT ids too (a takeover module's board; see
// `panelSetHiddenMap`). The answer comes from the workspace RECORD, which a
// cold boot may not hold yet while this hint paints, so the hint carries the
// last-known answer: a takeover board never flashes the App strip and Next up
// before its record lands, and a configured general workspace never flashes
// without them. `null` on a legacy envelope — the caller asks the record.
const _readWsPanelsCache = (root) => {
  try {
    const parsed = JSON.parse(localStorage.getItem(_wsPanelsCacheKey(root)) || 'null')
    if (!parsed || Array.isArray(parsed) || typeof parsed !== 'object') return null
    if (parsed.ws !== _wsScope(root)) return null
    if (!Array.isArray(parsed.panels) || !parsed.panels.length) return null
    return {
      panels: parsed.panels,
      governsHouse: typeof parsed.governsHouse === 'boolean' ? parsed.governsHouse : null
    }
  } catch (_) {
    return null
  }
}

// True while the workspace DECLARES a module source but its widget stash has
// not landed yet — the window in which the catalog is not yet a fair judge of
// whether a panel id is real. `settings.workspaceModule` is the singular
// takeover module, `settings.workspaceApps[]` the additive plural; both stash
// their manifest widgets onto root asynchronously (loadWorkspaceProject /
// loadWorkspaceApps), and _allModuleWidgetsFor is the union of both planes.
const _moduleWidgetsPending = (root) => {
  const wss = root?.workspaceSettings || {}
  const declaresModule = !!wss.workspaceModule
  // Only INSTALLED APP entries count (installedAppRefs) — never a server-managed
  // row such as the records-grant `legacy-backfill`.
  const declaresApps = installedAppRefs(wss).length > 0
  if (!declaresModule && !declaresApps) return false
  return _allModuleWidgetsFor(root).length === 0
}

// Warn ONCE per (workspace, ghost set) — this resolver runs on every home
// render, and a config error that logs on every frame is noise nobody reads.
const _warnedGhostPanels = new Set()
const _warnGhostPanels = (root, ghosts, authoritative) => {
  if (!shellIsLoudDevRuntime()) return
  const sig = `${_wsScope(root)}|${ghosts.join(',')}`
  if (_warnedGhostPanels.has(sig)) return
  _warnedGhostPanels.add(sig)
  console.warn(
    `[home] home_default_panels names ${ghosts.length} widget id(s) that do not exist: ${ghosts.join(', ')}. ` +
      (authoritative
        ? 'The rest of the set is still honoured.'
        : 'Nothing in the set resolves to a real widget, so the default board is shown instead of hiding everything.')
  )
}

const _resolveWsPanelsInfo = (root) => {
  const bag = root?.workspaceSettings
  const raw = bag?.home_default_panels
  // PROVENANCE gate (isolation audit #10): `workspaceSettings` is a global
  // key that lags a switch, so a stamped bag describing a DIFFERENT workspace
  // must not be treated as live — and must never be cache-written under the
  // new scope's key (the write below stamps the scope AT WRITE TIME, which
  // proved nothing about where the DATA came from; that's how three unrelated
  // workspaces all cached bellforge's panel set). An UNstamped bag (legacy /
  // optimistic-console write) keeps today's behavior.
  const bagWs = bag?.workspace_id ? String(bag.workspace_id) : null
  const bagForeign = !!(bagWs && bagWs !== _wsScope(root))
  if (!Array.isArray(raw) || bagForeign) {
    // Settings haven't landed yet (cold boot — home_default_panels arrives via
    // the async getWorkspace/applyWorkspaceConfig pass) or describe another
    // workspace. Fall back to the LAST-KNOWN set for this workspace so the
    // first paint already shows the workspace's own panels instead of the
    // generic default that gets visibly stripped seconds later. Hint only —
    // never authoritative.
    const hint = _readWsPanelsCache(root)
    return {
      panels: hint ? hint.panels : null,
      live: false,
      governsHouse: hint ? hint.governsHouse : null
    }
  }
  // A panel id that names no real widget is a CONFIG ERROR, not an instruction
  // to hide everything. The decision itself lives in functions/homePanelSet.js
  // so it is testable; the board's reconcile re-runs on every module stash
  // update, so this re-runs the moment a pending module lands.
  const pending = _moduleWidgetsPending(root)
  const verdict = resolveHomePanelSet({
    panels: raw,
    knownIds: new Set(homeCatalogFor(root).map((w) => w.id)),
    moduleWidgetsPending: pending
  })
  if (verdict.ghosts.length && !pending) {
    _warnGhostPanels(root, verdict.ghosts, verdict.authoritative)
  }
  const resolved = verdict.panels
  // Does this set govern the house default ids too? Only a takeover module's
  // (functions/homeDefaultWidgets.js `panelSetHiddenMap`) — read off the
  // record, which the live bag implies has landed.
  const governsHouse = !!takeoverModuleRef(root)
  // Write-through, scope-stamped. Skipped entirely until the active workspace
  // is actually known: writing under the 'default' placeholder is how an
  // unattributable set leaks into a real workspace's key.
  const scope = _wsScope(root)
  try {
    if (scope && scope !== 'default') {
      if (resolved) {
        localStorage.setItem(
          _wsPanelsCacheKey(root),
          JSON.stringify({ ws: scope, panels: resolved, governsHouse })
        )
      } else {
        localStorage.removeItem(_wsPanelsCacheKey(root))
      }
    }
  } catch (_) {
    /* cache is best-effort */
  }
  return { panels: resolved, live: true, governsHouse }
}

const _resolveWsPanels = (root) => _resolveWsPanelsInfo(root).panels

// A board item's node by widget id — reading only (the launcher drop's
// duplicate check). A module host owns an id whenever it exists, so it is
// preferred over a page widget that carries the same id.
const _findItemById = (id) => {
  if (typeof document === 'undefined' || !id) return null
  const sel = CSS.escape(String(id))
  return (
    document.querySelector(`.home-grid > [data-module-widget][data-widget-id="${sel}"]`) ||
    document.querySelector(`.home-grid > [data-widget-id="${sel}"]`)
  )
}

const _isHomeWidgetAlreadyVisible = (id) => {
  const item = _findItemById(id)
  return !!item && window.getComputedStyle(item).display !== 'none'
}

// The ORDER index a drop at (x, y) lands on: the index of the widget under the
// pointer, else null (a drop on an empty part of the board appends).
const _dropIndexAt = (gridEl, root, x, y) => {
  const node = gridEl && gridEl.node
  if (!node) return null
  for (const child of node.children) {
    const id = child.getAttribute && child.getAttribute('data-widget-id')
    if (!id) continue
    const r = child.getBoundingClientRect()
    if (!r.width || !r.height) continue
    if (x >= r.left && x <= r.right && y >= r.top && y <= r.bottom) {
      const at = _currentHomeLayout(root).findIndex((e) => e.id === id)
      return at < 0 ? null : at
    }
  }
  return null
}

// Returns a BUNDLE KEY (the catalog carries keys) or a module-supplied
// server string — polyglot resolves the first and returns the second verbatim.
const _homeWidgetLabel = (root, id) =>
  homeCatalogFor(root).find((widget) => widget.id === id)?.title || 'workspace.home.drop.thisApp'

const _notifyHomeWidgetDrop = (el, root, id, alreadyVisible) => {
  try {
    const label = el.call('polyglot', _homeWidgetLabel(root, id))
    el.call('openNotification', {
      type: alreadyVisible ? 'info' : 'success',
      title: el.call(
        'polyglot',
        alreadyVisible ? 'workspace.home.drop.already' : 'workspace.home.drop.added'
      ),
      message: el
        .call(
          'polyglot',
          alreadyVisible ? 'workspace.home.drop.alreadyMessage' : 'workspace.home.drop.addedMessage'
        )
        .replace('{app}', label)
    })
  } catch (_) {
    /* Home remains usable if a standalone surface has no toast mount. */
  }
}

// The launcher asked to put an app on the board (`_homeAddWidget`, the search
// launcher's "Add to dashboard") — reveal it once, in place.
const _consumeAddWidgetIntent = (root, sdk) => {
  const id = root && root._homeAddWidget
  if (!id) return
  root.update({ _homeAddWidget: null }, { preventFetch: true })
  if (_isHomeWidgetAlreadyVisible(String(id))) return
  _restoreWidget(String(id), root, null, sdk || root.sdk, true)
}

// True while an installed module ships a widget under this id — its host owns
// the id, and the page's own copy stands down (see `_widget`).
const _moduleShadows = (root, id) => !!id && _allModuleWidgetsFor(root).some((w) => w.id === id)

// Show a hidden widget again. It keeps its place in the order (a hide never
// removes the layout entry); a widget the layout never listed joins at the
// end, or where the launcher dropped it (`dropAt.index`). The hidden map
// persists at once, user choices only (_durableHiddenMap).
const _restoreWidget = (id, root, dropAt, sdk, userGesture) => {
  if (!id || !root?.update) return
  if (!_widgetRestorable(id, root)) {
    console.warn(
      '[home] restore blocked — module widget not granted by this workspace panel set:',
      id
    )
    return
  }
  let layout = homeLayoutEnsure(_currentHomeLayout(root), [
    { id, size: _defaultWidgetSize(root, id) }
  ])
  const moved = !!(dropAt && Number.isFinite(dropAt.index))
  if (moved) layout = homeLayoutMove(layout, id, dropAt.index)
  const cur = { ...(root._homeHiddenWidgets || {}) }
  cur[id] = false
  if (userGesture) _recordUserHidden(root, id, false)
  if (moved) _recordUserLayout(root, layout)
  // The restored card enters with the frame's own fade-up; the cards after it
  // glide aside (functions/homeWidgetMotion.js) instead of jumping.
  const apply = () => root.update({ _homeHiddenWidgets: cur, _homeLayout: layout })
  if (root._homeOps && typeof root._homeOps.withMotion === 'function')
    root._homeOps.withMotion(apply)
  else apply()
  // Persist the DURABLE subset — the user's own choices — never `cur`, which
  // also carries the workspace defaults and render clamps (_durableHiddenMap).
  _saveHiddenWidgets(root, _durableHiddenMap(root), sdk)
  if (moved) _persistHomeLayout(root, sdk)
}

// Hide a widget: the hidden map flips and persists at once; its layout entry
// stays, so a restore lands where it was. Two halves, split so the DECISION is
// written before the card takes its time to leave (memory: home prefs durability — never delay a
// decision write). The durable map reads the gesture ledger, not the live
// hidden map, so the order of the two halves cannot change what is saved.
const _persistWidgetHide = (id, root, sdk) => {
  _recordUserHidden(root, id, true)
  // The durable subset, never the composed map — writing the composed map is
  // what once put 16 defaults and a gate clamp into the server row on one Hide
  // click.
  _saveHiddenWidgets(root, _durableHiddenMap(root), sdk)
}
const _showWidgetHidden = (id, root) => {
  const cur = { ...(root._homeHiddenWidgets || {}) }
  cur[id] = true
  root.update({ _homeHiddenWidgets: cur })
}

// The board's hide (frame ×, the widget menu): persist now, then the card
// leaves (functions/homeWidgetMotion.js `homeWidgetExit`) and the board hides
// it with its neighbours gliding into the gap.
const _hideWidgetWithMotion = (el, root, id) => {
  if (!id || !root?.update) return
  _persistWidgetHide(id, root, el.sdk)
  const grid = el.MainColumn && el.MainColumn.GridContainer
  el.call('homeWidgetExit', grid, id, () =>
    el.call('homeWidgetFlip', grid, () => _showWidgetHidden(id, root))
  )
}

// Toast + delegate — the widget right-click "Share to workspace" / "Make
// personal" / "Delete widget" menu items (AI-CREATED plane only) route here.
// Both wrap functions/aiWidgets.js's mutations (the real gate is server-side;
// these two just report the outcome) with the SAME house toast every other
// dashboard write already uses (openNotification — see loadPrivateData.js's
// identical root.call?.(...) guard for a context with no `el`).
const _setAiWidgetVisibilityWithNotice = async (root, sdk, widget, visibility) => {
  // `root` is ROOT STATE, not an element (this path has no `el` — see the
  // note above). `root.call` is the same optional hop `openNotification`
  // already used; when it is absent nothing is shown either way, so the
  // translation hop carries the same guard rather than throwing.
  const t = (k) => (root.call ? root.call('polyglot', k) : k)
  const title = widget?.title || t('workspace.widget.untitled')
  const result = await setAiWidgetVisibility(root, sdk, widget, visibility)
  try {
    if (result.ok) {
      root.call?.('openNotification', {
        title: t(
          visibility === 'workspace' ? 'workspace.widget.shared' : 'workspace.widget.madePersonal'
        ).replace('{title}', title),
        message: t(
          visibility === 'workspace'
            ? 'workspace.widget.sharedMessage'
            : 'workspace.widget.personalMessage'
        ),
        type: 'success'
      })
    } else {
      root.call?.('openNotification', {
        title: t('workspace.widget.updateFailed'),
        message: result.error || t('workspace.widget.genericError'),
        type: 'error'
      })
    }
  } catch (_) {
    /* the notification surface isn't reachable from this context */
  }
}

const _deleteAiWidgetWithNotice = async (root, sdk, widget) => {
  const t = (k) => (root.call ? root.call('polyglot', k) : k)
  const title = widget?.title || t('workspace.widget.untitled')
  const result = await deleteAiWidgetDef(root, sdk, widget)
  try {
    if (result.ok) {
      root.call?.('openNotification', {
        title: t('workspace.widget.deleted').replace('{title}', title),
        type: 'success'
      })
    } else {
      root.call?.('openNotification', {
        title: t('workspace.widget.deleteFailed'),
        message: result.error || t('workspace.widget.genericError'),
        type: 'error'
      })
    }
  } catch (_) {
    /* the notification surface isn't reachable from this context */
  }
}

// House rows whose gate (`gateRoute`) is OFF for this workspace. They are NOT
// in `homeCatalogFor` — by design, so the drawer never offers them — but a
// board can still carry their card: a layout saved while the gate was on, or
// the static mount. Every consumer that reasons about "what is on the board"
// must treat them as hidden shell widgets, never as foreign ones (gita.pr,
// 2026-09-08: a gated-off `mail-inbox` read as foreign silently dropped every
// layout save for the workspace).
const _gatedOffHouseIds = (root) =>
  HOME_CATALOG.filter((w) => w.gateRoute && !shellNavItemVisible(root, w.gateRoute)).map(
    (w) => w.id
  )
// The LAST MILE every hidden-map resolver shares, in one place:
//   workspace/app DEFAULTS  ▸  the user's SAVED row  ▸  this document's
//   explicit GESTURES  ▸  the two clamps that are policy, not preference
//   (a module widget the panel set does not grant; a house widget whose route
//   gate is off).
// Extracted because three resolvers once carried their own copy of this
// ordering and DRIFTED twice (one discarded the saved map on a panels
// workspace and then persisted `false`, destroying a real user hide). The
// board now has one resolver (`_resolveBootHidden`, re-run by
// `_reconcileHomeBoard`); only the BASE differs per caller, so only the base
// is a parameter.
const _composeHiddenMap = (base, saved, root, wsVisible) => {
  const hidden = _enforceProjectGate(
    { ...base, ...(saved || {}), ...(_userHiddenFor(root) || {}) },
    wsVisible,
    root
  )
  // A gate that is OFF removes the route, so its card cannot stay on the
  // board — this outranks even an explicit user reveal, which is why it runs
  // after the merge rather than inside it.
  for (const id of _gatedOffHouseIds(root)) hidden[id] = true
  return hidden
}

// ── Board reconcile ───────────────────────────────────────────────
// ONE idempotent pass, run when the grid starts and on every update it
// subscribes to (workspace, settings, the module stash, the prefs row): the
// hidden map and the layout are recomputed from their live sources and written
// only when they differ. Sources, strongest first: this session's own gestures
// (_userHiddenFor / _userLayoutFor), an unconfirmed write from the last session
// (the prefs outbox), the account record when it exists (`{}` and a null layout
// included — an answer, not an absence), the device cache only when no record
// exists, then the workspace defaults. Nothing here writes to the server: only
// a gesture persists (memory: home prefs durability, four causes). This
// replaces the GridStack-era sync / resync / catch-up passes — the CSS grid has
// no engine to keep in step, so there is nothing to retry.
const _sameFlagMap = (a, b) => {
  const x = a || {}
  const y = b || {}
  const keys = new Set([...Object.keys(x), ...Object.keys(y)])
  for (const k of keys) if (!!x[k] !== !!y[k]) return false
  return true
}
const _sameLayout = (a, b) =>
  Array.isArray(a) &&
  Array.isArray(b) &&
  a.length === b.length &&
  a.every((e, i) => e && b[i] && e.id === b[i].id && e.size === b[i].size)

let _replayedOutboxScope = null
const _sameColorMap = (a, b) => {
  const x = a || {}
  const y = b || {}
  const keys = new Set([...Object.keys(x), ...Object.keys(y)])
  for (const k of keys) if ((x[k] || null) !== (y[k] || null)) return false
  return true
}
const _reconcileHomeBoard = (el, state) => {
  const root = untrackedState(state)
  if (!root || !root.update) return
  const hidden = _resolveBootHidden(root).hidden
  const layout = _resolveHomeLayout(root)
  // The colour picks ride the same prefs row, which usually lands after the
  // page's onCreate read the device cache alone.
  const colors = _resolveBootWidgetColors(root)
  const patch = {}
  if (!_sameFlagMap(hidden, root._homeHiddenWidgets)) patch._homeHiddenWidgets = hidden
  if (!_sameLayout(layout, root._homeLayout)) patch._homeLayout = layout
  if (!_sameColorMap(colors, root._homeWidgetColors)) patch._homeWidgetColors = colors
  if (Object.keys(patch).length) root.update(patch, { preventFetch: true })
  // A write the last session could not confirm lands now, once per scope.
  const scope = _wsScope(root)
  if (_replayedOutboxScope !== scope && root.currentUserId && el && el.sdk) {
    _replayedOutboxScope = scope
    try {
      _replayPrefsOutbox(el.sdk, root)
    } catch (err) {
      console.warn('[home] prefs outbox replay skipped:', err && err.message)
    }
  }
  _consumeAddWidgetIntent(root, el && el.sdk)
}

// The rows the customize drawer offers: every widget of THIS workspace's
// catalog that the board is currently hiding, minus the module-scoped ids no
// plane grants (`_widgetRestorable` — the SAME call `_restoreWidget` makes, so
// the drawer can never list what a click on it would refuse to restore, and an
// app-plane widget an APP-MODE workspace shows by default is offered back
// after a Hide instead of needing a layout reset).
//
// ONE source, three consumers: the list, the section's own `hide`, and the
// hint copy. They used to disagree — the list filtered through the catalog
// while the section and the hint counted RAW keys of `_homeHiddenWidgets`, a
// map that also carries render clamps (a gated-off house row) and ids no card
// here can show. On a workspace with the mail gate off that reads as "some
// widgets are hidden" above an empty list.
const _drawerHiddenRows = (root) => {
  const hidden = root?._homeHiddenWidgets || {}
  return homeCatalogFor(root).filter((w) => hidden[w.id] && _widgetRestorable(w.id, root))
}

// One row in the right customize drawer. State per row is one
// HOME_CATALOG entry; click restores by removing the hidden flag and
// re-registering the existing DOM node with Gridstack. Native HTML5
// drag also lifts the widget id onto the DataTransfer payload so the
// GridContainer's `onDrop` (below) can land it at the cursor's cell.
const _customizeDrawerItem = {
  tag: 'button',
  type: 'button',
  class: 'gs-no-drag',
  draggable: true,
  flow: 'x',
  gap: 'Z',
  align: 'center',
  padding: 'Z B',
  round: 'radiusControl',
  background: 'white.04',
  border: 'hairline dashed white.18',
  color: 'title',
  cursor: 'grab',
  width: '100%',
  textAlign: 'left',
  transition: '120ms ease background, 120ms ease border-color, 120ms ease transform',
  ':hover': {
    background: 'white.08',
    // `accentInk` (the [blueDeep, blueBright] semantic pair) rather than the
    // old rgba(120,160,255,0.55) literal — the home board is cross-themed, and
    // blueBright (#47A8FF) is what that literal was eyeballing in dark.
    borderColor: 'accentInk',
    transform: 'translateY(-1px)'
  },
  ':active': { cursor: 'grabbing' },

  onClick: (e, el, s) => {
    e.stopPropagation()
    _restoreWidget(s.id, s.root, null, el.sdk, true)
  },

  // dataTransfer carries the widget id; the grid container's onDrop
  // reads it back and restores the widget at the dropped cell.
  onDragstart: (e, el, s) => {
    e.dataTransfer.setData('text/x-home-widget-id', s.id || '')
    e.dataTransfer.effectAllowed = 'move'
  },

  Icon: { extends: 'Icon', name: (el, s) => s.icon || 'plus', color: 'caption' },
  // Title + subtitle stack, because TWO CATALOG ROWS CAN CARRY THE SAME TITLE.
  // Measured on the vault board (2026-09-10): `workspace.catalog.assets` and
  // `workspace.catalog.appDocuments` both resolve to "Notes", and `broadcast`
  // and `appBroadcast` both to "Broadcast". The house data card was hidden
  // while its app-shortcut namesake sat on the board, so the drawer read as
  // "Notes is listed hidden, but Notes is right there" — a LABEL collision,
  // not a state one (the drawer and the board read the same composed map, and
  // did so before this change too). The shortcut rows already ship a subtitle
  // in the bundle for their own card headers ("Notes & knowledge", "Tasks &
  // board"), so naming it here disambiguates with copy that already exists.
  Label: {
    flow: 'y',
    gap: '0',
    flex: '1',
    minWidth: '0',
    Title: {
      tag: 'span',
      // A catalog title is a polyglot KEY (`workspace.catalog.*` since the
      // extraction, `apps.widget.*` for platform widgets since 1c6e12d16) — the
      // raw read printed the keys in the drawer (Nika 2026-09-09). polyglot
      // returns its input for a non-key, so a manifest that still carries
      // resolved words renders unchanged.
      text: (el, s) => el.call('polyglot', s.title || ''),
      fontSize: 'Z2',
      color: 'title',
      overflowWrap: 'anywhere'
    },
    Subtitle: {
      tag: 'span',
      fontSize: 'Y',
      color: 'caption',
      lineHeight: '1.5',
      overflowWrap: 'anywhere',
      // The row's OWN `subtitle` — a literal bundle key carried by the
      // catalog entry (see HOME_CATALOG's header). A row without one renders
      // title-only; a module widget's manifest subtitle passes through
      // unchanged, because polyglot returns an already-resolved sentence
      // verbatim.
      text: (el, s) => (s.subtitle ? el.call('polyglot', s.subtitle) : ''),
      hide: (el, s) => !s.subtitle
    }
  },
  Add: { tag: 'span', text: '+', color: 'caption', fontSize: 'A', fontWeight: '700' }
}

// ── The board stylesheet ──────────────────────────────────────────
// The grid's geometry, generated from the ONE constants table
// (functions/homeGridMetrics.js, HOME_WIDGETS_SPEC.md §1): columns by the
// board's own width (a container query on MainColumn, so the assistant dock
// narrowing the page narrows the grid too), square cells capped at the spec's
// cell, the gutter per step, and what each size spans at each column count.
// App shortcuts are never grid items: they live in the App strip above the
// grid (HomeAppStrip, decision H1 A). Pure CSS, so the board paints its final geometry on the first frame — no
// rAF, no ResizeObserver, no engine (a hidden tab has neither; the >8 s blank
// was the GridStack boot waiting on them). Rows are a MINIMUM of one cell and
// grow with their content: a widget never clips (quality law).
//
// Why a stylesheet and not props: the smbls atomic engine does not wrap an
// `@container` key yet (it composes `.cls @container …`, dead CSS). The px
// values are the constants module's own; the lock test pins them to the spec.
// NOTE: single quotes only in these comments, never backticks — the whole
// sheet is one template literal.
// `el` = the page element (or any shell element): the spanAt helpers are
// registered functions, reached through el.call (no new sibling import).
const _injectWidgetStyles = (el) => {
  if (typeof document === 'undefined' || !el || typeof el.call !== 'function') return
  let style = document.getElementById('home-widget-styles')
  if (!style) {
    style = document.createElement('style')
    style.id = 'home-widget-styles'
    document.head.appendChild(style)
  }
  // The per-step rules are built by plain concatenation, so the sheet itself
  // stays ONE template literal below (a stray backtick can never split it).
  const steps = HOME_GRID_STEPS.slice().sort((a, b) => a.minWidth - b.minWidth)
  const stepRules = steps
    .map((step) => {
      // A 'fill' step (two columns, one) spreads its cells over the column
      // with no cap; every other step caps the cell (the base rule's value).
      const cell = step.fill
        ? 'calc((100cqw - (var(--home-cols) - 1) * var(--home-gap)) / var(--home-cols))'
        : 'min(' +
          HOME_GRID_CELL_CAP +
          'px, calc((100cqw - (var(--home-cols) - 1) * var(--home-gap)) / var(--home-cols)))'
      const vars =
        '.home-grid, .home-greeting, .home-app-strip, .home-boards-row { --home-cols: ' +
        step.cols +
        '; --home-gap: ' +
        step.gutter +
        'px; --home-cell: ' +
        cell +
        '; }\n' +
        // One column drops the square-row rule: a card is as tall as its content.
        '.home-grid { grid-auto-rows: ' +
        (step.rows === 'auto' ? 'auto' : 'minmax(var(--home-cell), auto)') +
        '; }'
      // What each size spans at this column count (functions/homeGridMetrics.js),
      // unless the card carries its manifest's span for this step
      // ('--home-span-c-<cols>', WS-HOME-GRID-SPAN-PER-STEP-1 — homeSpanVar).
      const spans = ['s', 'm', 'l', 'xl']
        .map((size) => {
          const span = homeWidgetSpan(size, step.cols)
          return (
            ".home-grid > [data-widget-size='" +
            size +
            "'] { grid-column: span " +
            el.call('homeSpanVar', 'c', step.cols, span.c) +
            '; grid-row: span ' +
            el.call('homeSpanVar', 'r', step.cols, span.r) +
            '; }'
          )
        })
        .join('\n')
      const body = vars + '\n' + spans
      if (step.minWidth <= 0) return body
      const query = '@container homeboard (min-width: ' + step.minWidth + 'px)'
      return query + ' {\n' + body + '\n}'
    })
    .join('\n')
  // A size narrower than its own arrangement (M and XL on one column, XL on
  // two) lays its slot body out as the width it really spans — the slot
  // grid's blocks (HOME_BODY_LAYOUTS, the same four components/HomeWidgetBody
  // declares), picked by homeBodyFit. Each step's rules sit in a BOUNDED
  // range, so nothing has to be undone at a wider step. A body whose own
  // arrangement has a list column but whose fit has none hides the list; its
  // visual list stays in the visual area.
  const bodyLayout = (size) => HOME_BODY_LAYOUTS[size] || HOME_BODY_LAYOUTS.s
  const hasList = (size) => /(^|[\s"])list([\s"]|$)/.test(bodyLayout(size).areas)
  const fitRules = steps
    .map((step, i) => {
      const next = steps[i + 1]
      const rules = ['s', 'm', 'l', 'xl']
        .map((size) => {
          const fit = homeBodyFit(size, step.cols)
          if (fit === size) return ''
          const at = ".home-grid > [data-widget-size='" + size + "']"
          const to = bodyLayout(fit)
          let out =
            at +
            ' .home-widget-body { grid-template-columns: ' +
            to.columns +
            '; grid-template-areas: ' +
            to.areas +
            '; grid-template-rows: ' +
            to.rows +
            '; }'
          if (hasList(size) && !hasList(fit)) {
            out +=
              '\n' +
              at +
              ' .home-widget-list:not([data-as-visual]) { display: none; }\n' +
              at +
              ' .home-widget-list[data-as-visual] { grid-column: visual; grid-row: visual; }'
          }
          return out
        })
        .filter(Boolean)
        .join('\n')
      if (!rules) return ''
      let range = ''
      if (step.minWidth > 0 && next)
        range = '(' + step.minWidth + 'px <= width < ' + next.minWidth + 'px)'
      else if (step.minWidth > 0) range = '(width >= ' + step.minWidth + 'px)'
      else if (next) range = '(width < ' + next.minWidth + 'px)'
      if (!range) return rules
      const query = '@container homeboard ' + range
      return query + ' {\n' + rules + '\n}'
    })
    .filter(Boolean)
    .join('\n')
  style.textContent = `
    [data-home-board] { container: homeboard / inline-size; }
    .home-grid, .home-greeting, .home-app-strip, .home-boards-row {
      --home-cell: min(${HOME_GRID_CELL_CAP}px, calc((100cqw - (var(--home-cols) - 1) * var(--home-gap)) / var(--home-cols)));
      --home-grid-w: calc(var(--home-cols) * var(--home-cell) + (var(--home-cols) - 1) * var(--home-gap));
    }
    .home-grid {
      display: grid;
      grid-auto-flow: row dense;
      justify-content: center;
      grid-template-columns: repeat(var(--home-cols), var(--home-cell));
      grid-auto-rows: minmax(var(--home-cell), auto);
      gap: var(--home-gap);
    }
    /* The page head, the App strip and the boards row sit on the grid's own
       width. */
    .home-greeting, .home-app-strip, .home-boards-row {
      width: min(100%, var(--home-grid-w));
      margin-inline: auto;
    }
    /* A TAB HOLDS THE HOME (functions/homeView.js): every tab but Dashboard
       is the home's own VIEW, not a layer over the board — Main's Fly
       canvas, Chat's conversation, Present's deck, Build's app builder, a
       tab's start screen (each a view part, data-home-view-part). The dashboard's cards
       leave the layout, so the page ends where the view's content ends — no
       blank scroll under a short screen — and the part on screen takes the
       grid's whole width in the flow. Back on the dashboard the cards return
       in place. */
    .home-grid:not([data-home-view='dashboard']) > :not([data-home-view-part]) { display: none; }
    .home-grid:not([data-home-view='dashboard']) > [data-home-view-part][data-open='true'] { position: relative; grid-column: 1 / -1; }
    ${stepRules}
    ${fitRules}
    ${el.call('homeSpanFitCss')}

    /* Edit mode: every card reads as movable — the dashed state ring, the
       one stroke the taste law allows — and the lifted card follows the
       pointer above its neighbours. */
    .home-grid > [data-editing] {
      outline: 2px dashed color-mix(in srgb, currentColor 35%, transparent);
      outline-offset: -2px;
    }
    .home-grid > [data-dragging] { cursor: grabbing; }

    /* Bodies not yet on the slot contract keep their two tiers: the compact
       one at S and M, the full one at L and XL. Their old in-card inset goes —
       the frame pads every card once. A module body's mount point fills the
       card as a flex column, except a slot body (home-widget-body): its grid
       is its own. */
    .home-grid .widget-tier-mini, .home-grid .widget-tier-full { display: none; }
    .home-grid > [data-widget-size='s'] .widget-tier-mini,
    .home-grid > [data-widget-size='m'] .widget-tier-mini,
    .home-grid > [data-widget-size='l'] .widget-tier-full,
    .home-grid > [data-widget-size='xl'] .widget-tier-full { display: flex; }
    .home-grid .widget-body > [data-key='content']:not(.home-widget-body) {
      display: flex;
      flex-direction: column;
      flex: 1 1 auto;
      min-height: 0;
    }
    .home-grid .widget-body > [data-key='content']:not(.home-widget-body) > * {
      flex: 1 1 auto;
      min-height: 0;
    }
    .home-grid .widget-tier-mini, .home-grid .widget-tier-full {
      flex: 1 1 auto;
      min-height: 0;
      padding: 0;
    }

    /* Phone top inset: the AppHeader sits below env(safe-area-inset-top) on a
       notched device and a spacing token cannot carry env(). */
    @media ${media.mobileL} {
      [data-key='content'] [data-key='MainColumn'] {
        padding-top: calc(var(--spacing-D1) + env(safe-area-inset-top));
      }
      /* The phone dock replaces the top header. Keep only the safe area and
         the normal gutter above the destination switch in that layout. */
      html:has([data-phone-nav="true"]) [data-home-board] {
        padding-top: calc(var(--spacing-A) + env(safe-area-inset-top));
      }
    }

    /* Touch: a long press is the context-menu gesture, so iOS must not race it
       with text selection on the board's chrome. */
    @media (pointer: coarse) {
      .home-grid { -webkit-touch-callout: none; -webkit-user-select: none; user-select: none; }
    }
  `
}

// ── Gridstack persistence ────────────────────────────────────────
// The dashboard layout is persisted PER USER + PER WORKSPACE via the
// existing `workspaceProject.userPreferences` SDK surface. Each
// (user, workspace) pair stores its own homeGridLayout — drag/resize
// in workspace A doesn't leak into workspace B.
//
// localStorage is kept as a fast cache so the initial paint can use
// the most recently saved layout while the server prefs are
// fetched. Once `root.userPrefs` lands, the server copy becomes
// authoritative; subsequent saves write to both.
//
// localStorage cache key is workspace-scoped too so a different
// active workspace doesn't see the previous one's cached layout.
//
// Workspace scope shared by every home-scoped key — the cache keys, the SAVE
// gate (content-scope check) and the explicit `workspaceId` pin.
//
// Precedence is TAB-TRUTH FIRST, deliberately (MULTI_WORKSPACE_TABS §6c/§6e):
//
//   1. `root.workspace.id`  — the workspace THIS TAB is pinned to. Under MWT
//      (default ON) bootShell reconciles it from the `/w/<org-slug>` URL via
//      `resolveActiveWorkspace`, and wireGlobalListeners refuses to drag the
//      tab off it when another tab switches.
//   2. the per-tab pointer — `sessionStorage.activeWorkspace` under MWT,
//      shared `localStorage` when opted out. Available SYNCHRONOUSLY at cold
//      boot, before authBridge has parsed any claims, which is what keeps the
//      first paint on the right workspace's cached layout.
//   3. `jwtClaims.active_workspace_id` — last resort only.
//
// The claim used to come FIRST here, which was the bug: it is SESSION-global,
// re-minted by a switch in ANY tab, so a second tab moving to another
// workspace could repoint this tab's dashboard reads/writes at a workspace it
// was never showing. It is kept as the final fallback for the pre-MWT path
// where neither the root nor the pointer has landed.
//
// Reading the pointer also retires the bespoke `symbols.home.lastWsScope`
// mirror this file used to write: that key duplicated — in SHARED
// localStorage — what activeWorkspacePointer already owns per tab, so under
// MWT it leaked a sibling tab's workspace into this tab's boot paint.
const _wsScope = (root) =>
  root?.workspace?.id ||
  shellGetActiveWorkspacePointer() ||
  root?.jwtClaims?.active_workspace_id ||
  'default'

// Kept as a named alias for the READ sites (cache keys / boot painting) so the
// call sites still say which side of the read/write split they are on. Both
// resolve identically now — the pointer supplies at boot exactly the
// last-known scope the separate fallback was invented for.
const _wsScopeForBootRead = _wsScope
// ── WS-GRID-PERSIST-1 — the home cache scope must be TIME-INVARIANT ──────
//
// Nika, twice: "why when I arrange widgets, and then reload they still load
// messy". Root-caused 2026-08-18 by instrumenting the WRITE, not the board.
//
// The write was never broken. A real drag fired 2 `change` events and wrote
// twice, correctly, to `gridLayout.v3.<current-workspace-id>`. But the only
// layout key on disk was `gridLayout.v3.<a DIFFERENT workspace's id>`, with
// none for the live workspace — while `hiddenWidgets`/`wsPanels` WERE correct.
// So the arrangement saved and the reload read a different key, missed, and
// rendered defaults. That asymmetry is the user-visible tell: WHICH widgets
// show survives a reload, WHERE they sit does not.
//
// Why the two keys differ, given both call the SAME builder: `_wsScope` is a
// fallback chain whose answer depends on WHEN it is asked.
//   * at save time  `root.workspace.id` has landed  → correct id
//   * at cold boot  it has not                      → falls to the pointer
// and with MWT OFF (the default) that pointer is SHARED
// `localStorage.activeWorkspace` — every tab reads the same value, so a
// previously-visited workspace or a sibling tab supplies it.
// `activeWorkspacePointer.js`'s own header says the URL is meant to override
// it at boot "resolved via resolveActiveWorkspace" — but that resolve is
// ASYNC and the cache read beats it. A miss is indistinguishable from "no
// saved layout", so nothing errors and the board silently renders defaults.
//
// FIX: scope on the URL, which names this tab's workspace SYNCHRONOUSLY and
// answers identically at boot and at save. `getWorkspaceRawSegments` is a
// pure regex parse — no resolver, no await, no shared storage, and it cannot
// be contaminated by another tab. That is the property the id lacked; the id
// is not "more correct", it is merely unavailable at the moment we need it.
// ⚠️ SECOND ORDER OF THE SAME BUG, measured live 2026-08-18 (leg 3 of the
// acceptance) — the first fix was necessary and NOT sufficient.
//
// The original defect was a scope that answered differently depending on WHEN
// it was asked. Scoping on the URL fixed that for a FIXED path. But the path
// itself is not fixed: **the app redirects `/w/<org>` → `/w/<org>/<ws>` shortly
// after boot.** So the boot read ran under `bellforge` and every save after the
// redirect wrote under `bellforge~ws_UYAOhQyNjm` — a SECOND key, and the
// arrangement was lost again. Same class, one level up.
//
// The measurement that caught it (innerWidth 1812): a move created
// `…v4.bellforge`; a hard reload matched all 8 live widgets with 0 mismatches;
// a move AFTER the redirect created `…v4.bellforge~ws_UYAOhQyNjm`; and loading
// `/w/bellforge` then rendered `crm-recent` at (8,3) while the last save held
// (8,6) and the shallow key held (0,6) — matching NEITHER.
//
// ⛔ REJECTED FIX — scope on `orgSlug` alone. It is stable across both URL
// shapes, which is why it looks right, but it MERGES every workspace in an org
// onto one layout. That is the cross-workspace bleed this file has fought
// twice, and `homeCacheScope.test.js` already asserts sibling workspaces must
// not share a key. Stability is not the only requirement; correctness of the
// boundary is.
//
// ⛔ ALSO REJECTED — append `seg2` unconditionally. It cannot be known before
// the redirect, so it makes the BOOT read wrong instead of the save. The same
// bug facing the other way, and it would pass a naive static-path test.
//
// THE SHAPE THAT IS ACTUALLY TRUE: `/w/<org>` is not a different scope from
// `/w/<org>/<ws>` — it is the SAME scope, not yet refined. So treat the
// org-only form as PROVISIONAL, and when the redirect refines it, MIGRATE the
// provisional value into the refined key. Identical machinery to the v3→v4
// migration: a scope refinement is a key change like any other.
const _homeScopeParts = (root) => {
  const path = (typeof location !== 'undefined' && location.pathname) || ''
  const { orgSlug, seg2 } = shellGetWorkspaceRawSegments(path)
  if (!orgSlug) {
    // No `/w/` prefix (pre-Scheme-A route, bare load): keep the OLD behaviour
    // rather than inventing a third scope. Unchanged, including its weakness.
    return { scope: _wsScopeForBootRead(root), provisional: null }
  }
  // Refined: the workspace is named. This is the terminal scope.
  if (seg2) return { scope: `${orgSlug}~${seg2}`, provisional: orgSlug }
  // Not yet refined — the redirect has not landed. Usable, but transitional.
  return { scope: orgSlug, provisional: null }
}

const _homeScopeKey = (root) => _homeScopeParts(root).scope

const _localCacheKey = (root) => `symbols.home.gridLayout.v4.${_homeScopeKey(root)}`

// The pre-fix key, built ONLY from a RESOLVED workspace id — deliberately not
// from `_wsScope`, whose whole problem is that it may hand back a stale
// pointer. Returns null until the id is known, so a migration can never read
// another workspace's layout and graft it onto this one (the exact
// cross-workspace bleed this file has fought twice).
const _legacyLocalCacheKey = (root) => {
  const id = root?.workspace?.id
  return id ? `symbols.home.gridLayout.v3.${id}` : null
}

// One-way migration so the fix does not RESET every saved arrangement — a
// key-format change that silently drops everyone's layout looks exactly like
// the bug it repairs. Copies the legacy value forward once, then removes it so
// a later boot cannot re-migrate stale geometry over newer work.
//
// ⚠️ Emptiness is checked as `parsed === null` / `!Array.isArray`, never as
// `if (!parsed)`: an injected-or-reset `[]` is TRUTHY yet zero-length, so a
// bare truthiness guard treats an empty survivor as real data. Same trap the
// state-layer audit flagged on reset-to-`[]` keys.
const _migrateLegacyLayoutKey = (root) => {
  const legacyKey = _legacyLocalCacheKey(root)
  if (!legacyKey) return null
  try {
    const raw = localStorage.getItem(legacyKey)
    if (raw === null) return null
    const parsed = JSON.parse(raw)
    if (!Array.isArray(parsed) || parsed.length === 0) {
      localStorage.removeItem(legacyKey)
      return null
    }
    const target = _localCacheKey(root)
    // Never clobber a layout already saved under the new key.
    if (localStorage.getItem(target) === null) _saveScoped(target, parsed)
    localStorage.removeItem(legacyKey)
    return parsed
  } catch {
    return null
  }
}

// Hidden-widget set persisted separately from the layout (which only tracks
// order and size). A fresh home (no saved value) defaults to
// _DEFAULT_HIDDEN_WIDGETS; the customize drawer offers them back and a
// restore persists (2026-06-09 per Nika).
const _hiddenWidgetsKey = (root) => `symbols.home.hiddenWidgets.v2.${_wsScopeForBootRead(root)}`

// Best-effort JSON write to a home-scoped localStorage key.
const _saveScoped = (key, val) => {
  try {
    localStorage.setItem(key, JSON.stringify(val))
  } catch (err) {
    console.warn('[home] cache write failed:', key, err && err.message)
  }
}
// Persist the hidden-widget set: localStorage (immediate, per-workspace cache)
// plus — when an sdk is supplied — the server profile (home_dashboard_prefs,
// per user × workspace) so a user's panel choices follow them across devices.
const _saveHiddenWidgets = (root, val, sdk) => {
  // The cache first, synchronously, so the board repaints instantly and an
  // offline device still remembers. It is a MIRROR, not an authority — the
  // server response overwrites it in `_flushPrefsPatch`.
  _saveScoped(_hiddenWidgetsKey(root), val)
  // IMMEDIATE, never debounced: hiding a widget is one decision, not a stream,
  // and a 600ms hold is a window the user closes by refreshing to check that it
  // stuck. See the header above `_savePrefsPatch` for the live measurement.
  if (sdk) _savePrefsPatch(sdk, root, { hidden_widgets: val }, 0, { immediate: true })
}

// Every widget id THIS workspace can actually render: the live catalog (house
// rows whose gate is on + the active module/app widgets) plus the app-plane
// and AI-plane ids, which are grantable here by construction even when a
// panel set does not name them. The durability filter uses it to refuse a key
// that has no card and no drawer row here.
const _renderableWidgetIds = (root) => {
  const ids = new Set(homeCatalogFor(root).map((w) => w.id))
  for (const id of _appPlaneWidgetIdsFor(root)) ids.add(id)
  for (const id of aiWidgetIdsFor(root)) ids.add(id)
  return ids
}

// The map to WRITE, given the live composed map. `_homeHiddenWidgets` stacks
// defaults, user choices and render clamps together (that is what the board
// needs); the durable stores must hold the choices alone. The decision itself
// lives in functions/homeHiddenPersist.js so a test can pin it without a
// browser — see that file's header for the three costs of getting it wrong.
// Called by BOTH gesture paths, so cache and server can never disagree.
const _durableHiddenMap = (root) =>
  persistableHiddenMap({
    stored: _storedHiddenMap(root),
    gestures: _userHiddenFor(root) || {},
    base: _resolveBootHidden(root).baseHidden,
    knownIds: _renderableWidgetIds(root),
    gatedIds: new Set(_gatedOffHouseIds(root))
  })

// What is persisted for this workspace RIGHT NOW: the scope-guarded server row
// if it has landed, else the workspace-scoped cache. Same precedence
// `_resolveBootHidden` reads its saved set through, so the durability filter
// prunes exactly what that resolver would have honoured.
// ── THE OUTBOX (WS-HOME-PREFS-PERSIST-ALWAYS-1) ────────────────────────────
//
// A decision the server has not ACKNOWLEDGED is not saved, and the browser is
// free to destroy the request that carries it. Measured on the fixed build,
// 2026-09-10: three hides clicked in one tick fire three (immediate) upserts,
// `location.reload()` runs in the same tick, and the browser ABORTS the newest
// in-flight request — 7 of the 8 ids landed, `app-documents` did not. Making
// the write immediate shrinks that window from 600ms to microseconds; it
// cannot close it, because an in-flight `fetch` dies with its document and the
// SDK has no `keepalive` transport.
//
// So the decision is written down BEFORE it is sent, and crossed off only when
// the server confirms it. The next boot finds the un-crossed entry and re-sends
// it. That is an outbox, and it is the only shape that makes "it always saves"
// true — it also covers the plane-mode case (hide something offline, it lands
// when the connection returns) which no retry timer can.
//
// THIS IS NOT THE CACHE OUTRANKING THE SERVER. The cache is a mirror and never
// wins. The outbox holds one thing: a write the server has not answered yet.
// It is cleared the instant it is confirmed, so it can never accumulate an
// opinion — and it is SCOPE-STAMPED, so a pending write for workspace A stays
// pending while the user is in B and replays when they come back.
//
// One record, not a queue: every write is an idempotent upsert of the FULL map
// for its field, so only the newest matters.
const _PREFS_OUTBOX_KEY = 'symbols.home.prefsOutbox.v1'

const _readPrefsOutbox = () => {
  try {
    const raw = localStorage.getItem(_PREFS_OUTBOX_KEY)
    const rec = raw ? JSON.parse(raw) : null
    return rec && typeof rec === 'object' && rec.scope && rec.payload ? rec : null
  } catch (_) {
    return null
  }
}

const _writePrefsOutbox = (scope, payload, at) => {
  try {
    localStorage.setItem(_PREFS_OUTBOX_KEY, JSON.stringify({ scope, payload, at }))
  } catch (err) {
    // Storage blocked (private mode / quota). The write still goes out; only
    // the crash-recovery half is unavailable, which is the pre-outbox
    // behaviour rather than a new failure.
    console.warn('[home] prefs outbox write failed:', err && err.message)
  }
}

// Cross it off — but ONLY if the record still describes the write we just had
// confirmed. A newer gesture may have replaced it while this request was in
// flight, and clearing that would drop a decision nobody has sent yet.
const _clearPrefsOutbox = (stamp) => {
  try {
    const rec = _readPrefsOutbox()
    if (rec && stamp && rec.at !== stamp) return
    localStorage.removeItem(_PREFS_OUTBOX_KEY)
  } catch (_) {
    /* storage blocked — nothing to clear */
  }
}

// The hidden map of an UNCONFIRMED write for this workspace, or null. Merged
// over the server row by both readers so the user's own last decision is on
// screen while it is still on its way.
const _pendingOutboxHidden = (root) => {
  const rec = _readPrefsOutbox()
  if (!rec || rec.scope !== _wsScope(root)) return null
  const hidden = rec.payload?.hidden_widgets
  return hidden && typeof hidden === 'object' && !Array.isArray(hidden) ? hidden : null
}

// The parsed cache for this workspace, or null when there is none. A cache is
// a MIRROR of the last answer the server confirmed — never a second opinion,
// and never a tie-breaker against a row that has landed.
const _cachedHiddenMap = (root) => {
  try {
    const raw = localStorage.getItem(_hiddenWidgetsKey(root))
    const parsed = raw ? JSON.parse(raw) : null
    return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed : null
  } catch (_) {
    return null
  }
}

const _storedHiddenMap = (root) => {
  const scopedPrefs = _prefsRowForScope(root)
  const { saved } = resolveSavedPrefsSource({
    row: scopedPrefs?.hidden_widgets,
    rowScope: scopedPrefs ? _wsScope(root) : null,
    scope: _wsScope(root),
    rowVersion: scopedPrefs?.dashboard_v || 0,
    currentVersion: CURRENT_DASHBOARD_V,
    cache: _cachedHiddenMap(root)
  })
  // An UNCONFIRMED write is still this user's latest decision — without it the
  // next gesture's durable map would be computed from a row that has not heard
  // about the previous one, and would quietly drop it.
  const pending = _pendingOutboxHidden(root)
  return pending ? { ...(saved || {}), ...pending } : saved || {}
}

// ── WS-HOME-PREFS-GESTURE-1 — an EXPLICIT user choice outranks every
// re-resolve, for the rest of this document's life ────────────────────────
//
// The bug, measured live on vault.pr.symbols.app (2026-09-09, staging shell
// 5f9cd5784, workspace 6a9d6f43208bdbbaed744900): hide an app icon while the
// board is still assembling and it comes BACK on screen ~1.5 s later. The
// trace is unambiguous — `_hideWidgetById` writes `{app-chat: true}` (state +
// cache + a PATCH carrying `app-chat: true`), then `_initGridstack`'s
// `root.update({ _homeHiddenWidgets: hiddenInit })` writes `{app-chat: false}`
// over it. `hiddenInit` comes from `_resolveBootHidden`, whose highest-priority
// source is the SERVER prefs row — and that row is still the PRE-gesture one:
// the write is debounced 600 ms, and the boot's own in-flight prefs GET
// resolves after it and re-publishes the stale row onto
// `root.userDashboardPrefs`. So the resolver is not wrong about its inputs; it
// simply has no way to know a newer, human decision already happened.
//
// The board is interactive from ~0.8 s after mount while module widgets keep
// landing for tens of seconds (see `_moduleWidgetsPending` + the 60 s
// `syncFromPrefs` poll), so this window is not a corner case — it is the
// window in which a person actually clicks. And the damage OUTLIVES the
// session: once state has been reverted, the NEXT gesture persists
// `{ ...root._homeHiddenWidgets, [next]: true }` — a map that now carries an
// explicit `false` for the widget the user hid a moment ago, on BOTH planes.
// That is the shape Nika reported ("hide just icons … this bug happens for a
// long time"): the local cache holds `true` for the icons she hid while the
// server row holds an explicit `false` for the same ids.
//
// The fix is a ledger of what the USER actually asked for, keyed by workspace
// scope, merged LAST by every resolver. Deliberately:
//   • module-scope, not root state — it must survive the very `root.update`
//     calls that clobber the map it protects;
//   • per-document, not persisted — it exists to win the boot race, and the
//     durable answer is the prefs row + cache the same gesture already wrote;
//   • scope-stamped with the SAME `_wsScope` the cache key and
//     `_savePrefsPatch` use, so a workspace switch can never carry one
//     tenant's choice onto another's board;
//   • recorded ONLY at the gesture sites (RemoveBtn, the widget context menu,
//     the customize drawer, drag-drop, the launcher's add intent, the colour
//     palette). The board's own reconcile (`_reconcileHomeBoard`) never
//     records here: recording a resolver's transient opinion would turn it
//     into a permanent user choice — the exact inversion this ledger exists
//     to prevent.
// `_enforceProjectGate` still runs AFTER the merge everywhere, so this can
// never reveal a module widget the workspace's panel set does not grant.
let _userWidgetChoices = { scope: null, hidden: {}, colors: {} }
const _userChoiceBucket = (root) => {
  const scope = _wsScope(root)
  if (_userWidgetChoices.scope !== scope) {
    _userWidgetChoices = { scope, hidden: {}, colors: {} }
  }
  return _userWidgetChoices
}
const _recordUserHidden = (root, id, hiddenFlag) => {
  if (!id || !root) return
  _userChoiceBucket(root).hidden[id] = !!hiddenFlag
}
const _recordUserColor = (root, id, family) => {
  if (!id || !root) return
  // `null` is a real choice — "clear back to the neutral solid" — so it is
  // STORED, not deleted. Deleting would let the boot resolver's older
  // non-null value win the merge and repaint the colour the user just cleared.
  _userChoiceBucket(root).colors[id] = family || null
}
// The ledger for the ACTIVE workspace, or an empty map. Never returns another
// scope's bucket: an unmatched scope reads as "no user choices here yet".
const _userHiddenFor = (root) =>
  _userWidgetChoices.scope === _wsScope(root) ? _userWidgetChoices.hidden : null
const _userColorsFor = (root) =>
  _userWidgetChoices.scope === _wsScope(root) ? _userWidgetChoices.colors : null

// ── PER-WIDGET COLOUR (2026-09-02) ───────────────────────────────────────
// `{ [widgetId]: <poster family key> }` — the colours the USER picked from
// the widget context menu's "Add color" palette. Persisted exactly like the
// hidden-widget set above, through the SAME scope builder, so a user's
// colours cannot leak across workspaces and cannot survive a workspace
// switch as another workspace's board.
//
// The key SHAPE and the boot PRECEDENCE are the two decisions that can leak a
// board across workspaces or resurrect a cleared colour, so both live in
// functions/homeWidgetColors.js where a test can pin them directly. The
// scope string handed in is the SAME `_wsScopeForBootRead` the hidden-widget
// key uses — one scope resolver in the app, no second copy to drift.
const _widgetColorsKey = (root) => homeWidgetColorsKey(_wsScopeForBootRead(root))

const _saveWidgetColors = (root, val, sdk) => {
  _saveScoped(_widgetColorsKey(root), val)
  // A colour pick is a decision too — same immediate rule as the hidden set.
  if (sdk) _savePrefsPatch(sdk, root, { widget_colors: val }, 0, { immediate: true })
}

// Resolve the boot colour map by the SAME precedence the hidden set uses:
// the scope-guarded server row ▸ the workspace-scoped local cache ▸ empty.
// Pure and synchronous, so the page can call it before the first paint and
// a coloured card never flashes neutral first.
// An empty map is the correct answer for a user who picked no colours — there
// is no default to overwrite, so `{}` needs no special reading here. (The
// hidden set reaches the same conclusion by a different route: see the
// ABSENT-vs-EMPTY note above `_prefsRowForScope`.)
const _resolveBootWidgetColors = (root) => {
  let raw = null
  try {
    raw = localStorage.getItem(_widgetColorsKey(root))
  } catch (_) {
    /* storage blocked (private mode / quota) → row-only, never a throw */
  }
  // Scope-guarded row (see `_prefsRowForScope`) ▸ the workspace-scoped cache.
  const resolved = readHomeWidgetColors(_prefsRowForScope(root), raw)
  // …then this document's own palette gestures (WS-HOME-PREFS-GESTURE-1).
  // Identical race to the hidden set: a re-resolve can read a prefs row that
  // still lags this document's gesture. A
  // stored `null` means "cleared", so it is applied as a DELETE rather than
  // written into the map — a null value would repaint the neutral solid via a
  // truthiness check somewhere downstream instead of removing the entry.
  const chosen = _userColorsFor(root)
  if (!chosen) return resolved
  const merged = { ...resolved }
  for (const [id, family] of Object.entries(chosen)) {
    if (family) merged[id] = family
    else delete merged[id]
  }
  return merged
}

// Set (or, with a falsy `family`, CLEAR) one widget's colour. The single
// write path — the palette in `HomeWidgetColorPicker` is its only caller, and
// it reaches it through `root._homeOps`, the same hook bag the context menu's
// refresh/hide/autolayout actions already use.
const _setWidgetColor = (id, family, root, sdk) => {
  if (!id || !root?.update) return
  const next = { ...(root._homeWidgetColors || {}) }
  // An explicit `null` for the cleared id, never an absent key. The server
  // merges `widget_colors` PER KEY (PrefsService `_mergeWidgetColorsPatch`):
  // a key present with `null` clears it, but an ABSENT key is left
  // untouched — so deleting it here and sending the remaining map never told
  // the server which id to clear, and the stale colour stayed in its row.
  if (family) next[id] = family
  else next[id] = null
  _recordUserColor(root, id, family)
  root.update({ _homeWidgetColors: next })
  _saveWidgetColors(root, next, sdk)
}

// Resolve the boot hidden-widget set by PRECEDENCE: the user's own choices
// (server profile if landed, then the workspace-scoped local cache) ▸ the
// workspace-admin default (home_default_panels) ▸ the global default
// (greeting-only for real users; showcase set for demo). Pure — every source
// is synchronously readable, so the page can call this BEFORE the widgets
// first render (their display factories read _homeHiddenWidgets) and the
// first paint already shows the correct set; the board's reconcile
// (`_reconcileHomeBoard`) re-resolves with the same helper as fresher sources
// land. Only a gesture persists.
// ABSENT vs EMPTY, and the shell no longer has to guess (WS-HOME-PREFS-
// PERSIST-ALWAYS-1). This block used to hold `_nonEmptyMap`, which mapped
// EVERY `{}` to "no saved set" and fell through to localStorage. It was a
// client-side patch over a SERVER ambiguity: `HomeDashboardPref.hiddenWidgets`
// defaulted to `{}`, so a LAYOUT-ONLY write (a drag sends `grid_layout` alone)
// created the row with an empty map and "never written" became
// indistinguishable from "nothing is hidden".
//
// Both halves of that guess were wrong in production, on opposite days:
//   • treating `{}` as an ANSWER wiped a board — row `hidden_widgets: {}` +
//     `grid_layout: [greeting]` while localStorage held the real 31-widget map;
//   • treating `{}` as an ABSENCE let one device's cache outrank the account —
//     measured 2026-09-10 on the Vault workspace, row `{}` and the board still
//     hiding eight widgets, i.e. exactly "it does not save / my other device
//     disagrees".
//
// The fix is at the layer that owns the fact: the column is NULLABLE now and
// the serializer emits `hidden_widgets: null` when it was never written
// (server src/core/models/HomeDashboardPref.js + PrefsService). So:
//   null / absent → never written → the boot cache may paint;
//   {}            → written and empty → authoritative, nothing is hidden.
// `resolveSavedPrefsSource` (functions/homePrefsBootSource.js) is the one
// place that reads that distinction, for the hidden set and for `_storedHiddenMap`.

// The user-prefs row ONLY when it provably belongs to the active workspace.
// The row tags its owner workspace (server serializer; _savePrefsPatch stamps
// the mirror the same way). During a switch the mirror briefly holds the
// PREVIOUS workspace's row, and a row with NO tag is unattributable — both
// read as "no saved prefs" rather than leak another workspace's hidden map /
// layout into this one (2026-07-30 isolation audit #4: _loadLayoutFromPrefs
// had the scope guard; its sibling readers — the hidden_widgets +
// dashboard_v reads — did not).
//
// That sibling gap is CLOSED: hidden_widgets and dashboard_v now both read
// through this function. The comment previously described it as "the
// still-live half" of the cross-workspace overwrite; that assertion was stale
// and is removed rather than softened — a comment claiming a live defect that
// is already fixed buys the next auditor a re-investigation or, worse, a
// re-fix on top of the fix.
//
// ⚠️ ASYMMETRY WITH `_wrongScope`, deliberate, do not "harmonise" without
// reading this: an UNTAGGED row is REJECTED here (`!prefs.workspace_id`) but
// is NOT considered wrong-scope by `_wrongScope` (whose `&&` short-circuits on
// a falsy tag), so reconcile would accept what boot refused. Verified
// 2026-08-16 that the server cannot currently PRODUCE an untagged row:
// `HomeDashboardPref.workspace` is `required: true` and the serializer maps
// `workspace_id: d.workspace ?? null`. The only rows that could be untagged
// are legacy documents written before that constraint existed — mongoose
// `required` gates writes, not documents already on disk. Treat the strict
// reading here as correct and `_wrongScope` as the lenient one.
const _prefsRowForScope = (root) => {
  const prefs = root?.userDashboardPrefs
  if (!prefs || !prefs.workspace_id) return null
  return prefs.workspace_id === _wsScope(root) ? prefs : null
}

const _resolveBootHidden = (root) => {
  const catalog = homeCatalogFor(root)
  const globalHidden = root?.isDemo
    ? Object.fromEntries(catalog.map((w) => [w.id, !_DEMO_VISIBLE_WIDGETS.includes(w.id)]))
    : _globalDefaultHidden(root)
  const wsPanels = _resolveWsPanelsInfo(root)
  const wsVisible = wsPanels.panels
  // A panel set decides what its writers could SAY — the ids it names, and
  // the house panels it leaves out. Its silence about a house DEFAULT id
  // (the greeting, Next up, the App strip shortcuts — no panel-set writer ever
  // offered them) is not a choice, so those follow the global default: the
  // redesign's defaults reach every workspace an admin has configured
  // (DESIGN-WL-HOME-DEFAULTS-ALL-WORKSPACES-1). A takeover module's panel set
  // keeps governing every id (functions/homeDefaultWidgets.js).
  const wsHidden = Array.isArray(wsVisible)
    ? panelSetHiddenMap({
        catalogIds: catalog.map((w) => w.id),
        panels: wsVisible,
        houseDefaultIds: _DEFAULT_VISIBLE_WIDGETS,
        globalHidden,
        panelsGovernHouseDefaults:
          typeof wsPanels.governsHouse === 'boolean'
            ? wsPanels.governsHouse
            : !!takeoverModuleRef(root)
      })
    : null
  const baseHidden = wsHidden || globalHidden
  // Installed apps' widget defaults join the base too — the one fold every
  // default path uses, so no pass can disagree about an app widget's default
  // state and visibly flip it.
  // Demo keeps its curated set.
  if (!root?.isDemo) _foldAppWidgetDefaults(baseHidden, wsVisible, root)
  // The user's saved set is honoured only when it carries the current
  // dashboard version; an older stamp is ignored once so everyone lands on
  // the new default (the one-time reset-everyone). The bumped cache key (v2)
  // abandons stale localStorage the same way. MUST match the columns the
  // write path (_savePrefsPatch) sends — snake_case, on userDashboardPrefs.
  // WS-HOME-PREFS-PERSIST-ALWAYS-1 — PRESENCE decides, not emptiness. A row
  // that belongs to this scope IS the answer, `{}` included ("nothing is
  // hidden"). The cache may only paint while no such row has landed. The
  // decision itself is pure — functions/homePrefsBootSource.js, whose header
  // carries the live measurement of the cache outranking an empty row.
  const scopedPrefs = _prefsRowForScope(root)
  const { saved: savedFromStore } = resolveSavedPrefsSource({
    row: scopedPrefs?.hidden_widgets,
    rowScope: scopedPrefs ? _wsScope(root) : null,
    scope: _wsScope(root),
    rowVersion: scopedPrefs?.dashboard_v || 0,
    currentVersion: CURRENT_DASHBOARD_V,
    cache: _cachedHiddenMap(root)
  })
  // …then this device's UNCONFIRMED write, if it has one. Not a second opinion
  // — a decision still on its way to the server (see the outbox header).
  const _pendingHidden = _pendingOutboxHidden(root)
  const savedHidden = _pendingHidden
    ? { ...(savedFromStore || {}), ..._pendingHidden }
    : savedFromStore
  // The user's per-widget choices win on top of the base default; a widget
  // they explicitly restored (stored `false`) stays shown, and a widget new
  // to the catalog inherits the base. A workspace panel set (wsHidden —
  // live OR cached) defines that BASE for its dashboard, but it must NOT
  // discard the user's own entries: the previous `wsAuthoritative` discard
  // meant every boot on a panels workspace re-hid what the Customize drawer
  // had just restored-and-persisted — the "dashboard swept my widgets" bug
  // (2026-08-01 per Nika; restores must stick, 2026-06-09 rule). Module-
  // scoped widgets are NOT loosened by this: _enforceProjectGate below
  // clamps them to the panel set AFTER the merge, so user prefs can only
  // ever govern generic widgets.
  // …and an EXPLICIT gesture made in this document outranks even the row
  // (WS-HOME-PREFS-GESTURE-1): this resolver re-runs from `_reconcileHomeBoard`
  // while the board is interactive, and its highest-priority source is a
  // prefs row that is still a debounce — and one in-flight GET — behind.
  const hidden = _composeHiddenMap(baseHidden, savedHidden, root, wsVisible)
  return { hidden, savedHidden, baseHidden, wsVisible }
}

// Strip engine-rename artifacts from a stored layout. When BOTH widget planes
// (static v1 twin + v2 module host) briefly held the same gs-id, Gridstack's
// engine de-collided the second registration by renaming it `<id>_<n>`
// (gridstack-engine prepareNode) and wrote that back to the DOM — and the next
// save PERSISTED those phantom rows. Widget ids can never legitimately contain
// an underscore (the manifest normalizer enforces /^[a-z0-9-]+$/ and every
// catalog id is dash-cased), so a `<base>_<n>` row whose base id also exists in
// the same layout is definitively poison: drop it, or every boot re-applies the
// phantom geometry and the corruption can never heal.
const _sanitizeLayout = (layout) => {
  if (!Array.isArray(layout) || !layout.length) return layout
  const ids = new Set(layout.map((n) => n && n.id))
  const clean = layout.filter((n) => {
    const m = n && typeof n.id === 'string' && n.id.match(/^(.+)_\d+$/)
    return !(m && ids.has(m[1]))
  })
  return clean.length ? clean : layout
}

const _loadLayoutFromPrefs = (root) => {
  // Scope-guarded read — see _prefsRowForScope: a mid-switch stale mirror or
  // an unattributable (tag-less) row must never shape this workspace's grid.
  const layout = _prefsRowForScope(root)?.grid_layout
  return Array.isArray(layout) && layout.length ? _sanitizeLayout(layout) : null
}

// Fold a PROVISIONAL org-only layout into the refined `org~ws` key once the
// redirect names the workspace. Without this the pre-redirect save is orphaned
// under the shallow key and the post-redirect board reads a different one —
// the exact two-key split measured in leg 3.
//
// Direction is one-way and never destructive: the refined key WINS if it
// already exists (it is strictly better-scoped), and the provisional key is
// removed either way so it cannot go stale and re-surface on a later
// `/w/<org>` load — which is how the (8,3) vs (8,6) vs (0,6) three-way
// disagreement arose.
const _refineProvisionalLayoutKey = (root) => {
  const { scope, provisional } = _homeScopeParts(root)
  if (!provisional || provisional === scope) return null
  const shallowKey = `symbols.home.gridLayout.v4.${provisional}`
  const refinedKey = `symbols.home.gridLayout.v4.${scope}`
  try {
    const raw = localStorage.getItem(shallowKey)
    if (raw === null) return null
    const parsed = JSON.parse(raw)
    localStorage.removeItem(shallowKey)
    // Same `[]`-is-truthy discipline as the v3 migration: an empty array is a
    // miss, never a layout to promote.
    if (!Array.isArray(parsed) || parsed.length === 0) return null
    if (localStorage.getItem(refinedKey) === null) _saveScoped(refinedKey, parsed)
    return parsed
  } catch {
    return null
  }
}

const _loadLayoutFromCache = (root) => {
  try {
    // Refinement runs BEFORE the read: if the redirect has landed and a
    // provisional layout is waiting, it must be folded in first or this read
    // misses and falls back to defaults.
    _refineProvisionalLayoutKey(root)
    const raw = localStorage.getItem(_localCacheKey(root))
    if (raw === null) {
      // Miss under the URL-scoped key. Before falling back to defaults — which
      // is what LOOKED like "my arrangement was lost" — try the pre-fix
      // id-scoped key. Only fires when the resolved id is already known, so it
      // can never adopt another workspace's layout.
      const migrated = _migrateLegacyLayoutKey(root)
      return migrated ? _sanitizeLayout(migrated) : null
    }
    const parsed = JSON.parse(raw)
    // `parsed.length === 0` is a real (if unusual) saved state, not a miss, so
    // it must NOT fall through to the legacy read — that would resurrect an
    // arrangement the user had emptied.
    return Array.isArray(parsed) && parsed.length ? _sanitizeLayout(parsed) : null
  } catch {
    return null
  }
}

const _saveLayoutToCache = (root, layout) => {
  // Consume any provisional key on the way past, so a save cannot leave a
  // shallow sibling behind to be read on a later `/w/<org>` load.
  _refineProvisionalLayoutKey(root)
  return _saveScoped(_localCacheKey(root), layout)
}

// ── Layout v2: an order of widgets, one size each ─────────────────
// (functions/homeLayoutV2.js, HOME_WIDGETS_SPEC.md §6.) It lives in the same
// places the 12-column GridStack map did: `grid_layout` on the account row and
// `symbols.home.gridLayout.v4.<scope>` on this device; a 12-column record
// converts once, on read.
let _userLayoutChoice = { scope: null, layout: null }
const _recordUserLayout = (root, layout) => {
  _userLayoutChoice = { scope: _wsScope(root), layout }
}
const _userLayoutFor = (root) =>
  _userLayoutChoice.scope === _wsScope(root) ? _userLayoutChoice.layout : null
// A reset forgets this session's layout and hide gestures, so the defaults it
// writes are what the board resolves to.
const _forgetHomeGestures = () => {
  _userLayoutChoice = { scope: null, layout: null }
  _userWidgetChoices = { scope: null, hidden: {}, colors: {} }
}

// An unconfirmed layout write from the last session (the outbox), THIS scope.
const _pendingOutboxLayout = (root) => {
  const rec = _readPrefsOutbox()
  if (!rec || rec.scope !== _wsScope(root)) return null
  const gl = rec.payload && rec.payload.grid_layout
  return Array.isArray(gl) ? gl : null
}

// The state row of any widget the board can render — a page widget's
// declared row, else the active modules' row.
const _widgetRowFor = (root, id) =>
  _STATIC_WIDGET_ROWS.get(id) || _allModuleWidgetsFor(root).find((w) => w.id === id) || null

const _defaultWidgetSize = (root, id) => {
  const row = _widgetRowFor(root, id)
  return row ? homeWidgetDefaultSize(row) : 's'
}

// The DEFAULT board: every widget this workspace can show, in the reading
// order of its declared cell (static spec geometry, module manifest spec), at
// its declared size — and, when the workspace sets `home_default_panels`, the
// cards that list names first, in ITS order (HOME-DEFAULT-PANELS-ORDER-1: the
// list sets which cards are on AND their order; a cross-app first screen no
// longer depends on each app's manifest coordinates). A member's saved layout
// keeps its own order. The greeting is the page head, not a grid cell.
const _defaultLayoutEntries = (root) => {
  // Resolve the current module rows once. Looking up each default size through
  // _widgetRowFor re-ran the entire catalog and its visibility gates per card.
  // These indexes live for this pass only: subsequent state changes stay live.
  const moduleWidgets = _allModuleWidgetsFor(root)
  const moduleRows = new Map(moduleWidgets.map((w) => [w.id, w]))
  const catalog = homeCatalogFor(root, moduleWidgets)
  const legacy = []
  const seen = new Set(['greeting'])
  for (const w of _specLayoutFor(root, catalog)) {
    if (seen.has(w.id)) continue
    seen.add(w.id)
    legacy.push(w)
  }
  for (const w of moduleWidgets) {
    if (seen.has(w.id)) continue
    seen.add(w.id)
    const spec = w.spec || {}
    legacy.push({ id: w.id, x: spec.x, y: spec.y, w: spec.w || 4, h: spec.h || 3 })
  }
  for (const w of catalog) {
    if (seen.has(w.id)) continue
    seen.add(w.id)
    legacy.push({ id: w.id })
  }
  const entries = homeLayoutFromLegacy(legacy).map((e) => {
    const row = _STATIC_WIDGET_ROWS.get(e.id) || moduleRows.get(e.id)
    return { id: e.id, size: row ? homeWidgetDefaultSize(row) : 's' }
  })
  return homeLayoutOrderBy(entries, _resolveWsPanels(root))
}

const _resolveHomeLayout = (root) => {
  const row = _prefsRowForScope(root)
  const saved =
    _userLayoutFor(root) ||
    _pendingOutboxLayout(root) ||
    (row ? _loadLayoutFromPrefs(root) : _loadLayoutFromCache(root))
  const layout = homeLayoutNormalize(Array.isArray(saved) ? saved : []).filter(
    (e) => e.id !== 'greeting'
  )
  return homeLayoutEnsure(layout, _defaultLayoutEntries(root))
}

const _currentHomeLayout = (root) =>
  Array.isArray(root && root._homeLayout) && root._homeLayout.length
    ? root._homeLayout
    : _resolveHomeLayout(root)

// Persist the live layout at once — a reorder or a size pick is a decision,
// never debounced; the outbox and the single-flight write own its durability.
const _persistHomeLayout = (root, sdk) => {
  const layout = _currentHomeLayout(root)
  _recordUserLayout(root, layout)
  _saveLayoutToCache(root, layout)
  if (sdk) _savePrefsPatch(sdk, root, { grid_layout: layout }, 0, { immediate: true })
}

// Live reorder while a card is dragged — persisted on the drop.
const _moveHomeWidget = (root, id, targetId) => {
  const layout = _currentHomeLayout(root)
  const to = layout.findIndex((e) => e.id === targetId)
  if (to < 0) return
  const next = homeLayoutMove(layout, id, to)
  if (next === layout) return
  _recordUserLayout(root, next)
  root.update({ _homeLayout: next }, { preventFetch: true })
}

// The keyboard path: one place back or forward past the next VISIBLE widget,
// persisted at once.
const _moveHomeWidgetBy = (root, id, delta, sdk) => {
  const layout = _currentHomeLayout(root)
  const from = layout.findIndex((e) => e.id === id)
  if (from < 0) return
  const hidden = root._homeHiddenWidgets || {}
  let to = from + delta
  while (to > 0 && to < layout.length - 1 && hidden[layout[to].id]) to += delta
  to = Math.max(0, Math.min(layout.length - 1, to))
  if (to === from) return
  const next = homeLayoutMove(layout, id, to)
  _recordUserLayout(root, next)
  root.update({ _homeLayout: next }, { preventFetch: true })
  _persistHomeLayout(root, sdk)
}

const _setHomeLayout = (root, layout, opts, sdk) => {
  const next = homeLayoutNormalize(layout)
  _recordUserLayout(root, next)
  root.update({ _homeLayout: next }, { preventFetch: true })
  if (!opts || opts.persist !== false) _persistHomeLayout(root, sdk)
}

// A size pick from edit mode — never below the widget's own minimum.
const _setHomeWidgetSize = (root, id, size, sdk) => {
  const row = _widgetRowFor(root, id)
  const min = row && (row.minSize || (row.spec && row.spec.minSize))
  const next = homeLayoutSetSize(_currentHomeLayout(root), id, size, min)
  _recordUserLayout(root, next)
  root.update({ _homeLayout: next }, { preventFetch: true })
  _persistHomeLayout(root, sdk)
}

// Accumulate an arbitrary patch (grid_layout, hidden_widgets) and debounce-write
// it to the per-user, per-workspace home_dashboard_prefs row via the
// workspaceProject API. The row's columns ARE grid_layout / hidden_widgets /
// dashboard_v; the /sb proxy pins user_id + workspace_id server-side, so we send
// only the data columns. Every write stamps the current dashboard version so a
// user who has customized is exempt from the next reset-everyone. The merged set
// is mirrored onto root.userDashboardPrefs immediately for cross-component reads.
let _savePrefsTimer = null
let _pendingPrefsPatch = null
let _pendingPrefsScope = null
// Transport hiccups are ROUTINE, not exceptional: the API restarts under
// nodemon in dev and rolls during a deploy in prod, and every in-flight request
// in that window fails with a bare "Failed to fetch". Swallowing those (the old
// behaviour) meant a drag made in that window stayed correct on screen and in
// localStorage while never reaching the server — so it came back wrong on the
// next device, or after a cache clear. The write is an idempotent upsert of the
// full payload, so retrying is always safe.
const _PREFS_MAX_RETRIES = 3
// COALESCING IS FOR STREAMS, NOT FOR DECISIONS (WS-HOME-PREFS-PERSIST-ALWAYS-1).
//
// The debounce below existed for one real reason: a drag emits a burst of
// gridstack change events and each one carries a whole layout. Hiding a widget
// is not that. It is ONE deliberate act, and holding it for 600ms put the
// user's decision in a window they can end at any moment — and they do, because
// the natural thing to do after hiding something is to refresh and check that
// it stuck.
//
// MEASURED, live on vault.pr.symbols.app (staging shell f6ce7c87e, Vault
// workspace 6a9d6f43208bdbbaed744900, 2026-09-10). Hide `app-agents`,
// `app-people`, `app-documents` and reload in the same tick:
//   • localStorage cache → 8 hidden ids (written synchronously by the gesture);
//   • server row         → 5 hidden ids, the three newest MISSING;
//   • after the reload   → all three back on screen, because boot correctly
//                          prefers the row and the row never heard about them.
// That is Nika's report, exactly, and it is the whole bug: the PUT died with
// the document. No error was raised anywhere — the timer simply never fired.
//
// So the write path now takes the kind of change into account. `immediate`
// writes go out on the spot (a hide, a restore, a colour pick, a reset);
// `grid_layout` keeps its 600ms coalesce because it is a stream. Every write
// carries the FULL map for its field and the upsert is idempotent, so several
// in flight at once cannot corrupt anything — the newest simply wins, which
// `_prefsWriteSeq` below enforces on the RESPONSE side too.
const _PREFS_DEBOUNCE_MS = 600

// Monotonic write id. A response may land out of order (a retry that succeeds
// after a newer write already did), and adopting an older server row would
// undo the newer decision on screen. Only the newest write's response is
// adopted; older ones are acknowledged and discarded.
let _prefsWriteSeq = 0

// Bumped every time a decision is QUEUED. The outbox record carries the value
// it had when it was written, so a confirmation can only cross off the record
// it actually sent — a newer decision queued mid-flight keeps its own entry.
let _outboxStamp = 0

// How to flush whatever is pending, captured at queue time so the page-hide
// listener below needs no closure over a render.
let _pendingPrefsFlush = null

// Mark/clear the "your change is not saved" state. Never silent: a decision the
// server did not accept must be visible, or the user learns to distrust the
// board. Cleared by the next success.
const _setPrefsUnsaved = (root, unsaved, err) => {
  if (!root?.update) return
  if (!!root._homePrefsUnsaved === !!unsaved) return
  root.update({ _homePrefsUnsaved: !!unsaved })
  if (!unsaved) return
  try {
    root.call?.('openNotification', {
      type: 'error',
      title: root.call?.('polyglot', 'workspace.home.prefs.notSavedTitle'),
      message:
        root.call?.('polyglot', 'workspace.home.prefs.notSavedMessage') || (err && err.message)
    })
  } catch (_) {
    /* the notice is best-effort; the flag is the contract */
  }
}

// Issue the accumulated patch NOW. Split out of `_savePrefsPatch` so three
// callers share one body: the debounce timer, an `immediate` write, and the
// page-hide flush.
// SINGLE-FLIGHT, and it is a correctness rule rather than a politeness one.
// Making a decision write IMMEDIATE meant a burst of clicks issued a request
// each, every one an upsert of the FULL map — and HTTP does not promise
// arrival order. Measured on the fixed build (2026-09-10, three hides in one
// tick): the third request carried all three ids, the second carried two, the
// SECOND arrived last, and the row settled at two. An older payload won.
// So one request at a time: a write requested while one is in flight is merged
// into `_pendingPrefsPatch` and sent when the current one settles. The newest
// value always goes last, a burst collapses to at most two requests, and the
// abort window shrinks with the request count.
let _prefsInFlight = null

async function _flushPrefsPatch(sdk, root, scope, attempt, opts = {}) {
  // Already sending. The caller's patch is in `_pendingPrefsPatch` (merged by
  // `_savePrefsPatch` before it called us) and the tail below will send it.
  if (_prefsInFlight) return _prefsInFlight
  const merged = _pendingPrefsPatch || {}
  _pendingPrefsPatch = null
  if (!Object.keys(merged).length) return
  if (!root?.currentUserId || !sdk?.execute) return
  // The claim flipped while the write was debouncing — the snapshot belongs
  // to the previous workspace; writing it now would cross-tenant clobber.
  if (_wsScope(root) !== scope) return
  const payload = { ...merged, dashboard_v: CURRENT_DASHBOARD_V }
  const seq = ++_prefsWriteSeq
  // The outbox entry was written at QUEUE time (`_savePrefsPatch`), so a
  // decision is recoverable even if it never reaches this function — a tab
  // closed while another write was in flight, for instance. Read the stamp it
  // carries so the confirmation below crosses off exactly this record.
  const stamp = _outboxStamp
  root.update({
    userDashboardPrefs: { ...(root.userDashboardPrefs || {}), ...payload, workspace_id: scope }
  })
  // workspaceId pins the row server-side (readExplicitWorkspaceId) so the
  // write is bound to the workspace the snapshot was taken in, not to
  // whatever claim is active when the request lands. The 'default'
  // fallback scope isn't a real workspace id — leave the server on its
  // claim resolution there.
  const request = sdk.execute('workspaceProject.homeDashboardPrefs', 'update', {
    payload,
    ...(scope !== 'default' ? { workspaceId: scope } : {})
  })
  _prefsInFlight = request
  try {
    const stored = await request
    // RECONCILE TO WHAT THE SERVER STORED. The row is the source of truth and
    // the write is not a promise that it took our value verbatim — the tenancy
    // guard drops a provably foreign id (server widgetTenancy.js). Adopting the
    // response is what keeps the board, the cache and the account in agreement
    // instead of three hopeful copies.
    if (
      seq === _prefsWriteSeq &&
      _wsScope(root) === scope &&
      stored &&
      typeof stored === 'object'
    ) {
      root.update({ userDashboardPrefs: { ...stored, workspace_id: scope } })
      // The cache is a MIRROR of the confirmed answer, never a second opinion.
      if (stored.hidden_widgets && typeof stored.hidden_widgets === 'object') {
        _saveScoped(_hiddenWidgetsKey(root), stored.hidden_widgets)
      }
    }
    // CONFIRMED — cross it off. Anything still in the outbox after this is a
    // decision the server has genuinely not seen.
    _clearPrefsOutbox(stamp)
    _setPrefsUnsaved(root, false)
  } catch (err) {
    // Re-queue with backoff instead of dropping the change on the floor. Bail
    // out if the workspace moved on — that snapshot no longer belongs to the
    // active scope and re-sending it would be the cross-workspace clobber the
    // scope guards above exist to prevent.
    if (attempt < _PREFS_MAX_RETRIES && _wsScope(root) === scope) {
      // A retry keeps the ORIGINAL urgency: a decision that failed once is not
      // suddenly a stream, and adding the debounce back would re-open the very
      // window this ticket closed.
      setTimeout(
        () => _savePrefsPatch(sdk, root, merged, attempt + 1, opts),
        800 * Math.pow(2, attempt)
      )
      return
    }
    console.warn(
      `[home] save dashboard prefs failed after ${attempt + 1} attempt(s):`,
      err && err.message
    )
    _setPrefsUnsaved(root, true, err)
  } finally {
    _prefsInFlight = null
    // Whatever the user decided while this request was in the air goes NOW,
    // and it goes last — that is what makes the newest value the stored one.
    if (_pendingPrefsPatch && Object.keys(_pendingPrefsPatch).length) {
      _flushPrefsPatch(sdk, root, _wsScope(root), 0, opts)
    }
  }
}

const _savePrefsPatch = (sdk, root, patch, attempt = 0, opts = {}) => {
  const scope = _wsScope(root)
  // A patch queued for a DIFFERENT workspace must never merge with (or ride
  // the timer of) the previous one — drop the stale pending data.
  if (_pendingPrefsScope !== scope) _pendingPrefsPatch = null
  _pendingPrefsScope = scope
  // A RETRY carries an OLDER snapshot than anything queued since it failed, so
  // it merges UNDERNEATH the pending patch — a re-sent stale value must never
  // clobber a fresher one the user produced while the retry was waiting.
  _pendingPrefsPatch = attempt
    ? { ...patch, ...(_pendingPrefsPatch || {}) }
    : { ...(_pendingPrefsPatch || {}), ...patch }
  // WRITE IT DOWN THE MOMENT IT IS DECIDED — before any request exists. From
  // here the decision survives an aborted request, a closed tab, a crash and
  // an offline device; the next boot finds it un-crossed-off and re-sends it.
  // See the outbox header above `_PREFS_OUTBOX_KEY`.
  _outboxStamp += 1
  _writePrefsOutbox(
    scope,
    { ...(_pendingPrefsPatch || {}), dashboard_v: CURRENT_DASHBOARD_V },
    _outboxStamp
  )
  // Remember how to flush whatever is pending when the document goes away.
  _pendingPrefsFlush = { sdk, root, scope }
  _bindPrefsHideFlush()
  if (_savePrefsTimer) clearTimeout(_savePrefsTimer)
  if (opts.immediate) {
    _flushPrefsPatch(sdk, root, scope, attempt, opts)
    return
  }
  _savePrefsTimer = setTimeout(() => {
    _savePrefsTimer = null
    _flushPrefsPatch(sdk, root, scope, attempt, opts)
  }, _PREFS_DEBOUNCE_MS)
}
// Re-send a decision the server never acknowledged (see the outbox header).
// Called once per home boot, AFTER the prefs row has had its chance to land,
// so a confirmed write is already crossed off and this is a no-op for
// everybody whose last write completed normally.
//
// Idempotent by construction: the payload is a full-map upsert, so re-sending
// one that DID land (only its response was lost) changes nothing.
const _replayPrefsOutbox = (sdk, root) => {
  const rec = _readPrefsOutbox()
  if (!rec || !sdk?.execute || !root?.currentUserId) return false
  const scope = _wsScope(root)
  // A pending write for ANOTHER workspace stays pending — it replays when the
  // user returns there, and must never land under this workspace's row.
  if (rec.scope !== scope) return false
  _savePrefsPatch(sdk, root, rec.payload, 0, { immediate: true })
  return true
}

const _saveLayoutToPrefs = (sdk, root, layout) =>
  _savePrefsPatch(sdk, root, { grid_layout: layout })

// LAST CHANCE for a coalescing write. `grid_layout` is the only field still
// debounced, so a drag finished inside the last 600ms of the document's life
// would otherwise be lost the same way the hides were. `pagehide` is the event
// that fires for a reload, a same-tab navigation and a tab close alike;
// `visibilitychange → hidden` catches the mobile/background path that never
// fires `pagehide`. Both are cheap and idempotent — the flush is a no-op when
// nothing is pending.
const _flushPendingPrefsOnHide = () => {
  if (!_pendingPrefsPatch || !_pendingPrefsFlush) return
  if (_savePrefsTimer) {
    clearTimeout(_savePrefsTimer)
    _savePrefsTimer = null
  }
  const { sdk, root, scope } = _pendingPrefsFlush
  _flushPrefsPatch(sdk, root, scope, 0)
}
// The latch is MODULE scope, never a `window` flag (Rule 64): this module is
// evaluated once per document, which is exactly the lifetime the listeners need.
let _hideFlushBound = false
const _bindPrefsHideFlush = () => {
  if (_hideFlushBound || typeof window === 'undefined') return
  _hideFlushBound = true
  window.addEventListener('pagehide', _flushPendingPrefsOnHide)
  window.addEventListener('visibilitychange', () => {
    if (typeof document !== 'undefined' && document.visibilityState === 'hidden') {
      _flushPendingPrefsOnHide()
    }
  })
}

// Discard any debounced grid_layout/hidden_widgets write still in flight.
// RESET-LAYOUT-1: ResetBtn calls this FIRST, before stamping its own reset
// payload — without it, a drag/resize made in the ~600ms before the click
// (the pending patch this cancels) can flush its STALE pre-reset geometry
// to the server AFTER the reset's explicit `grid_layout: null` write,
// silently resurrecting the exact size/position Reset was meant to clear.
// Full reload gives that stale timer ample time to fire first — this app's
// own boot can run seconds long (see the 60s syncFromPrefs poll below).
const _cancelPendingPrefsSave = () => {
  if (_savePrefsTimer) {
    clearTimeout(_savePrefsTimer)
    _savePrefsTimer = null
  }
  _pendingPrefsPatch = null
}

// "Reset layout to defaults" — in place, no reload: forget this session's
// gestures and re-resolve. The drawer's reset has just written the account
// row (`grid_layout: null`, default hides), so the board falls back to the
// declared default order and sizes.
const _resetLayoutInPlace = (root) => {
  if (!root || !root.update) return false
  _forgetHomeGestures()
  root.update(
    { _homeLayout: _resolveHomeLayout(root), _homeHiddenWidgets: _resolveBootHidden(root).hidden },
    { preventFetch: true }
  )
  return true
}

// True while the workspace's module / app routes are still loading — the
// window in which a saved layout can name widgets that have not rendered yet.
// The page now shows before the apps finish (boot fix step 2), so on an
// apps-only workspace the loader flag, the module stash and the published
// list can all be empty here: the workspace RECORD's declared app list (the
// source the home signals and Next up read) keeps the window open.
const _moduleRoutesPending = (root) => {
  if (!root) return false
  // Apps still setting up after the reveal (boot fix step 4b): an app's widgets
  // publish only after its page wrap, so its saved cells keep their skeletons.
  if (root._appsSettingUp === true) return true
  if (root._appRoutesSettled === true) return false
  // Only INSTALLED APP entries count (installedAppRefs, the one shared reader):
  // the list also holds server-managed rows that are not apps (the
  // records-grant `legacy-backfill` row) — they never load and publish no widget.
  return !!(
    root._dataLoading ||
    root.workspaceProject ||
    installedAppRefs(root.workspaceSettings).length > 0 ||
    installedAppRefs(root.workspace?.settings).length > 0 ||
    (Array.isArray(root.workspaceApps) && root.workspaceApps.length)
  )
}

// Saved cells whose widget is still on its way hold a skeleton in their final
// place (HomeWidgetPending), so the board never reflows when it lands. Once
// the routes settle, an entry nobody rendered is simply not shown.
const _pendingWidgetRows = (root) => {
  if (!_moduleRoutesPending(root)) return []
  const layout = Array.isArray(root && root._homeLayout) ? root._homeLayout : []
  const hidden = (root && root._homeHiddenWidgets) || {}
  const live = new Set([
    ..._STATIC_WIDGET_ROWS.keys(),
    ..._allModuleWidgetsFor(root).map((w) => w.id)
  ])
  // Once the apps list is published, only a widget an app of THIS member still
  // sets up keeps its skeleton (boot fix step 4b: `pendingWidgets`); a saved cell
  // of an app the member cannot use drops then, not after every app settled.
  const arriving = Array.isArray(root && root.workspaceApps)
    ? new Set(
        root.workspaceApps.flatMap((a) =>
          a && Array.isArray(a.pendingWidgets) ? a.pendingWidgets : []
        )
      )
    : null
  return layout
    .filter((e) => e && !live.has(e.id) && !hidden[e.id])
    .filter((e) => !arriving || arriving.has(e.id) || !root._appsSettingUp)
    .map((e) => ({ key: 'pending-' + e.id, id: e.id, title: '', icon: 'star', size: e.size }))
}

// The dashboard's canonical tab title — helmet writes it when the page mounts,
// and the AppWindow PageSlot's close branch RE-writes it (the keep-alive slot
// leaves the last panel page mounted, so its helmet title would otherwise
// linger on the tab after close).
//
// Through `pageTitle`, the helper every other page uses: it resolves the key in
// the session language and appends the brand. This was the literal `Home / …`,
// so the dashboard was the one tab that stayed English on a Georgian workspace
// (measured live, 2026-09-20).
const HOME_TITLE = (el) => el.call('pageTitle', 'shell.app.home')

export const main = {
  // AppPage = full-bleed flow-x layout (like /chat): a flush left rail and
  // the scrolling main column, each scrolling independently.
  extends: 'AppPage',
  metadata: (el) => ({ title: HOME_TITLE(el) }),
  height: '100%',
  // NOTE: no per-page module-rail clearance here anymore — the SHELL root
  // pads left by the rail's live width for EVERY in-flow page when the
  // active module declares chrome.rail (app.js shellApp paddingLeft), the
  // chat-sidebar-style docked column done once at the root instead of per
  // surface (2026-08-14 per Nika "fix the layout on all pages for
  // bellforge sidebar … make workspace more abstract and modular").
  // Page-level base color so the gutter on wide monitors stays in the
  // active theme. The dot grid lives on MainColumn (the scroll container)
  // so dots scroll with content as the user scrolls. Tried moving
  // them up here onto `main` with `overflow: visible` so the body
  // scrolls naturally, but AppPage's grandparent grid clamps
  // height to 100dvh — Gridstack then can't size widgets, and the
  // layout collapses. Keeping the scroll on MainColumn is the
  // working compromise.
  //
  // ONE ground chain (BF-WIDGET-INK-1, 2026-08-16): the page paints the
  // SAME `var(--stage-bg, var(--color-stage))` the shell root and MainColumn
  // paint (canvasBackground.js STAGE_BACKGROUND). It used to hold its own
  // gray15/gray5 — a fourth resolution path between the root and the board
  // that showed as a band in the wide-monitor gutter and beside a docked
  // module rail. `style`, not `background`: a var() chain is theme-agnostic
  // and must not be split into '@dark'/'@light' halves.
  style: { background: 'var(--stage-bg, var(--color-stage))' },

  // #2312 — the Projects widget showed the PREVIOUS org's projects after the
  // user switched owner/org. onCreate fetches _projectsForActiveOrg once using
  // the boot-time org; the page stays mounted on switch (jwtClaims updates
  // reactively) so the one-shot fetch went stale. Re-fetch whenever the active
  // org id actually changes. Guarded by el.__loadedOrgId so this no-ops on the
  // frequent unrelated root updates.
  onUpdate: (el, s) => {
    const root = s.root
    // Per-widget COLOURS are WORKSPACE-scoped, and a workspace switch inside
    // one org never reaches the org guard below — so the re-resolve is keyed
    // on the workspace scope itself, ABOVE that guard. The switch wipe clears
    // `_homeWidgetColors` (state-app.js) and this reads the NEW workspace's
    // row/cache back; without it the board would sit uncoloured until a full
    // reload. Latched on `el.__homeColorScope` (same shape as
    // `el.__loadedOrgId`) so it fires once per scope move, never on the
    // frequent unrelated root updates.
    const colorScope = _wsScopeForBootRead(root)
    if (colorScope !== el.__homeColorScope) {
      el.__homeColorScope = colorScope
      root.update({
        _homeWidgetColors: _resolveBootWidgetColors(root),
        // A palette left open across the switch points at a widget the new
        // workspace need not have.
        _homeColorPicker: null
      })
    }
    const orgId = root?.jwtClaims?.workspace_org_id || (root?.orgs && root.orgs[0]?.id)
    if (!orgId || orgId === el.__loadedOrgId) return
    el.__loadedOrgId = orgId
    const orgs = root?.jwtClaims?.orgs || root?.orgs || []
    const orgSlug = orgs.find((o) => o.id === orgId)?.slug
    if (orgSlug && el.sdk?.getProjects) {
      el.sdk
        .getProjects({ owner: orgSlug, limit: 500 })
        .then((res) => {
          const raw = Array.isArray(res) ? res : res?.data || res?.items || []
          root.update({ _projectsForActiveOrg: raw })
        })
        .catch((err) => console.warn('[home] org-switch getProjects failed:', err && err.message))
    }
    // Received/Given widgets — the org-switch path (loadPrivateData) wipes
    // root.tickets, and _ensureHomeTickets is one-shot, so without this
    // reset the widgets stayed empty until a full reload. Re-arm + re-fetch
    // for the new org.
    root._homeTicketsLoaded = false
    _ensureHomeTickets(el, root, { force: true }).catch((err) =>
      console.warn('[home] org-switch tickets load failed:', err && err.message)
    )
    // Agent roster — `_agentsLoaded`/`_agents` are workspace-scoped since
    // WS-ROOT-STATE-FORM2-WRITE-HOLE-1 (the switch wipe re-arms the shared
    // one-shot gate), and home stays mounted across the switch, so re-run
    // the loader here exactly like the tickets pair above. Idempotent: the
    // gate itself refuses a duplicate while a fetch is in flight.
    root._agentsLoaded = false
    _loadHomeAgentRoster(el, root)
  },

  // Same data prefetching as before: analytics snapshot for the
  // Analytics widget, chat channels for the sidebar, projects for the
  // Projects widget. After data is in flight, schedule Gridstack init
  // for after the DOM settles.
  onCreate: (el, s) => {
    if (el.__homeLoaded) return
    el.__homeLoaded = true
    const root = s.root

    // Resolve the hidden set, the colours and the LAYOUT before the widgets
    // first paint: their size / order / display factories read these keys and
    // every source is synchronously readable (the prefs mirror, the
    // workspace-scoped localStorage, the declared defaults), so the first
    // frame is already the final board. The grid's reconcile keeps them in
    // step as late sources land.
    try {
      root.update({
        _homeHiddenWidgets: _resolveBootHidden(root).hidden,
        _homeWidgetColors: _resolveBootWidgetColors(root),
        _homeLayout: _resolveHomeLayout(root)
      })
    } catch (_) {
      /* the grid's own reconcile resolves them */
    }

    // Bridge the shell PageSlot to the home's page resolver so a slot opened
    // from the home dashboard resolves through `_appPageFor` (workspaceProject
    // .pages + the lazy `_APP_WINDOW_PAGES` map, then the rest of
    // context.pages, so the ?page= URL sync names every route the pane
    // mounts) — this is what keeps the home's divergent routes
    // (`/tickets`→myView, `/tickets/my`,
    // `/workspace-analytics/logs/users`) mapping to the right page even though
    // the shared router registry serves something else (or nothing) for them.
    // Assigned directly (NOT via root.update) so the function survives — smbls
    // state clones update payloads and would drop a function value. PageSlot's
    // `_defFor` calls this before falling back to context.pages; it is absent on
    // every non-home surface, so /studio /infra /tickets are unaffected.
    root._slotResolve = (path) => _appPageFor(path, root, el.context)

    // PageSlot FAMILY registry (2026-08-14, Nika-approved partial-UI scope
    // 2): same-family routes share one slot host — the family's chrome
    // (header + controls, resolved via `chromePath` through the same lazy
    // page channel) persists while only the page body swaps. Family
    // classification mirrors analytics/pages/headerControls.js `_family`
    // exactly: LOGS are workspace telemetry wherever their URL lives; the
    // geo map is projects data wherever its URL lives. Assigned directly
    // (not root.update) — functions don't survive the state clone.
    root._slotFamilyFor = (clean) => {
      const p = shellStripWorkspacePrefix(String(clean || '')).split('?')[0]
      const inAnalytics =
        p === '/analytics' ||
        p.startsWith('/analytics/') ||
        p === '/workspace-analytics' ||
        p.startsWith('/workspace-analytics/') ||
        p === '/logs' ||
        p.startsWith('/logs/')
      if (!inAnalytics) return null
      if (
        p.startsWith('/analytics/__slot-chrome') ||
        p.startsWith('/workspace-analytics/__slot-chrome')
      )
        return null
      const workspaceFam =
        p.includes('/logs') ||
        (p.startsWith('/workspace-analytics') && !p.startsWith('/workspace-analytics/map'))
      return workspaceFam
        ? { id: 'analytics-workspace', chromePath: '/workspace-analytics/__slot-chrome' }
        : { id: 'analytics-projects', chromePath: '/analytics/__slot-chrome' }
    }

    // Restore the home tab title when the shell PageSlot closes: the mounted
    // sub-app's helmet wrote its own title on open, and the slot is keep-alive
    // so nothing re-writes it on close. PageSlot calls this on every close; the
    // currentPath === '/' guard keeps it home-only (a slot closed on /studio etc.
    // leaves that page's title alone). Assigned directly for the same reason as
    // _slotResolve (functions don't survive root.update's clone).
    root._slotOnClose = () => {
      if (typeof document !== 'undefined' && shellStripWorkspacePrefix(root.currentPath) === '/') {
        applyMetadata({ title: HOME_TITLE(el) }, document)
        // Immersive module chrome: these members have no shell chrome and the
        // bare board is not their surface — closing the module page lands
        // them back on the module's landing page instead of stranding them
        // on a chromeless board. Deferred a tick so the slot's own close
        // write settles before the reopen.
        //
        // EXCEPT an admin PREVIEWING a non-admin persona (moduleViewAsActive):
        // their real board exists behind the sheet and the CloseBtn's dismiss
        // is deliberate — this reopen was the SECOND writer that undid it
        // 11ms later (bellf-qa state.update trace, 2026-08-15: X → null →
        // this setTimeout → '/overview'). Same exemption as the CloseBtn.
        // One-shot suppression — moduleGoHome (the rail logo) closes the
        // slot DELIBERATELY to land on home; without this the deferred
        // reopen below undoes that close for immersive members (the same
        // second-writer shape the view-as exemption fixed).
        if (root._slotSkipReopenOnce) {
          root._slotSkipReopenOnce = false
          return
        }
        if (moduleImmersiveActive(root) && !moduleViewAsActive(root)) {
          const landing = moduleImmersiveLanding(root)
          if (landing && _appPageFor(landing, root)) {
            setTimeout(() => {
              if (!root._canvasSlot) root.update({ _canvasSlot: landing })
            }, 0)
          }
        }
      }
    }

    // Restore a sub-app opened before a reload (per Nika): a
    // ?page=<route> in the URL re-opens that PageSlot route so the sheet
    // survives a refresh. The PageSlot keeps the param in sync while open and
    // the close buttons clear it. Resolved via _appPageFor so a workspace-MODULE
    // page (root.workspaceProject.pages) restores too — when the module hasn't
    // injected yet at this point, loadWorkspaceProject's own ?page= recovery
    // re-applies it the moment the pages exist.
    try {
      const bootUrl = new URL(window.location.href)
      // Custom Calendar modal bodies are registered by the lazy Calendar
      // package, after the shell ModalRoot is constructed. Replaying an
      // in-app event snapshot during this cold interval pushes a valid layer
      // descriptor with no resolvable body, producing the empty rounded
      // window reported on refresh. Treat hard refresh like closing that
      // transient modal; the RSVP itself has already been persisted. Plain
      // shared `?event=<id>` links carry no snapshot and retain the existing
      // deep-link loading behavior.
      if (bootUrl.searchParams.has('eventSnapshot')) {
        for (const key of [
          'event',
          'eventSeries',
          'eventFingerprint',
          'eventSnapshot',
          'eventRsvp'
        ]) {
          bootUrl.searchParams.delete(key)
        }
        window.history.replaceState(null, '', bootUrl.toString())
      }
      const pageParam = bootUrl.searchParams.get(APP_WINDOW_URL_PARAM)
      if (pageParam && _appPageFor(pageParam, root)) {
        root.update({ _canvasSlot: pageParam })
      }
    } catch (_) {}

    // All widget data loaders (analytics, tickets, chat channels, projects) route
    // through _refreshHomeData so this cold prefetch and the "Refresh"
    // context-menu action never drift. Cold load self-gates tickets (force:false);
    // the menu forces a refetch.
    _refreshHomeData(el, root)
    // #2312 — mark the org we loaded for so onUpdate only re-fetches projects on an
    // actual org switch (not on every unrelated root update).
    el.__loadedOrgId = root?.jwtClaims?.workspace_org_id || (root?.orgs && root.orgs[0]?.id)

    // Expose the board's operations to the right-click ContextDropdown
    // (functions/homeContextMenu.js → homeContextAction) and to the widget
    // frame (components/HomeWidgetFrame.js, functions/homeWidgetDrag.js) through
    // root._homeOps — the loaders and the layout live on this page; the
    // registered functions are thin dispatchers that delegate back here.
    // Every change of the board's arrangement plays through ONE motion
    // (functions/homeWidgetMotion.js, DESIGN-WL-HOME-WIDTH-RESIZE-1): the
    // cards read before, the change, then each moved card glides from where
    // it was (FLIP). No motion under reduced motion / a hidden tab — the
    // change simply lands.
    const withMotion = (change, opts) =>
      el.call('homeWidgetFlip', el.MainColumn && el.MainColumn.GridContainer, change, opts)
    root._homeOps = {
      withMotion,
      refresh: () => _refreshHomeData(el, root, { force: true }),
      // The hide is a decision: it persists at once; the card then leaves
      // (fade + a short fall) and its neighbours close the gap.
      hideWidget: (id) => _hideWidgetWithMotion(el, root, id),
      // THE SETUP WIZARD's apps step (functions/onboardingWizard.js): the apps
      // it offers, and its picks written in one pass.
      onboardingApps: () => _onboardingAppRows(root),
      setAppsShown: (picks) => _setAppsShown(root, picks, el.sdk),
      // "Add color" ▸ a swatch (or "Default") in HomeWidgetColorPicker.
      // `family` falsy = clear back to the neutral solid.
      setWidgetColor: (id, family) => _setWidgetColor(id, family, root, el.sdk),
      // AI-CREATED plane manage actions (tickets/opus.md "Per-workspace
      // AI-CREATED widgets" additive remainder) — share/unshare/delete.
      setAiWidgetVisibility: (widget, visibility) =>
        _setAiWidgetVisibilityWithNotice(root, el.sdk, widget, visibility),
      deleteAiWidget: (widget) => _deleteAiWidgetWithNotice(root, el.sdk, widget),
      // "Change with AI" (HOME-WIDGET-CHANGE-WITH-AI-1): the card the assistant
      // made (functions/aiWorkspaceOps.js aiCreateWidget `replaces`) takes the
      // place of the card it replaces — the old card's slot and size — and the
      // old card is hidden, restorable from Customize like any hide. False
      // when the old card is not on this board.
      // The old card may be named by its app-qualified ref; homeLayoutCardId
      // resolves it to the id this board lists (else nothing is replaced).
      replaceWidget: (oldRef, newId) => {
        const layout = _currentHomeLayout(root)
        const oldId = homeLayoutCardId(layout, oldRef)
        if (!oldId) return false
        const next = homeLayoutReplace(layout, oldId, newId)
        if (next === layout) return false
        withMotion(() => _setHomeLayout(root, next, undefined, el.sdk))
        _hideWidgetWithMotion(el, root, oldId)
        return true
      },
      // Layout v2 — drag-to-reorder, the keyboard path and the size picker;
      // `setLayout` restores a cancelled drag without persisting.
      // The card under the pointer follows the pointer — only its
      // neighbours glide while it is dragged.
      moveWidget: (id, targetId) =>
        withMotion(() => _moveHomeWidget(root, id, targetId), { exclude: [id] }),
      moveBy: (id, delta) => withMotion(() => _moveHomeWidgetBy(root, id, delta, el.sdk)),
      setLayout: (layout, opts) => withMotion(() => _setHomeLayout(root, layout, opts, el.sdk)),
      persistLayout: () => _persistHomeLayout(root, el.sdk),
      // A resize (the corner handle, Shift + arrow, the picker): the card
      // glides to its new cell and is revealed to its new size, and the page
      // brings it into view when it landed outside it.
      setWidgetSize: (id, size) =>
        withMotion(() => _setHomeWidgetSize(root, id, size, el.sdk), {
          resized: id,
          // after the flush and the glide: the card's FINAL box
          after: () =>
            el.call(
              'homeWidgetReveal',
              el.MainColumn,
              el.MainColumn && el.MainColumn.GridContainer,
              id
            )
        })
    }

    // The board's grid stylesheet (container queries from the constants table).
    _injectWidgetStyles(el)
  },

  // HomeSidebar removed from the main view — channels / DMs /
  // agents / events now surface from the AppHeader's "Chat"
  // dropdown instead (see shared/components/AppHeader.js).

  // Main column — the dashboard grid + scroll container. AppPage's
  // overflow:hidden + the grandparent's min-h-100dvh together prevent
  // a true browser-window scroll. So MainColumn keeps its own scroll
  // (overflowY:auto + height:100%), and the DOT GRID lives here on
  // the scroll container so dots move with content as the user
  // scrolls — visually the "dots follow scroll" effect. Opacity
  // bumped to 0.12 (from 0.07) for visibility against the page bg.
  // The home board opens straight on its widgets and shows no title, so this
  // is its one `h1`: announced, never seen (design-direction.md §3).
  PageSrTitle: { text: '{{ shell.app.home | polyglot }}' },

  // The App strip — the Rail (DESIGN-WL-HOME-TOP-STRIP-SUMMARY-1, Nika's pick
  // row 1 C, a personal option): BESIDE the board, in the page's own row, so
  // the board narrows to the room that is left (its columns follow its own
  // width) and nothing overlays a card. The same rows as the Dock.
  HomeAppRail: {
    subscribeTo: ['workspaceProject', 'workspaceApps'],
    // The dashboard's, like the Dock: it steps aside while another tab holds
    // the home (functions/homeView.js).
    hide: (el, s) => _appStripHidden(el, s.root, 'rail') || _tabHoldsHome(el, s.root),
    RailCapsule: {
      Items: { children: (el, s) => _appStripChildren(s.root) }
    }
  },

  MainColumn: {
    // The board's container-query root (see _injectWidgetStyles).
    attr: { 'data-home-board': 'true' },
    // `data-customizing` (a flat hook, like AppDialog's dataPresentation) —
    // 'open' while edit mode shows its panel, 'tucked' while the panel is
    // tucked: on a phone the panel is a bottom sheet over the board's lower
    // half, and workspace/app.js gives the board that much room below its last
    // card, so every card can scroll above the sheet. Both keys are read on
    // every run, so both stay tracked.
    dataCustomizing: (el, s) => {
      const on = !!s.root._homeCustomizeMode
      const tucked = !!s.root._homeCustomizeDrawerTucked
      return on ? (tucked ? 'tucked' : 'open') : null
    },
    flow: 'y',
    gap: 'B',
    flex: '1',
    minWidth: '0',
    height: '100%',
    overflowY: 'auto',
    // Top inset D1 (2026-06-08 per Nika "D1 offset from top and
    // bottom") so the first widget sits D1 below the floating header —
    // matching the GridContainer's existing `marginBottom: 'D1'` so the
    // dashboard is symmetrically inset top↔bottom.
    // The bottom track is scroll CLEARANCE for the ~280px BottomPrompt fade
    // zone, not a bottom offset — without it the last widgets sit permanently
    // behind the gradient. The visible bottom breathing room is the
    // GridContainer's D1 marginBottom.
    //
    // ⚠️ D1 STATIC, not G. The clearance is only owed when the composer is
    // actually on screen, and app.js already supplies exactly that:
    //   html[data-shell-route="home"][data-prompt="shown"] … MainColumn
    //     { padding-bottom: max(var(--spacing-G), var(--slot-bottom)) }
    // So the composer case is unchanged. What changes is the case where there
    // is no composer to clear: this used to reserve ~324px of dead space
    // regardless, which showed up as a large empty area under a short board
    // AND pushed the page into a scrollbar it did not need (Nika: "dont give
    // vertical scroll and big empty area if not needed"). Immersive members —
    // every non-admin on a takeover module like bellforge — never see the
    // composer at all (_composerVisible in app.js), so they were paying the
    // full reservation for something that is never rendered for them.
    padding: 'D1 C D1 C',
    maxWidth: '100%',
    boxSizing: 'border-box',
    '@mobileL': {
      gap: 'B',
      // Side gutters + scroll clearance shrink on a phone, the TOP inset does
      // NOT: the AppHeader doesn't shrink there, it GROWS (the mobile nav pill
      // measures ~63px against the desktop header's 48px). The old `B` top
      // (~26px) parked the first widget row ~37px UNDER the navbar, so the
      // greeting card's avatar was sliced off on every phone load with no white
      // space anywhere between the chrome and the board (2026-08-02 per Nika
      // "top part gets cut" + "add reasonable top padding"). D1 — the same
      // token the desktop inset uses — clears the phone header with ~21px of
      // open space, and the mobileL rule in _injectWidgetStyles tops it up by
      // env(safe-area-inset-top) for notched devices.
      padding: 'D1 A F A',
      overflowX: 'clip'
    },
    style: {
      // Board surface = the ONE ground chain every workspace surface paints
      // (canvasBackground.js STAGE_BACKGROUND, BF-WIDGET-INK-1): the active
      // module's `--stage-bg` override, else the design-system `stage`
      // token (light `milk`, dark `gray5`). The shell root, the `main` page
      // above and every canvas Stage read the same chain, so the board can
      // never differ from the root beside it.
      // NO `--home-bg` above the chain any more (2026-08-16, measured by ai
      // on ws_UYAOhQyNjm: the record's `--home-bg: light-dark(milk,
      // transparent)` outranked the module stage → #F2EDE3 strip beside a
      // #D8D8D8 board in light — the exact seam Nika reported, re-created
      // one layer up). A per-workspace ground has ONE channel, `--stage-bg`
      // (chrome.stage or a record var); a second board-only channel is the
      // four-painter defect again. `--canvas-bg` is different: it is the
      // USER's own Zoombar pick, per theme, canvas-only. History: was
      // `transparent`, then `var(--home-bg, var(--color-stage))`, then
      // `var(--home-bg, var(--stage-bg, …))`.
      // Longhand, NOT the `background` shorthand: the shorthand resets
      // background-image/size/attachment and would wipe the dot grid declared
      // right below it.
      backgroundColor: 'var(--stage-bg, var(--color-stage))',
      // Dot grid — kept as a mechanism (`--home-dot-color` lets a workspace
      // tint it; GreenIG greens the dots over its green canvas) but the
      // house default is OFF (transparent), per the same Codex record: the
      // milk stage reads as one flat plane, dots were noise on it.
      backgroundImage:
        'radial-gradient(circle, var(--home-dot-color, transparent) 1px, transparent 1px)',
      backgroundSize: '24px 24px',
      // `local` (vs. default `scroll`) attaches the bg to the
      // element's content, so as MainColumn scrolls the dot grid
      // moves WITH the widgets rather than staying pinned to the
      // visible viewport — the "dots follow scroll" effect.
      backgroundAttachment: 'local'
    },

    // Touch: arm the shared long-press → synthetic-contextmenu shim (see
    // shared/functions/longPress.js — built for exactly this, previously
    // unwired). One arm HERE covers the whole dashboard by bubbling: a 500ms
    // stationary press on a widget fires that widget's own onContextmenu
    // (which stopPropagation()s, exactly like a real right-click), and a press
    // on empty space falls through to the handler below. No-op on fine
    // pointers — the shim gates on isCoarsePointer internally.
    onRender: (el) => el.call('armLongPress'),
    // The top sky's fallback (no CSS scroll-driven animations): it fades out
    // once the home leaves its top. A no-op where the CSS path runs
    // (functions/ambientBackdrop.js ambientSkyScrolled).
    onScroll: (e, el) => el.call('ambientSkyScrolled', el),

    // Right-click empty dashboard space → the house ContextDropdown with
    // [Refresh, Customize dashboard] (functions/homeContextMenu.js →
    // homeContextAction). Widgets stopPropagation in their own onContextmenu
    // so they show the widget menu instead; text fields fall through to the
    // native menu. (2026-06-29 per Nika "context menu on empty".)
    //
    // Label normalized to "Customize dashboard" (tickets/sonnet.md
    // ADMIN-MICRO-1 #4) — this used to read bare "Customize" while the
    // per-widget context menu (below, ~L1264/L1811) and the drawer heading
    // (~L7746) both say "Customize dashboard"; same action, same
    // destination, now the same string everywhere it appears.
    // THE KEYBOARD WAY into the same menus (persona audit WH-9): Shift+F10 or
    // the context-menu key on a card — or on any control inside one — sends
    // the board the `contextmenu` a right-click sends, at that element
    // (functions/homeCtxTip.js homeKeyboardContextMenu). A text field keeps
    // its own menu.
    onKeydown: (e, el) => el.call('homeKeyboardContextMenu', e),

    onContextmenu: (e, el, s) => {
      // Scope gate (D2 §5, P1) — the board does not answer while a PageSlot
      // page is open over it. The slot frame is inset and its backdrop is
      // pointerEvents:none, so the pixels around an open pane fall through to
      // this handler and produced a HOME menu over a module page.
      // See functions/homeContextMenu.js homeContextMenuAllowed.
      if (!el.call('homeContextMenuAllowed', e, s.root)) return
      e.preventDefault()
      s.root.update(
        {
          contextDropdown: {
            open: true,
            x: e.clientX,
            y: e.clientY,
            items: [
              { label: 'common.action.refresh', action: 'refresh' },
              { label: 'workspace.menu.customize', action: 'customize' }
            ],
            actionFn: 'homeContextAction'
          }
        },
        { preventFetch: true }
      )
      // INTERACT-1: move focus into the menu so Esc/arrow-keys actually
      // reach it — see functions/homeContextMenu.js:focusHomeContextMenu.
      el.call('focusHomeContextMenu')
    },

    // Ask AI composer moved to the BOTTOM — see BottomPrompt below.
    // Matches the canvas `CanvasPromptTextarea` pattern in
    // packages/canvas/components/CanvasPromptTextarea.js, which sits
    // bottom-center of the studio surface and stays in view as the
    // canvas pans.

    // THE HOME'S TWO VIEWS — Dashboard · Fly, the column's first row
    // (components/HomeViewSwitch.js, functions/homeView.js; Nika 2026-10-22
    // "add switcher at the top - dashboard/fly"). It stays in both views.
    HomeViewSwitch: {},

    // The page head (HOME_WIDGETS_SPEC.md §4.1) — not a grid cell: the date
    // as a micro-label, one editorial line, then THE HOME TOP
    // (DESIGN-WL-HOME-TOP-STRIP-SUMMARY-1): the sub-line of what needs the
    // person — the top two signals of HOME_SIGNALS_CONTRACT v1 — and the
    // summary in the person's own style (Brief | Signals,
    // components/HomeTopSummary.js). Aligned to the grid's tracks by the board
    // stylesheet (.home-greeting). The `greeting` hide pref still hides it.
    //
    // Its own clock (`state.now`, functions/homeSignals.js homeTopClock): the
    // greeting's hour band and the date follow the wall clock, and the signals
    // refresh. A source of a built-in signal changing — the tasks, the events,
    // the mail count, the installed apps, the workspace — marks that signal
    // stale and runs a pass at once (homeSignalsOnData).
    HomeGreeting: {
      tag: 'header',
      class: 'home-greeting',
      state: { now: null },
      // The `greeting` hide pref, and THE AI HOLDS THE MAIN COLUMN — the main
      // thread or a generated board over the grid area (homeTopYields,
      // functions/homeSummaryCompose.js): the head steps aside while it shows
      // and returns on Esc / Back to home with its content intact — hidden,
      // never unmounted (no mount-site `if:`), so nothing re-renders, nothing
      // flashes and only this block leaves the layout. Both read up front.
      hide: (el, s) => {
        const off = !!s.root?._homeHiddenWidgets?.greeting
        const yields = !!el.call('homeTopYields', s.root)
        return off || yields
      },
      display: 'flex',
      flexDirection: 'column',
      gap: 'Z',
      minWidth: '0',
      flexShrink: '0',
      boxSizing: 'border-box',
      padding: 'B',
      round: 'radiusCard',
      background: 'var(--home-card-bg, var(--theme-card-background))',
      '@mobileL': { padding: 'A' },
      onRender: (el) => el.call('homeTopClock'),
      onRemove: (el) => el.call('homeTopClockStop'),
      stateDeps: [
        (el, s) => s.root._userTickets,
        (el, s) => s.root.intranetEvents,
        (el, s) => (s.root.navUnreadCounts || {}).mail,
        (el, s) => s.root.workspaceApps,
        (el, s) => s.root.workspaceProject,
        (el, s) => (s.root.workspace && (s.root.workspace.id || s.root.workspace._id)) || null,
        // the inbox signal's sources: the unread count and the first answer
        (el, s) => (s.root.notifs || []).filter((n) => n && !n.read).length,
        (el, s) => !!s.root.notifsLoaded
      ],
      onStateUpdate: (el, s, ctx, change) => el.call('homeSignalsOnData', el, s, change),

      DateLabel: {
        extends: 'AppMicroLabel',
        // `subtitle`, not the micro-label's `inactive` nor `caption`: the
        // taller top band (2026-10-02) puts this line on the SOLID cloud, where
        // the near + far layers overlap at full strength. Measured live:
        // `caption` 4.32:1 dark there (4.80:1 over one layer), `inactive`
        // 2.64:1; `subtitle` 5.16:1 dark / >7:1 light
        // (DESIGN-WL-HOME-SKY-TOP-GROUND-1).
        color: 'subtitle',
        text: (el, s) =>
          el.call('localizedDate', s.now || Date.now(), {
            weekday: 'long',
            day: 'numeric',
            month: 'long'
          })
      },

      // A line, not a heading: the page's one h1 is PageSrTitle ("Home"),
      // which gives way to the pane bar when the board opens in a pane.
      // NEVER `Line`: a bare PascalCase key extends the registered component
      // of that name, and the brand's `Line` is the 1 px hairline divider —
      // the greeting collapsed to 1 px and the summary painted over it (clip
      // audit round 7, __tests__/homeGreetingOneLine.test.js).
      GreetingLine: {
        tag: 'p',
        margin: '0',
        fontSize: 'C',
        fontWeight: '400',
        lineHeight: '1.25',
        color: 'title',
        overflowWrap: 'anywhere',
        Lead: {
          tag: 'span',
          text: (el, s) =>
            el.call(
              'polyglot',
              _greetLeadKey(
                new Date(s.now || Date.now()).getHours(),
                !!_shellFirstName(shellFindMe(s).name)
              )
            )
        },
        Name: {
          tag: 'span',
          fontWeight: '700',
          text: (el, s) => _shellFirstName(shellFindMe(s).name)
        }
      },

      // The sub-line holds a skeleton line until the providers have answered —
      // a first-paint "nothing needs you" would be a false claim.
      HomeSignalsLine: {},
      HomeTopSummary: {}
    },

    // The App strip — the Dock (HOME_WIDGETS_SPEC.md §1, decision H1 A; the
    // person's style, DESIGN-WL-HOME-TOP-STRIP-SUMMARY-1): the app shortcuts
    // in ONE glass capsule between the page head and the grid — never grid
    // items. In the Rail style the page's HomeAppRail stands beside the board
    // instead. Its items are the page's (_appStripChildren): layout order,
    // hides kept; it re-reads them when the hidden map or the order changes.
    HomeAppStrip: {
      subscribeTo: ['workspaceProject', 'workspaceApps'],
      // …and it is the DASHBOARD's: it steps aside while another tab holds
      // the home (functions/homeView.js).
      hide: (el, s) => _appStripHidden(el, s.root, 'dock') || _tabHoldsHome(el, s.root),
      DockCapsule: {
        Items: { children: (el, s) => _appStripChildren(s.root) }
      }
    },

    // THE BOARDS ROW (ASSISTANT_BOARD_MODE_SPEC §4, decision 24;
    // DESIGN-WL-ASSISTANT-MAIN-HOME-1): its own row directly above the grid,
    // on the grid's width — Home · the generated board · the saved boards, the
    // kit's view switch (it never cuts a segment: the track scrolls) — and
    // Edit on the right, the board's edit mode (it switches the grid, so it
    // sits on the grid). Shown only while there is more than Home. The rows,
    // the current segment and the switch are the assistant's
    // (functions/assistantBoard.js).
    AssistantBoardsRow: {
      class: 'home-boards-row',
      // `?board=<id>` mirrors the current segment; a cold link opens its
      // board once Page mode answers (functions/homeBoardUrl.js).
      subscribeTo: ['_aiBoard', '_aiBoards', '_aiBoardsAvailable', 'assistantDock'],
      onRender: (el, s) => el.call('homeBoardUrlTick', s.root),
      onUpdate: (el, s) => el.call('homeBoardUrlTick', s.root),
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      gap: 'Z',
      minWidth: '0',
      flexShrink: '0',
      // The dashboard's boards — never under another tab (functions/homeView.js).
      hide: (el, s) =>
        !el.call('assistantBoardSegments', s.root).length || _tabHoldsHome(el, s.root),
      AppSegmented: {
        variant: 'view',
        minWidth: '0',
        ariaLabel: (el) => el.call('assistantBoardRowLabel'),
        current: (el, s) => el.call('assistantBoardCurrentSegment', s.root),
        select: 'assistantBoardSelect',
        childrenAs: 'state',
        children: (el, s) => el.call('assistantBoardSegments', s.root)
      },
      // Edit edits the board on screen — the home board; a saved board's own
      // edit mode arrives with its record's ops (core ai.boards), so Edit
      // steps aside while a generated or saved board is shown.
      AppCapsuleBtnToolbar: {
        compact: true,
        flexShrink: '0',
        hide: (el, s) => el.call('assistantBoardOpen', s.root),
        ariaPressed: (el, s) => String(!!s.root._homeCustomizeMode),
        text: '{{ common.action.edit | polyglot }}',
        onClick: (e, el, s) =>
          s.root.update({ _homeCustomizeMode: !s.root._homeCustomizeMode }, { preventFetch: true })
      }
    },

    // The widget grid (HOME_WIDGETS_SPEC.md §1): a CSS grid flowing the saved
    // ORDER of widgets, each at one of four sizes; its geometry lives in the
    // board stylesheet (_injectWidgetStyles). Module widgets render through
    // the ModuleWidgets host; saved cells of widgets still loading hold a
    // skeleton (PendingWidgets). The assistant's generated board mounts as the
    // grid's last child, OVER the cards (AssistantBoardView below).
    GridContainer: {
      class: 'home-grid',
      // Which of the home's tabs holds the area (functions/homeView.js): while
      // it is not the Dashboard, the board stylesheet takes the cards out of
      // the layout and puts the tab's view part in the flow
      // (_injectWidgetStyles).
      dataHomeView: (el, s) => el.call('homeViewOf', s.root),
      // the containing block of the generated board laid over the grid area
      position: 'relative',
      subscribeTo: [
        'workspace',
        'workspaceSettings',
        'workspaceProject',
        'workspaceApps',
        'userDashboardPrefs'
      ],
      width: '100%',
      minWidth: '0',
      flexShrink: '0',
      marginBottom: 'D1',
      '@mobileL': { marginBottom: 'C' },

      // One reconcile BEFORE the widgets render (they read the layout and the
      // hidden map), then on every subscribed update.
      onInit: (el, s) => {
        _injectWidgetStyles(el)
        _reconcileHomeBoard(el, s.root)
      },
      // …and the top sky re-measures its clear edge after each: the reconcile
      // settles the hidden map, which shows or hides the head rows above the
      // grid (the greeting, the App strip), so the first row with a box moves
      // (functions/ambientBackdrop.js ambientFadeTop — the grid is the last
      // anchor; only a changed edge writes the sky's state).
      onRender: (el, s) => {
        _reconcileHomeBoard(el, s.root)
        el.call('ambientSkySync', el)
      },
      onUpdate: (el, s) => {
        _reconcileHomeBoard(el, s.root)
        el.call('ambientSkySync', el)
      },

      // A widget dragged in from the customize drawer (`move`) or the app
      // launcher (`copy`) — accepted anywhere on the board.
      onDragover: (e, el) => {
        const types = e.dataTransfer && e.dataTransfer.types
        if (!types || !Array.from(types).includes('text/x-home-widget-id')) return
        e.preventDefault()
        e.dataTransfer.dropEffect = el.call('isLauncherDashboardDrag', types) ? 'copy' : 'move'
      },
      // The drop lands the widget where the pointer is in the ORDER (on top of
      // a widget = that widget's place), else at the end. A launcher drop of an
      // app already on the board only says so.
      onDrop: (e, el, s) => {
        const types = e.dataTransfer && e.dataTransfer.types
        const id = e.dataTransfer && e.dataTransfer.getData('text/x-home-widget-id')
        if (!id) return
        e.preventDefault()
        const fromLauncher = el.call('isLauncherDashboardDrag', types)
        if (fromLauncher && _isHomeWidgetAlreadyVisible(id)) {
          try {
            el.call('closeDropdown', 'searchApps')
          } catch (_) {
            /* the drag can also start from a detached launcher layer */
          }
          _notifyHomeWidgetDrop(el, s.root, id, true)
          return
        }
        const at = _dropIndexAt(el, s.root, e.clientX, e.clientY)
        _restoreWidget(id, s.root, at == null ? null : { index: at }, el.sdk, true)
        if (fromLauncher) {
          try {
            el.call('closeDropdown', 'searchApps')
          } catch (_) {
            /* Home can receive a synthetic/test drag without the launcher */
          }
          _notifyHomeWidgetDrop(el, s.root, id, false)
        }
      },

      // Next up — the day's next event and the ones after it
      // (HOME_AMBIENT_SKIN_SPEC.md §4, components/HomeNextUpBody.js). First
      // on a fresh board; M, never smaller.
      NextUpWidget: _widget(
        {
          id: 'next-up',
          x: 0,
          y: 0,
          w: 6,
          h: 3,
          size: 'm',
          minSize: 'm',
          title: 'workspace.widget.nextUp.title',
          icon: 'date outline',
          openRoute: '/calendar'
        },
        { HomeNextUpBody: {} }
      ),
      ReceivedWidget: _widget(
        {
          id: 'received',
          x: 0,
          y: 4,
          w: 6,
          h: 4,
          title: 'workspace.widget.received.title',
          icon: 'check',
          openRoute: '/tasks/my'
        },
        {
          flow: 'y',
          minHeight: '0',

          // ── Mini tier (3-5 cols): numeral + one line ──────────────────
          Mini: {
            class: 'widget-tier-mini',
            flow: 'y',
            gap: 'X',
            align: 'flex-start flex-end',
            padding: 'Z',
            Count: {
              tag: 'span',
              fontSize: 'G',
              fontWeight: '300',
              lineHeight: '0.9',
              text: (el, s) =>
                el.call('homeTicketCountText', s.root, _ticketCount(_receivedTickets(s.root))),
              title: (el, s) =>
                el.call('homeTicketListState', s.root, 0) === 'error'
                  ? el.call('polyglot', 'workspace.widget.received.subtitleError')
                  : ''
            },
            CountLabel: {
              extends: 'HomePosterCaption',
              text: '{{ workspace.caption.openTasks | polyglot }}'
            }
          },

          // ── Full tier (6+ cols): the real mini-app — rows + quick-add ─
          Full: {
            class: 'widget-tier-full',
            flow: 'y',
            gap: 'V',
            padding: 'Z',
            minHeight: '0',

            QuickAdd: _taskQuickAddRow('received', 'received'),

            // The skeleton stands in while the set is unread — a mounted /
            // unmounted shared `SkeletonRow`, never an animation a returned
            // `null` cannot clear (the parent's measured trap).
            LoadingRow: {
              extends: 'SkeletonRow',
              // Row-height placeholder, so the skeleton occupies the space a
              // ticket row will — the default `SkeletonRow` height is a text
              // line (measured 2px here) and read as a hairline, not a row.
              height: 'B',
              hide: (el, s) =>
                el.call('homeTicketListState', s.root, _ticketCount(_receivedTickets(s.root))) !==
                'loading'
            },
            Empty: {
              hide: (el, s) => {
                const st = el.call(
                  'homeTicketListState',
                  s.root,
                  _ticketCount(_receivedTickets(s.root))
                )
                return st !== 'empty' && st !== 'error'
              },
              extends: 'HomePosterSoftInk',
              padding: 'A B',
              fontSize: 'A',
              text: (el, s) =>
                el.call(
                  'polyglot',
                  el.call('homeTicketListState', s.root, 0) === 'error'
                    ? 'workspace.widget.received.bodyError'
                    : 'workspace.widget.received.bodyEmpty'
                )
            },

            List: {
              hide: (el, s) =>
                el.call('homeTicketListState', s.root, _ticketCount(_receivedTickets(s.root))) !==
                'list',
              flow: 'y',
              gap: 'V',
              overflow: 'auto',
              minHeight: '0',
              children: (el, s) =>
                _receivedTickets(s.root)
                  .slice(0, 5)
                  .map((t) =>
                    _ticketRowChild(
                      t,
                      '/tasks/my',
                      t.reporterEmail || t.reporter_email,
                      _cardHue(el, s, 'received')
                    )
                  )
            }
          }
        }
      ),
      GivenWidget: _widget(
        {
          id: 'given',
          x: 6,
          y: 4,
          w: 6,
          h: 4,
          title: 'workspace.widget.given.title',
          icon: 'check',
          openRoute: '/tasks'
        },
        {
          flow: 'y',
          minHeight: '0',

          // ── Mini tier (3-5 cols): numeral + one line ──────────────────
          Mini: {
            class: 'widget-tier-mini',
            flow: 'y',
            gap: 'X',
            align: 'flex-start flex-end',
            padding: 'Z',
            Count: {
              tag: 'span',
              fontSize: 'G',
              fontWeight: '300',
              lineHeight: '0.9',
              text: (el, s) =>
                el.call('homeTicketCountText', s.root, _ticketCount(_givenTickets(s.root))),
              title: (el, s) =>
                el.call('homeTicketListState', s.root, 0) === 'error'
                  ? el.call('polyglot', 'workspace.widget.given.subtitleError')
                  : ''
            },
            CountLabel: {
              extends: 'HomePosterCaption',
              text: '{{ workspace.caption.delegated | polyglot }}'
            }
          },

          // ── Full tier (6+ cols): the real mini-app — rows + quick-add ─
          Full: {
            class: 'widget-tier-full',
            flow: 'y',
            gap: 'V',
            padding: 'Z',
            minHeight: '0',

            QuickAdd: _taskQuickAddRow('given', 'given'),

            LoadingRow: {
              extends: 'SkeletonRow',
              height: 'B',
              hide: (el, s) =>
                el.call('homeTicketListState', s.root, _ticketCount(_givenTickets(s.root))) !==
                'loading'
            },
            Empty: {
              hide: (el, s) => {
                const st = el.call(
                  'homeTicketListState',
                  s.root,
                  _ticketCount(_givenTickets(s.root))
                )
                return st !== 'empty' && st !== 'error'
              },
              extends: 'HomePosterSoftInk',
              padding: 'A B',
              fontSize: 'A',
              text: (el, s) =>
                el.call(
                  'polyglot',
                  el.call('homeTicketListState', s.root, 0) === 'error'
                    ? 'workspace.widget.given.bodyError'
                    : 'workspace.widget.given.bodyEmpty'
                )
            },

            List: {
              hide: (el, s) =>
                el.call('homeTicketListState', s.root, _ticketCount(_givenTickets(s.root))) !==
                'list',
              flow: 'y',
              gap: 'V',
              overflow: 'auto',
              minHeight: '0',
              children: (el, s) =>
                _givenTickets(s.root)
                  .slice(0, 5)
                  .map((t) =>
                    _ticketRowChild(
                      t,
                      '/tasks',
                      t.assigneeEmail || t.assignee_email,
                      _cardHue(el, s, 'given')
                    )
                  )
            }
          }
        }
      ),
      TeamPulseWidget: _widget(
        {
          id: 'teampulse',
          x: 0,
          y: 8,
          w: 6,
          h: 3,
          title: 'workspace.widget.teampulse.title',
          icon: 'analytics',
          openRoute: '/tasks',
          hue: 'analytics'
        },
        {
          flow: 'y',
          minHeight: '0',

          // ── Mini tier (3-5 cols): total + a slim bar, no legend ───────
          Mini: {
            class: 'widget-tier-mini',
            flow: 'y',
            gap: 'X',
            align: 'flex-start flex-end',
            padding: 'Z',
            Count: {
              tag: 'span',
              fontSize: 'G',
              fontWeight: '300',
              lineHeight: '0.9',
              text: (el, s) => {
                const counts = _workspacePulse(s.root)
                return String(_PULSE_BUCKETS.reduce((sum, b) => sum + counts[b.key], 0))
              }
            },
            CountLabel: {
              extends: 'HomePosterCaption',
              text: '{{ workspace.caption.openTickets | polyglot }}'
            }
          },

          // ── Full tier (6+ cols): bar + per-bucket counts + legend ─────
          Full: {
            class: 'widget-tier-full',
            flow: 'y',
            gap: 'Y',
            padding: 'Z',
            minHeight: '0',

            // Proportional bar — 24 fixed segments (board-status.py's own
            // largest-remainder allocation), so a bucket with even ONE
            // ticket never rounds away to nothing.
            Bar: {
              flow: 'x',
              height: 'Y',
              round: 'radiusControl',
              overflow: 'hidden',
              background: 'line',
              children: (el, s) => {
                const counts = _workspacePulse(s.root)
                return _pulseBarSegments(counts, 24).map((seg) => ({
                  key: seg.key,
                  flex: String(seg.units),
                  background: seg.hue,
                  minWidth: '0'
                }))
              }
            },

            // Per-bucket counts — fixed-width cells (this ticket's own
            // zero-shift rule: a count crossing a digit boundary must not
            // reflow its neighbours).
            Counts: {
              flow: 'x',
              flexWrap: 'wrap',
              gap: 'Y',
              childrenAs: 'state',
              children: (el, s) => {
                const counts = _workspacePulse(s.root)
                return _PULSE_BUCKETS.map((b) => ({ key: b.key, ...b, n: counts[b.key] }))
              },
              childExtends: {
                flow: 'x',
                gap: 'V',
                align: 'center',
                fontSize: 'Z1',
                color: 'caption',
                Dot: {
                  tag: 'span',
                  boxSize: 'X1',
                  round: '50%',
                  flexShrink: '0',
                  background: (el, s) => s.hue
                },
                N: {
                  tag: 'span',
                  display: 'inline-block',
                  minWidth: 'C',
                  fontVariantNumeric: 'tabular-nums',
                  fontWeight: '700',
                  color: 'title',
                  text: (el, s) => String(s.n)
                },
                Label: {
                  tag: 'span',
                  // `.replace` is a no-op on the rows whose value carries no
                  // placeholder (the pulse / priority buckets and the person
                  // rows); only the workload "N more" row substitutes.
                  text: (el, s) =>
                    el.call('polyglot', s.label).replace('{count}', s.moreCount ?? '')
                }
              }
            },

            // Legend — who can act, not just what state things are in.
            // "Assigned to you" (Received's own count, reused rather than
            // re-derived) is the one line that turns a summary into a call
            // to action.
            Legend: {
              tag: 'div',
              hide: (el, s) => _receivedTickets(s.root).length === 0,
              fontSize: 'Z1',
              color: 'caption',
              paddingTop: 'V',
              text: (el, s) => {
                const n = _receivedTickets(s.root).length
                return el
                  .call(
                    'polyglot',
                    n === 1 ? 'workspace.pulse.needsYouOne' : 'workspace.pulse.needsYou'
                  )
                  .replace('{count}', n)
              }
            }
          }
        }
      ),
      PriorityMixWidget: _widget(
        {
          id: 'prioritymix',
          x: 6,
          y: 8,
          w: 6,
          h: 3,
          title: 'workspace.widget.prioritymix.title',
          icon: 'analytics',
          openRoute: '/tasks',
          hue: 'tickets'
        },
        {
          flow: 'y',
          minHeight: '0',

          // ── Mini tier (3-5 cols): the urgent count alone ──────────────
          Mini: {
            class: 'widget-tier-mini',
            flow: 'y',
            gap: 'X',
            align: 'flex-start flex-end',
            padding: 'Z',
            Count: {
              tag: 'span',
              fontSize: 'G',
              fontWeight: '300',
              lineHeight: '0.9',
              text: (el, s) => {
                const counts = _workspacePriorityMix(s.root)
                return String(counts.urgent + counts.high)
              }
            },
            CountLabel: {
              extends: 'HomePosterCaption',
              text: '{{ workspace.caption.urgentOrHigh | polyglot }}'
            }
          },

          // ── Full tier (6+ cols): bar + per-bucket counts + legend ─────
          Full: {
            class: 'widget-tier-full',
            flow: 'y',
            gap: 'Y',
            padding: 'Z',
            minHeight: '0',

            Bar: {
              flow: 'x',
              height: 'Y',
              round: 'radiusControl',
              overflow: 'hidden',
              background: 'line',
              children: (el, s) =>
                _barSegments(_PRIORITY_BUCKETS, _workspacePriorityMix(s.root), 24).map((seg) => ({
                  key: seg.key,
                  flex: String(seg.units),
                  background: seg.hue,
                  minWidth: '0'
                }))
            },

            Counts: {
              flow: 'x',
              flexWrap: 'wrap',
              gap: 'Y',
              childrenAs: 'state',
              children: (el, s) => {
                const counts = _workspacePriorityMix(s.root)
                return _PRIORITY_BUCKETS.map((b) => ({ key: b.key, ...b, n: counts[b.key] }))
              },
              childExtends: {
                flow: 'x',
                gap: 'V',
                align: 'center',
                fontSize: 'Z1',
                color: 'caption',
                Dot: {
                  tag: 'span',
                  boxSize: 'X1',
                  round: '50%',
                  flexShrink: '0',
                  background: (el, s) => s.hue
                },
                N: {
                  tag: 'span',
                  display: 'inline-block',
                  minWidth: 'C',
                  fontVariantNumeric: 'tabular-nums',
                  fontWeight: '700',
                  color: 'title',
                  text: (el, s) => String(s.n)
                },
                Label: {
                  tag: 'span',
                  // `.replace` is a no-op on the rows whose value carries no
                  // placeholder (the pulse / priority buckets and the person
                  // rows); only the workload "N more" row substitutes.
                  text: (el, s) =>
                    el.call('polyglot', s.label).replace('{count}', s.moreCount ?? '')
                }
              }
            },

            // Legend — who can act: an unset priority is a triage debt,
            // not a bucket, so it is named here rather than counted above.
            Legend: {
              tag: 'div',
              hide: (el, s) => _workspacePriorityMix(s.root).unset === 0,
              fontSize: 'Z1',
              color: 'caption',
              paddingTop: 'V',
              text: (el, s) =>
                el
                  .call('polyglot', 'workspace.widget.prioritymix.toTriage')
                  .replace('{count}', _workspacePriorityMix(s.root).unset)
            }
          }
        }
      ),
      WorkloadWidget: _widget(
        {
          id: 'workload',
          x: 0,
          y: 11,
          w: 6,
          h: 4,
          title: 'workspace.widget.workload.title',
          icon: 'people',
          openRoute: '/tasks',
          hue: 'tickets'
        },
        {
          flow: 'y',
          minHeight: '0',

          // ── Mini tier (3-5 cols): the busiest person's load ───────────
          Mini: {
            class: 'widget-tier-mini',
            flow: 'y',
            gap: 'X',
            align: 'flex-start flex-end',
            padding: 'Z',
            Count: {
              tag: 'span',
              fontSize: 'G',
              fontWeight: '300',
              lineHeight: '0.9',
              text: (el, s) => String(_workloadRows(s.root).rows[0]?.n || 0)
            },
            CountLabel: {
              extends: 'HomePosterCaption',
              text: (el, s) => {
                const top = _workloadRows(s.root).rows[0]
                if (!top) return el.call('polyglot', 'workspace.workload.captionEmpty')
                return el
                  .call('polyglot', 'workspace.workload.captionTop')
                  .replace('{name}', el.call('polyglot', top.label))
              }
            }
          },

          // ── Full tier (6+ cols): bar + per-person counts + legend ─────
          Full: {
            class: 'widget-tier-full',
            flow: 'y',
            gap: 'Y',
            padding: 'Z',
            minHeight: '0',

            Bar: {
              flow: 'x',
              height: 'Y',
              round: 'radiusControl',
              overflow: 'hidden',
              background: 'line',
              children: (el, s) => {
                const { rows } = _workloadRows(s.root)
                const counts = {}
                for (const r of rows) counts[r.key] = r.n
                return _barSegments(rows, counts, 24).map((seg) => ({
                  key: seg.key,
                  flex: String(seg.units),
                  background: seg.hue,
                  minWidth: '0'
                }))
              }
            },

            Counts: {
              flow: 'x',
              flexWrap: 'wrap',
              gap: 'Y',
              childrenAs: 'state',
              children: (el, s) => _workloadRows(s.root).rows,
              childExtends: {
                flow: 'x',
                gap: 'V',
                align: 'center',
                fontSize: 'Z1',
                color: 'caption',
                Dot: {
                  tag: 'span',
                  boxSize: 'X1',
                  round: '50%',
                  flexShrink: '0',
                  background: (el, s) => s.hue
                },
                N: {
                  tag: 'span',
                  display: 'inline-block',
                  minWidth: 'C',
                  fontVariantNumeric: 'tabular-nums',
                  fontWeight: '700',
                  color: 'title',
                  text: (el, s) => String(s.n)
                },
                Label: {
                  tag: 'span',
                  // `.replace` is a no-op on the rows whose value carries no
                  // placeholder (the pulse / priority buckets and the person
                  // rows); only the workload "N more" row substitutes.
                  text: (el, s) =>
                    el.call('polyglot', s.label).replace('{count}', s.moreCount ?? '')
                }
              }
            },

            Legend: {
              tag: 'div',
              hide: (el, s) => _workloadRows(s.root).unassigned === 0,
              fontSize: 'Z1',
              color: 'caption',
              paddingTop: 'V',
              text: (el, s) =>
                el
                  .call('polyglot', 'workspace.workload.unassignedCount')
                  .replace('{count}', _workloadRows(s.root).unassigned)
            }
          }
        }
      ),
      ProjectsWidget: _widget(
        {
          id: 'projects',
          x: 0,
          y: 8,
          w: 12,
          h: 7,
          title: 'workspace.widget.websites.title',
          icon: 'layers',
          openRoute: '/studio'
        },
        {
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(min(100%, 220px), 1fr))',
          // Explicit row height so every grid cell (real ProjectCard +
          // EmptyProjectCard) renders at the same visual size — without
          // it the cards size to their intrinsic content and the row
          // jumped around (ProjectCard gradient = tall ~ 240px;
          // EmptyProjectCard = ~200px). `gridAutoRows: '180px'` plus a
          // matching `childProps.height` on the populated Cards branch
          // keeps the heights uniform regardless of which branch is
          // showing.
          // Populated: fixed 200px rows so every ProjectCard + the AddCard
          // tile render at a uniform height. Empty: a single row that
          // stretches (`1fr`) to fill the widget body so the lone "Create
          // your first project" tile fills the cell instead of shrinking to
          // 200px and leaving a tall dead gap below it.
          gridAutoRows: (el, s) =>
            _activeWorkspaceProjects(s.root).length === 0 ? 'minmax(200px, 1fr)' : '200px',
          gap: 'B',
          padding: 'B',

          Cards: {
            hide: (el, s) => _activeWorkspaceProjects(s.root).length === 0,
            display: 'contents',
            childExtends: 'ProjectCard',
            // The card ships `theme: 'canvas-card-glass'` (near-black glass +
            // `line` border) — right on a neutral canvas, wrong INSIDE a
            // saturated widget colour: every tile read as a navy plate with a
            // grey hairline (Nika 2026-08-15 "fix colors on widgets"). Re-plate
            // it with the widget colour's own ink-alpha system
            // (`homeWidgetHue.js` contract: fill = ink.12, wash = ink.08,
            // inkSoft = ink.55) — one hue per widget.
            // 2026-09-02: the pair of duplicated `@dark` / `@light` blocks is
            // GONE and each value is a factory instead. Those blocks existed
            // because "a plain prop would lose to the theme's own [data-theme]
            // rules" — true of a STATIC prop, which compiles to a class rule.
            // A function-valued prop is written as a reactive INLINE style,
            // which beats every stylesheet rule in both schemes AND repaints
            // when the user picks or clears the colour. Keeping the blocks
            // would in fact invert the result: smbls stamps `!important` on a
            // theme-block declaration that collides with a reactive inline
            // counterpart (element/src/create.js `collidesWithReactiveInline`).
            childProps: {
              height: '200px',
              minHeight: '200px',
              background: (el, s) => (_cardHue(el, s, 'projects') || {}).fill || 'overlayStrong',
              borderColor: (el, s) => (_cardHue(el, s, 'projects') || {}).wash || 'line',
              Body: {
                Name: { color: (el, s) => (_cardHue(el, s, 'projects') || {}).ink || 'title' },
                Desc: {
                  color: (el, s) => (_cardHue(el, s, 'projects') || {}).inkSoft || 'caption'
                }
              }
            },
            childrenAs: 'state',
            children: (el, s) => {
              const orgs = s.root?.jwtClaims?.orgs || s.root?.orgs || []
              const orgId = s.root?.jwtClaims?.workspace_org_id
              const ownerSlug = orgs.find((o) => o.id === orgId)?.slug || ''
              const wsId = s.root?.jwtClaims?.active_workspace_id || s.root?.workspace?.id
              const wsList = s.root?.jwtClaims?.workspaces || []
              const wsSlug = wsList.find((w) => w.id === wsId)?.slug || ''
              // ownerSlugOf: extract a human-readable slug from an owner
              // field. When the field is still a raw Mongo ObjectId (24 hex
              // chars — un-migrated project) treat it as absent and fall
              // through to the JWT-derived ownerSlug so the card routes
              // correctly instead of navigating to /{objectId}/... (#2185).
              const _isObjectId = (v) => /^[a-f0-9]{24}$/i.test(String(v || ''))
              const ownerSlugOf = (v) => {
                if (!v) return ''
                if (typeof v === 'string') return _isObjectId(v) ? '' : v
                return v?.slug || ''
              }
              return _activeWorkspaceProjects(s.root).map((p) => ({
                ...p,
                owner: ownerSlugOf(p.owner) || ownerSlug,
                workspaceSlug: ownerSlugOf(p.workspaceSlug) || wsSlug
              }))
            }
          },

          // "+ New project" tile — shown alongside existing project cards so
          // the create affordance is always reachable, not just in the empty
          // state. Reuses EmptyProjectCard (same +/dashed look + picker click),
          // relabelled for the populated case.
          AddCard: {
            hide: (el, s) => _activeWorkspaceProjects(s.root).length === 0,
            extends: 'EmptyProjectCard',
            height: '200px',
            minHeight: '200px',
            // Same re-plating as the Cards above: EmptyProjectCard's `line`
            // dashed ring + `overlay`/`dialog` cover + `title`/`caption` ink
            // are neutral-canvas values that read grey-on-blue inside a
            // saturated widget colour. Dashed ring = ink at .35
            // (design-direction §8's dashed-ring alpha), cover = ink wash,
            // plus = ink fill, text = ink / inkSoft — and every one of them
            // falls back to the neutral token the shared card ships with when
            // the widget has no colour (2026-09-02).
            borderColor: (el, s) => {
              const h = _cardHue(el, s, 'projects')
              return h ? h.ink + '.35' : 'line'
            },
            color: (el, s) => (_cardHue(el, s, 'projects') || {}).inkSoft || 'caption',
            // The hover ring brightens from the .35 dashed alpha to the FULL
            // live ink via `currentColor`. It used to name the ink token
            // directly and also hop the text from inkSoft to ink; a `:hover`
            // block is resolved ONCE at create (smbls `resolveCssBlock`), so a
            // named token there would freeze at whatever colour the card had
            // on first paint and go stale the moment the user picks another.
            // `currentColor` reads the element's own reactive `color`, so the
            // ring follows every colour change with no staleness.
            ':hover': { borderColor: 'currentColor' },
            Cover: {
              background: (el, s) => (_cardHue(el, s, 'projects') || {}).wash || 'overlay',
              Plus: {
                background: (el, s) => (_cardHue(el, s, 'projects') || {}).fill || 'dialog',
                color: (el, s) => (_cardHue(el, s, 'projects') || {}).ink || 'title'
              }
            },
            Body: {
              Title: {
                text: '{{ workspace.projects.new | polyglot }}',
                color: (el, s) => (_cardHue(el, s, 'projects') || {}).ink || 'title'
              },
              Hint: {
                text: '{{ workspace.projects.addToWorkspace | polyglot }}',
                color: (el, s) => (_cardHue(el, s, 'projects') || {}).inkSoft || 'caption'
              }
            }
          },

          Empty: {
            hide: (el, s) => _activeWorkspaceProjects(s.root).length !== 0,
            extends: 'EmptyProjectCard',
            // Fill the whole widget body (full width across all columns, and
            // stretch to the full height of the `1fr` row) so the empty state
            // reads as a deliberate full-size CTA rather than a shrunken 200px
            // tile. `alignSelf: stretch` overrides EmptyProjectCard's
            // flex-start so it grows to the row height.
            gridColumn: '1 / -1',
            alignSelf: 'stretch',
            height: 'auto',
            minHeight: '200px',
            // Center the + and the "Create your first project" label together as
            // one group in the middle of the full-size card. EmptyProjectCard's
            // Cover is 16/9 BY WIDTH — at full widget width that computes to a
            // ~770px-tall cover that overflows the card and shoves the CTA out of
            // view. Drop the ratio + the filled cover box, keep the + at its
            // natural size, and center the [cover, body] column as a group.
            align: 'center center',
            gap: 'Z',
            // Poster ink, like the AddCard branch above. EmptyProjectCard is a
            // SHARED component (packages/shared): its `title`/`caption` neutrals
            // are right on a plain canvas and wrong on this fixed poster hue —
            // measured Title rgb(4,4,4) 3.24:1 and Hint rgb(36,36,40) 2.44:1 on
            // posterStudio #0F45FF in light, and failing the other way in dark
            // (HOME-POSTER-NEUTRAL-INK-ON-HUE-1). The resolvers keep the neutral
            // tokens on every non-poster mount of the same component.
            color: HomePosterSoftInk.color,
            // `currentColor` for the same reason as the AddCard branch above:
            // a `:hover` block is resolved once at create and would freeze a
            // named colour token; `currentColor` follows the element's own
            // reactive ink instead.
            ':hover': { borderColor: 'currentColor' },
            Cover: {
              aspectRatio: 'auto',
              flex: '0 0 auto',
              width: 'auto',
              background: 'transparent',
              padding: '0',
              // Same re-plate the AddCard branch applies — a neutral `dialog`
              // disc reads as a grey plate on a coloured card.
              Plus: {
                background: (el, s) => (_cardHue(el, s, 'projects') || {}).fill || 'dialog',
                color: HomePosterInk.color
              }
            },
            Body: {
              align: 'center',
              textAlign: 'center',
              padding: '0',
              Title: { color: HomePosterInk.color },
              Hint: { color: HomePosterSoftInk.color }
            }
          }
        }
      ),
      DocumentsWidget: _widget(
        {
          id: 'assets',
          x: 0,
          y: 15,
          w: 12,
          h: 4,
          title: 'workspace.widget.assets.title',
          icon: 'document',
          openRoute: '/notes'
        },
        {
          flow: 'y',
          minHeight: '0',

          // Inline fetch (same declaration the /documents page uses) —
          // populates `root.documents` so the recent-list below renders
          // immediately and the /documents page benefits from the warm
          // cache on navigation. Kept at this top level (sibling to the
          // Mini/Full tiers below), not nested inside Full — the tiers are
          // a CSS-visibility split, not a conditional mount, so the fetch
          // fires exactly once regardless of which tier is showing.
          fetch: [
            {
              method: 'select',
              from: 'docs',
              order: 'updatedAt',
              ascending: false,
              as: '~/documents',
              cache: '30s',
              transform: (res) => {
                const rows = Array.isArray(res) ? res : res?.data || []
                return {
                  // Mirror documentsFetch's shape (documents/functions) via
                  // the shared `_mapDocRow` mapper — non-KB rows only,
                  // folderPath '/' → unfiled. `updatedAt` stays RAW here
                  // (DATE-FMT-1 — formatting moved to the render boundary,
                  // see Caption below); do NOT re-introduce a per-callsite
                  // `.slice(0, 10)`/format here.
                  documents: rows.filter((r) => r.type !== 'kb_article').map(_mapDocRow),
                  _documentsLoaded: true
                }
              },
              onFetchError: (err, el, s) => {
                console.warn('[home] documents fetch failed:', err?.message || err)
                const root = s?.root || s
                if (root?.update)
                  root.update({ _documentsLoaded: true, documents: root.documents || [] })
              }
            }
          ],

          // ── Mini tier (3-5 cols): numeral + one line ──────────────────
          Mini: {
            class: 'widget-tier-mini',
            flow: 'y',
            gap: 'X',
            align: 'flex-start flex-end',
            padding: 'Z',
            Count: {
              tag: 'span',
              fontSize: 'G',
              fontWeight: '300',
              lineHeight: '0.9',
              text: (el, s) => String((s.root?.documents || []).length)
            },
            CountLabel: {
              extends: 'HomePosterCaption',
              text: '{{ workspace.caption.notes | polyglot }}'
            }
          },

          // ── Full tier (6+ cols): the existing chip grid + a quick-new-
          // note row (board §2 "notes/assets: chip grid (exists) +
          // quick-new-note input row").
          Full: {
            class: 'widget-tier-full',
            flow: 'y',
            gap: 'V',
            padding: 'Z',
            minHeight: '0',

            QuickAdd: {
              flow: 'x',
              gap: 'V',
              align: 'center',
              flexShrink: '0',
              Input: {
                flex: '1',
                minWidth: '0',
                minHeight: 'controlHCompact',
                round: 'radiusControl',
                fontSize: 'Z1',
                padding: '0 Z1',
                // The field law (brand `field` theme), as on the Reminders row.
                theme: 'field',
                border: 'none',
                aria: { label: (el) => el.call('polyglot', 'workspace.notes.newAria') },
                placeholder: (el) => el.call('polyglot', 'workspace.notes.newPlaceholder'),
                value: (el, s) => s.root?._noteDraft || '',
                onClick: (e) => e.stopPropagation(),
                onInput: (e, el, s) => s.root.update({ _noteDraft: e.target.value }),
                onKeydown: (e, el, s) => {
                  e.stopPropagation()
                  if (e.key !== 'Enter') return
                  e.preventDefault()
                  _submitNoteQuickAdd(el, s.root)
                }
              },
              AddBtn: {
                tag: 'button',
                type: 'button',
                class: 'gs-no-drag',
                flexShrink: '0',
                // The quick-add row's primary action: solid ink (`primary`), never
                // a hue — `color: 'indigoblue'` on `black` was the old poster's
                // hue as action ink. The theme owns the hover fill step, so the
                // opacity hover and its transition are gone (Rule 65); same shape
                // as the Reminders Add button.
                theme: 'primary',
                border: 'none',
                minHeight: 'controlHCompact',
                round: 'radiusPill',
                padding: '0 Z',
                fontSize: 'Z1',
                fontWeight: '700',
                cursor: 'pointer',
                // THE DISABLED LAW (brand 3.14.793): the field-disabled pair, never
                // opacity.
                ':disabled': { theme: 'field-disabled' },
                disabled: (el, s) => !(s.root?._noteDraft || '').trim(),
                text: '{{ workspace.notes.add | polyglot }}',
                onClick: (e, el, s) => {
                  e.stopPropagation()
                  _submitNoteQuickAdd(el, s.root)
                }
              }
            },

            // Grid of tile cards — replaces the legacy vertical row list.
            // Each tile opens /documents in the home AppWindow PageSlot via
            // `selectedPage`, NOT via `el.router(route)`: the user stays on
            // the dashboard, the documents page mounts inline, and the
            // close-x returns them to home unchanged. Same dispatch shape
            // the widget header's "Open Documents" action uses.
            //
            // Layout: auto-fill `minmax(160px, 1fr)` so tiles re-flow at the
            // current widget width without per-breakpoint media queries.
            // Inline tile definition (no `childExtends: 'AppListItem'`) —
            // AppListItem is a wide-row launcher, the wrong shape for a
            // grid tile (icon-top vs icon-left, no two-up wrap, route-based
            // onClick we'd have to fight). Keeping the tile inline also
            // keeps the home page self-contained — no new shared component
            // shipping just for this widget.
            DocumentsList: {
              hide: (el, s) => !(s.root.documents || []).length,
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(min(100%, 160px), 1fr))',
              gap: 'V',
              overflow: 'auto',
              minHeight: '0',
              children: (el, s) =>
                (s.root.documents || []).slice(0, 8).map((d) => ({
                  // Stable identity key (DOMQL reconciles `children` arrays by
                  // key, not array position — project_domql_children_reconcile
                  // _by_key). Without it, a re-render that reorders
                  // `root.documents` (a background 30s-cache refetch landing
                  // after first paint, a resort by `updatedAt`) reconciles by
                  // POSITION: index 0's node gets reused for whatever row now
                  // sits at index 0, even though it held a different doc a
                  // moment ago. Each card's static `text: d.title` / `text:
                  // […, el.call('shortDate', d.updatedAt)]` is resolved once,
                  // here, inside this factory — with a stable `key: d.id` a
                  // reorder now correctly creates/moves/updates by DOCUMENT,
                  // not by slot, so a card can never show one document's
                  // shortDate-formatted caption while momentarily still
                  // wired to a different document's raw row underneath.
                  key: d.id,
                  scope: {
                    d: d
                  },

                  tag: 'button',
                  type: 'button',
                  flow: 'y',
                  gap: 'V',
                  align: 'flex-start',
                  padding: 'Z A',
                  round: 'radiusCard',
                  textAlign: 'left',
                  cursor: 'pointer',
                  background: _posterStep('wash'),
                  border: 'none',
                  color: HomePosterInk.color,
                  minHeight: 'F',
                  transition: '160ms defaultBezier background',
                  ':hover': { background: _posterStep('fill') },

                  // Click opens the EXACT clicked document in the editor
                  // (2026-06-08 per Nika — items open their own doc; the widget
                  // header arrow still opens the documents LIST). Mirrors the
                  // documents page's own DocsCard dispatch: seed editorDoc +
                  // activeSection for instant render, then navigate to the
                  // canonical doc URL whose hash the editor reads to load it.
                  onClick: (e, el, s) => {
                    e.preventDefault()
                    // Keep the selected record on the destination route. The
                    // editor reads that route through readPageHash, which uses
                    // the owning pane in PageSlot and the address bar on a
                    // dedicated page. Clear any stale record before it restores.
                    s.root.update({
                      editorDoc: null,
                      editorMode: 'view',
                      activeSection: 'editor'
                    })
                    el.call(
                      'openAppPage',
                      '/notes/editor#note=' + encodeURIComponent(d.id || ''),
                      e
                    )
                  },

                  IconTile: {
                    width: 'C',
                    height: 'C',
                    minWidth: 'C',
                    round: 'radiusControl',
                    align: 'center center',
                    // 'document' is NOT a colour token — it fell through the token
                    // table to solid black, a heavy square fighting the indigoblue
                    // poster. Ink-alpha fill per the poster secondary system.
                    background: _posterStep('fill'),
                    color: HomePosterInk.color,
                    Icon: { name: 'document', boxSize: 'A' }
                  },

                  Title: {
                    tag: 'span',
                    fontSize: 'A',
                    fontWeight: '700',
                    color: HomePosterInk.color,
                    // Primary text wraps — never clamped behind an ellipsis.
                    overflowWrap: 'anywhere',
                    text: d.title || el.call('polyglot', 'documents.notes.untitledShort')
                  },

                  Caption: {
                    extends: 'HomePosterSoftInk',
                    tag: 'p',
                    margin: '0',
                    marginTop: 'auto',
                    fontSize: 'Y',
                    lineHeight: '1.4',
                    overflowWrap: 'anywhere',
                    // DATE-FMT-1 — format at the render boundary via the
                    // shared `shortDate` formatter, never a per-callsite
                    // slice/format in the fetch transform above: `d.updatedAt`
                    // is the raw server ISO string (see `_mapDocRow`), so this
                    // is the ONE place it ever gets displayed, and it can
                    // never reach the DOM unformatted.
                    text: [d.folder, el.call('shortDate', d.updatedAt)].filter(Boolean).join(' · ')
                  }
                }))
            },

            // Empty state — only renders once the fetch has settled with
            // zero documents (loading flicker is silenced by the
            // `_documentsLoaded` gate).
            Empty: {
              hide: (el, s) => !s.root?._documentsLoaded || (s.root.documents || []).length > 0,
              flow: 'y',
              gap: 'A',
              padding: 'A B',
              Description: {
                extends: 'HomePosterSoftInk',
                tag: 'p',
                margin: '0',
                fontSize: 'A',
                lineHeight: '1.5',
                text: '{{ workspace.notes.empty | polyglot }}'
              }
            }
          }
        }
      ),
      AgentsWidget: _widget(
        {
          id: 'agents',
          x: 0,
          y: 27,
          w: 6,
          h: 4,
          title: 'workspace.widget.agents.title',
          icon: 'star',
          openRoute: '/team-and-agents'
        },
        {
          // Same geex frame-26 recipe as AnalyticsWidget below: a full-width
          // segmented share bar (people vs. agents of total headcount), then
          // per-segment giant numerals with square legend markers. Reads
          // root.people / root._agents — both already shell-loaded (same
          // contract AppTeamWidget's combined count uses above) — no new
          // fetch.
          flow: 'y',
          gap: 'B',
          padding: 'B',
          SegBar: {
            flow: 'x',
            width: '100%',
            height: 'Z2',
            round: 'radiusPill',
            overflow: 'hidden',
            background: _posterStep('fill'),
            // Only shown once BOTH counts are positively read — the bar is a
            // proportion, and a proportion of an unread population is a
            // confident lie. `SegBarSkeleton` below stands in until then.
            hide: (el, s) => el.call('teamSplitUnknown', s.root),
            title: (el, s) => el.call('teamSplitTitle', s.root),
            // FALSE-ZERO-1 — a PROPORTION is a measurement. Both segments
            // used to divide by a total that silently omitted an unread
            // roster, so the bar asserted 100% people / 0% agents (measured
            // live: SegPeople 375.9px, SegAgents 0px, roster absent). Both
            // now collapse to 0 until BOTH counts are positively read; the
            // track then shows unsegmented, and the SegBar pulse marks it as
            // loading rather than empty.
            SegPeople: {
              background: _posterStep('ink'),
              transition: 'A defaultBezier flex',
              flex: (el, s) => el.call('teamSplitFlex', s.root, 'people')
            },
            SegAgents: {
              background: _posterStep('inkMeta'),
              transition: 'A defaultBezier flex',
              flex: (el, s) => el.call('teamSplitFlex', s.root, 'agents')
            }
          },
          // FALSE-ZERO-1 loading affordance. Reuses the design system's own
          // placeholder (shared `components/Skeleton.js` — pulse keyframe +
          // a prefers-reduced-motion branch) instead of animating SegBar
          // from a factory: a returned `null` does not clear an already-set
          // animationName, so the bar kept pulsing after the roster landed —
          // a permanent "still loading" is the same class of lie as a
          // permanent zero. Mount/unmount cannot get stuck that way.
          SegBarSkeleton: {
            extends: 'SkeletonRow',
            width: '100%',
            height: 'Z2',
            round: 'radiusPill',
            hide: (el, s) => !el.call('teamSplitUnknown', s.root),
            title: (el, s) => el.call('teamSplitTitle', s.root)
          },
          Rows: {
            flow: 'x',
            gap: 'D',
            wrap: 'wrap',
            RowPeople: {
              flow: 'y',
              gap: 'X',
              Num: {
                tag: 'span',
                fontSize: 'F',
                fontWeight: '300',
                lineHeight: '0.95',
                text: (el, s) => el.call('peopleCountText', s.root),
                title: (el, s) => el.call('peopleTitle', s.root)
              },
              LegendRow: {
                flow: 'x',
                align: 'center start',
                gap: 'Y',
                Mark: {
                  tag: 'div',
                  width: 'X2',
                  height: 'X2',
                  background: _posterStep('ink'),
                  flexShrink: '0'
                },
                Name: {
                  extends: 'HomePosterCaption',
                  text: '{{ workspace.caption.teamMembers | polyglot }}'
                }
              }
            },
            RowAgents: {
              flow: 'y',
              gap: 'X',
              Num: {
                extends: 'HomePosterSoftInk',
                tag: 'span',
                fontSize: 'F',
                fontWeight: '300',
                lineHeight: '0.95',
                // FALSE-ZERO-1 surface 4 — the literal "0 AI agents": was a
                // bare Array.isArray(_agents) ? length : 0, which reads 0
                // for BOTH "genuinely zero agents" and "roster still
                // loading / failed to load". agentRosterCountText
                // distinguishes all three per the tri-state contract in
                // shared/state.js:142-147.
                text: (el, s) => el.call('agentRosterCountText', s.root),
                title: (el, s) =>
                  el.call('agentRosterStatus', s.root) === 'error'
                    ? el.call('polyglot', 'workspace.home.error.agents')
                    : ''
              },
              LegendRow: {
                flow: 'x',
                align: 'center start',
                gap: 'Y',
                Mark: {
                  tag: 'div',
                  width: 'X2',
                  height: 'X2',
                  background: _posterStep('inkMeta'),
                  flexShrink: '0'
                },
                Name: {
                  extends: 'HomePosterCaption',
                  text: '{{ workspace.caption.aiAgents | polyglot }}'
                }
              }
            }
          },

          // Avatar row (board §2 "team&agents split counts + avatars
          // row (currently near-empty dark card — defect)" — the counts
          // half was already fixed 2026-08-12; this adds the avatars).
          // Reuses AvatarBundleItem's overlapping-circle styling (NOT the
          // AvatarBundle wrapper — that one's own `children` factory is
          // peopleByEmail-bound, people-only; agents need a second,
          // cross-domain source), fed a MIX of root.people + root._agents
          // (both already shell-loaded, no new fetch). borderColor
          // overridden to the poster's own bg (purple) via childProps —
          // AvatarBundleItem's default 'overlay' border is an ADAPTIVE
          // token that would collide with this fixed-ink poster the same
          // way title/caption did for CityMemberRow/UpcomingEventCard.
          Avatars: {
            // Both factories below short-circuit on `Array.isArray(...)`,
            // so the auto-tracker can miss `_agents` on a run where
            // `people` alone already decides the branch (FA515 — factory
            // deps are control-flow dependent) — declared explicitly so
            // both stay reactive regardless of which check short-circuits.
            hideDeps: ['people', '_agents'],
            // FALSE-ZERO-1: `people + agents === 0` hid the row whenever the
            // roster was merely unread — an unread population rendered as a
            // confident "nobody here". Only a LOADED roster may hide it.
            hide: (el, s) => el.call('teamAvatarsHidden', s.root),
            flow: 'x',
            align: 'center',
            marginTop: 'V',
            childExtends: 'AvatarBundleItem',
            childrenAs: 'state',
            // borderColor: poster bg ring (see comment above). BundlePlate:
            // AvatarBundleItem's house identity plate ships the ADAPTIVE
            // overlayStrong/title pair — pin it to this fixed purple
            // poster's own derived {fill, ink} (white-ink family) so dark
            // mode can't flip the initials illegible. ':last-child' stops
            // the overlap stack's negative margin bleeding into the next
            // sibling.
            childProps: {
              borderColor: (el, s) => (_cardHue(el, s, 'agents') || {}).bg || 'overlay',
              ':last-child': { marginInlineEnd: '0' },
              BundlePlate: {
                background: (el, s) => (_cardHue(el, s, 'agents') || {}).fill || 'overlayStrong',
                color: (el, s) => (_cardHue(el, s, 'agents') || {}).ink || 'title'
              }
            },
            childrenDeps: ['people', '_agents'],
            children: (el, s) => el.call('teamAvatarRows', s.root)
          }
        }
      ),
      YourAgentsWidget: _widget(
        {
          id: 'youragents',
          x: 0,
          y: 33,
          w: 12,
          h: 5,
          title: 'workspace.widget.youragents.title',
          icon: 'star',
          action: {
            icon: 'plus',
            label: 'workspace.widget.action.createAgent',
            set: { _createAgentOpen: true, _createAgentError: null }
          }
        },
        {
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 240px), 1fr))',
          gap: 'V',
          padding: 'Z',
          childrenAs: 'state',
          children: (el, s) => el.call('yourAgentsRows', s.root),
          childExtends: 'YourAgentCard'
        }
      ),
      AnalyticsWidget: _widget(
        {
          id: 'analytics',
          x: 0,
          y: 33,
          w: 6,
          h: 4,
          title: 'workspace.widget.analytics.title',
          icon: 'analytics',
          openRoute: '/workspace-analytics'
        },
        {
          // Geex frame 26 (design-refs/geex/26-analytics-segmented-progress
          // .png): ONE full-width segmented bar — solid ink for active-now,
          // ink washes for the rest — then per-segment giant numerals with
          // square legend markers on the widget's gray2 poster. Reads
          // `root._homeWorkspaceActivity` (PRESENCE-ZERO-1 — see
          // `_loadHomeWorkspaceActivity` above; was the Projects-scoped
          // `root._analytics`, which read 0 in every dogfood session), with
          // ?? guards so a bare mount renders zeros, never a throw (the
          // package's own series call is the known .map crash — this
          // composition deliberately does not call it).
          flow: 'y',
          gap: 'B',
          padding: 'B',
          SegBar: {
            flow: 'x',
            width: '100%',
            height: 'Z2',
            round: 'radiusPill',
            overflow: 'hidden',
            background: _posterStep('fill'),
            // FALSE-ZERO-HOME-NON-AGENT-KEYS-1 — a PROPORTION is a
            // measurement. Both segments used to read `Number(x) || 0` over
            // a payload nobody had read (or that had failed), so the bar
            // asserted "0 of 0 today" as a confident 4%/96% track. Hidden
            // until BOTH metrics are positively read; `SegBarSkeleton` below
            // stands in (mount/unmount, never a nulled animation — see the
            // team bar above for why). The visual floor for a nonzero share
            // and the collapsed genuinely-zero segment live in
            // `homeActivitySegFlex` (functions/homeLoadState.js).
            hide: (el, s) => el.call('homeActivityUnknown', s.root),
            title: (el, s) => el.call('homeActivityTitle', s.root),
            SegNow: {
              background: _posterStep('ink'),
              transition: 'A defaultBezier flex',
              flex: (el, s) => el.call('homeActivitySegFlex', s.root, 'now')
            },
            SegRest: {
              background: _posterStep('inkMeta'),
              transition: 'A defaultBezier flex',
              flex: (el, s) => el.call('homeActivitySegFlex', s.root, 'rest')
            }
          },
          SegBarSkeleton: {
            extends: 'SkeletonRow',
            width: '100%',
            height: 'Z2',
            round: 'radiusPill',
            hide: (el, s) => !el.call('homeActivityUnknown', s.root),
            title: (el, s) => el.call('homeActivityTitle', s.root)
          },
          Rows: {
            flow: 'x',
            gap: 'D',
            wrap: 'wrap',
            RowNow: {
              flow: 'y',
              gap: 'X',
              Num: {
                tag: 'span',
                fontSize: 'F',
                fontWeight: '300',
                lineHeight: '0.95',
                text: (el, s) => el.call('homeActiveNowText', s.root),
                title: (el, s) => el.call('homeActivityTitle', s.root)
              },
              LegendRow: {
                flow: 'x',
                align: 'center start',
                gap: 'Y',
                Mark: {
                  tag: 'div',
                  width: 'X2',
                  height: 'X2',
                  background: _posterStep('ink'),
                  flexShrink: '0'
                },
                Name: {
                  extends: 'HomePosterCaption',
                  text: '{{ workspace.caption.activeNow | polyglot }}'
                }
              }
            },
            RowToday: {
              flow: 'y',
              gap: 'X',
              Num: {
                extends: 'HomePosterSoftInk',
                tag: 'span',
                fontSize: 'F',
                fontWeight: '300',
                lineHeight: '0.95',
                text: (el, s) => el.call('homeActiveTodayText', s.root),
                title: (el, s) => el.call('homeActivityTitle', s.root)
              },
              LegendRow: {
                flow: 'x',
                align: 'center start',
                gap: 'Y',
                Mark: {
                  tag: 'div',
                  width: 'X2',
                  height: 'X2',
                  background: _posterStep('inkMeta'),
                  flexShrink: '0'
                },
                Name: {
                  extends: 'HomePosterCaption',
                  text: '{{ workspace.caption.today | polyglot }}'
                }
              }
            }
          }
        }
      ),
      ActivityWidget: _widget(
        {
          id: 'activity',
          x: 0,
          y: 39,
          w: 12,
          h: 6,
          title: 'workspace.widget.activity.title',
          icon: 'analytics',
          openRoute: '/tasks/my'
        },
        {
          padding: 'B',
          // The heatmap is a SHARED tickets-package component (it also mounts on
          // the neutral /tasks/my page), so its own ink stays the adaptive
          // neutral pair and the POSTER re-plates it here — the same direction
          // every other sub-card on a poster now takes. Measured on the
          // analytics poster #141416 in light: the legend rgb(36,36,40) 1.19:1
          // and the caption line 2.49:1 (HOME-POSTER-NEUTRAL-INK-ON-HUE-1).
          TicketsActivityHeatmap: {
            CaptionTitle: { color: HomePosterSoftInk.color },
            Legend: { tag: 'div', color: HomePosterSoftInk.color },
            DetailsCard: {
              Header: { Title: { color: HomePosterInk.color } },
              Empty: { color: HomePosterSoftInk.color },
              List: {
                childProps: {
                  Time: { color: HomePosterSoftInk.color },
                  Detail: { color: HomePosterInk.color }
                }
              }
            }
          }
        }
      ),
      EventsWidget: _widget(
        {
          id: 'events',
          x: 0,
          y: 45,
          w: 12,
          h: 6,
          title: 'workspace.widget.events.title',
          icon: 'date',
          openRoute: '/calendar'
        },
        {
          flow: 'y',
          minHeight: '0',

          // ── Mini tier (3-5 cols): numeral + one line ──────────────────
          Mini: {
            class: 'widget-tier-mini',
            flow: 'y',
            gap: 'X',
            align: 'flex-start flex-end',
            padding: 'Z',
            Count: {
              tag: 'span',
              fontSize: 'G',
              fontWeight: '300',
              lineHeight: '0.9',
              text: (el, s) => String(_weekEvents(s).length)
            },
            DueLabel: {
              extends: 'HomePosterCaption',
              text: '{{ workspace.caption.next7Days | polyglot }}'
            }
          },

          // ── Full tier (6+ cols): the existing card grid, now with a
          // JOIN shortcut per meet-linked card (UpcomingEventCard.JoinBtn)
          // and an explicit empty state (was silent — a bare 0-card grid —
          // the header subtitle already says "0 events" but the body
          // itself gave no visual confirmation). Grid nested one level
          // under the tier wrapper: the tier toggle forces `display` on
          // '.widget-tier-full' itself (see _injectWidgetStyles), which
          // would clobber `display: grid` if applied to the grid directly
          // — same nesting reminders/received/given don't need (their
          // tiers are plain flow:'y' lists), but a css-grid body does.
          Full: {
            class: 'widget-tier-full',
            flow: 'y',
            minHeight: '0',

            Empty: {
              hide: (el, s) => !!_weekEvents(s).length,
              extends: 'HomePosterSoftInk',
              padding: 'A B',
              fontSize: 'A',
              text: '{{ workspace.events.empty | polyglot }}'
            },

            Grid: {
              hide: (el, s) => !_weekEvents(s).length,
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 220px), 1fr))',
              // Top-align cards at their natural height (2026-06-08 per Nika)
              // — grid's default `stretch` made a 2-line-title card stretch
              // every sibling in its row to match.
              alignItems: 'start',
              gap: 'A',
              padding: 'B',
              childExtends: 'UpcomingEventCard',
              childrenAs: 'state',
              children: (el, s) => _weekEvents(s).slice(0, 6)
            }
          }
        }
      ),
      BroadcastWidget: _widget(
        {
          id: 'broadcast',
          x: 0,
          y: 57,
          w: 12,
          h: 4,
          title: 'workspace.widget.broadcast.title',
          icon: 'bell',
          openRoute: '/announcements',
          // Opening the news marks it seen (functions/broadcast.js).
          openCall: 'markBroadcastSeen'
        },
        {
          flow: 'y',
          minHeight: '0',

          // ── Mini tier (3-5 cols): numeral + one line ──────────────────
          Mini: {
            class: 'widget-tier-mini',
            flow: 'y',
            gap: 'X',
            align: 'flex-start flex-end',
            padding: 'Z',
            Count: {
              tag: 'span',
              fontSize: 'G',
              fontWeight: '300',
              lineHeight: '0.9',
              text: (el, s) => String(el.call('broadcastUnreadCount', s.root))
            },
            CountLabel: {
              extends: 'HomePosterCaption',
              text: '{{ workspace.caption.unread | polyglot }}'
            }
          },

          // ── Full tier (6+ cols): the existing colored rows ────────────
          Full: {
            class: 'widget-tier-full',
            flow: 'y',
            gap: 'Z',
            padding: 'A B',
            minHeight: '0',

            // Latest announcements (2026-06-12 per Nika "BroadcastWidget does
            // not show news") — the widget was a static CTA; root.announcements
            // is already loaded shell-wide by loadPrivateData, so render the
            // newest three as clickable rows (same detail-modal recipe as
            // IntranetRows: selectedAnnouncement carrier + windows layer).
            // onClick here (not on BroadcastNewsRow itself) marks the
            // widget "seen" on ANY click bubbling up from inside — a named
            // row's own onClick can't be overridden via childExtends in
            // v3.14 (see that component's file-header comment), so the
            // listener lives one level up, on this container.
            News: {
              flow: 'y',
              gap: 'Z1',
              flex: '1',
              minHeight: '0',
              overflow: 'auto',
              childExtends: 'BroadcastNewsRow',
              childrenAs: 'state',
              // rowIndex is positional metadata (kept for any rotating
              // consumer); BroadcastNewsRow itself now paints the poster's
              // own ink-alpha fill, not a second hue per row — see its header.
              children: (el, s) =>
                (s.root?.announcements || [])
                  .slice(0, 3)
                  .map((a, rowIndex) => ({ ...a, rowIndex })),
              onClick: (e, el, s) => el.call('markBroadcastSeen')
            },

            Hint: {
              hide: (el, s) => (s.root?.announcements || []).length > 0,
              extends: 'HomePosterSoftInk',
              tag: 'p',
              margin: '0',
              fontSize: 'Z1',
              lineHeight: '1.5',
              text: '{{ workspace.broadcast.empty | polyglot }}'
            }
          }
        }
      ),
      MailInboxWidget: _widget(
        {
          id: 'mail-inbox',
          x: 0,
          y: 61,
          w: 12,
          h: 5,
          title: 'workspace.widget.mailInbox.title',
          icon: 'email',
          openRoute: '/mail'
        },
        {
          flow: 'y',
          minHeight: '0',
          // The count comes from the shell-scope stream, but the ROWS are a
          // REST read the /mail route owns — so the card fetches its own. The
          // fold is idempotent and single-flight (functions/mailFold.js), and
          // `loadMailThreads` no-ops when the slot already has rows, so a
          // re-render costs nothing. Explicit `listKey`: the card always shows
          // the UNIFIED inbox and must never move the /mail page's active view.
          onRender: async (el, s) => {
            if (!shellNavItemVisible(s.root, '/mail')) return
            try {
              const ok = await el.call('ensureMailContext')
              if (ok === false) return
              el.call('loadMailThreads', { listKey: MAIL_INBOX_LIST_KEY })
            } catch (err) {
              console.warn('[home] mail-inbox widget load failed:', err?.message || err)
            }
          },

          // ── Mini tier (3-5 cols): numeral + one line ──────────────────
          Mini: {
            class: 'widget-tier-mini',
            flow: 'y',
            gap: 'X',
            align: 'flex-start flex-end',
            padding: 'Z',
            subscribeTo: ['navUnreadCounts'],
            Count: {
              tag: 'span',
              fontSize: 'G',
              fontWeight: '300',
              lineHeight: '0.9',
              text: (el, s) => String(_mailUnread(s))
            },
            CountLabel: {
              extends: 'HomePosterCaption',
              text: '{{ workspace.caption.unread | polyglot }}'
            }
          },

          // ── Full tier (6+ cols): Compose + the newest five threads ─────
          Full: {
            class: 'widget-tier-full',
            flow: 'y',
            gap: 'Z',
            padding: 'A B',
            minHeight: '0',
            subscribeTo: ['mailThreads', 'navUnreadCounts'],

            // §3.2a: `openMailCompose` is the ONE compose entry point — it
            // owns every gate state (off / connect / link / ask-admin), so
            // this button is correct on a workspace with no mailbox yet and
            // needs no gate of its own.
            ComposeBtn: {
              tag: 'button',
              type: 'button',
              class: 'gs-no-drag',
              flow: 'x',
              align: 'center center',
              gap: 'Y',
              alignSelf: 'flex-start',
              padding: 'Y A',
              round: 'radiusControl',
              border: 'none',
              cursor: 'pointer',
              fontSize: 'Z1',
              fontWeight: '700',
              color: 'inherit',
              // currentColor wash, not a token fill — the card repaints when
              // the user picks a colour for it, and a fixed token would read
              // as a second hue on a coloured poster (RULES Rule 67, the same
              // reason _widget's own Header hover is derived).
              background: 'color-mix(in srgb, currentColor 10%, transparent)',
              transition: '120ms defaultBezier background',
              ':hover': { background: 'color-mix(in srgb, currentColor 18%, transparent)' },
              ComposeIcon: { extends: 'Icon', name: 'plus', boxSize: 'Z1' },
              ComposeLabel: {
                tag: 'span',
                text: '{{ workspace.widget.mailInbox.compose | polyglot }}'
              },
              onClick: (e, el) => {
                e.stopPropagation()
                el.call('openMailCompose')
              }
            },

            Empty: {
              hide: (el, s) => !!_mailInboxRows(s).length,
              extends: 'HomePosterSoftInk',
              padding: 'Z 0',
              fontSize: 'A',
              text: '{{ workspace.widget.mailInbox.empty | polyglot }}'
            },

            Rows: {
              hide: (el, s) => !_mailInboxRows(s).length,
              flow: 'y',
              gap: 'Y',
              flex: '1',
              minHeight: '0',
              overflow: 'auto',
              childExtends: 'HomeMailRow',
              childrenAs: 'state',
              children: (el, s) => _mailInboxRows(s)
            }
          }
        }
      ),
      TeamTimeWidget: _widget(
        {
          id: 'teamtime',
          x: 0,
          y: 51,
          w: 12,
          h: 6,
          title: 'workspace.widget.teamtime.title',
          icon: 'globe'
        },
        {
          flow: 'y',
          minHeight: '0',

          // ── Mini tier (3-5 cols): numeral + one line ──────────────────
          Mini: {
            class: 'widget-tier-mini',
            flow: 'y',
            gap: 'X',
            align: 'flex-start flex-end',
            padding: 'Z',
            Count: {
              tag: 'span',
              fontSize: 'G',
              fontWeight: '300',
              lineHeight: '0.9',
              text: (el, s) => String(_teamTimezoneGroups(s).length)
            },
            DueLabel: {
              extends: 'HomePosterCaption',
              text: '{{ workspace.caption.timeZones | polyglot }}'
            }
          },

          // ── Full tier (6+ cols): one CLUSTER per distinct timezone (was
          // one flat row per teammate — 2026-08-13 per Nika "on timezones
          // use avatarbundle and display grouped with timezones"). Each
          // cluster = zone label + giant live local time + the AvatarBundle
          // of that zone's members. Every avatar is clickable (opens their
          // profile via the same `openProfileModal` dispatch IntranetRows /
          // the People page member-open already use) and carries a hover
          // tooltip (name + role + local time) via the shared
          // @symbo.ls/tooltip contract, plus an online dot sourced from
          // `root.presenceById` — the same per-id map PersonCard's
          // PcOnlineDot reads (O(1) lookup, doesn't re-fire every dot when
          // one person's status changes).
          Full: {
            class: 'widget-tier-full',
            flow: 'y',
            gap: 'X',
            padding: 'B',
            overflow: 'auto',
            minHeight: '0',

            Clusters: {
              flow: 'y',
              gap: 'X',
              childExtends: 'TzCluster',
              childrenAs: 'state',
              children: (el, s) => _teamTimezoneGroups(s)
            },

            // Every list needs an empty state (product-UI brief §7) — a
            // solo workspace (or one where nobody has a timezone set) would
            // otherwise render a big blank poster below the header.
            Empty: {
              extends: 'HomePosterCaption',
              hide: (el, s) => _teamTimezoneGroups(s).length > 0,
              fontSize: 'Z1',
              text: '{{ workspace.teamtime.empty | polyglot }}'
            }
          }
        }
      ),
      // Module / app / AI widgets — state-driven rows through the same frame.
      ModuleWidgets: {
        tag: 'fragment',
        subscribeTo: ['workspaceProject', 'workspaceApps'],
        childrenAs: 'state',
        childExtends: HomeModuleWidget,
        // An icon-only module widget is an app: the App strip holds it.
        children: (el, s) => _allModuleWidgetsFor(s.root).filter((w) => !homeWidgetIsApp(w))
      },

      // Saved cells whose widget is still loading — skeletons in place.
      PendingWidgets: {
        tag: 'fragment',
        subscribeTo: [
          'workspaceProject',
          'workspaceApps',
          '_appRoutesSettled',
          '_appsSettingUp',
          '_homeLayout'
        ],
        childrenAs: 'state',
        childExtends: HomeWidgetPending,
        children: (el, s) => _pendingWidgetRows(s.root)
      },

      // THE GENERATED BOARD (ASSISTANT_BOARD_MODE_SPEC §4, decision 18;
      // components/AssistantBoardView.js): a VIEW of this grid area — laid
      // over the cards from the grid's top edge, out of the grid's flow, so
      // the saved board keeps every cell and nothing on the page moves. The
      // view gates and crossfades itself (opacity + visibility, `C`); the
      // cards under it step aside (inert, aria-hidden, faded — the frame's
      // homeWidgetUnderView), so no focus, click or J / K stop reaches them.
      // No `if:` / `hide:` here: a mount-site gate REPLACES the component's
      // own. A board taller than the grid runs into MainColumn's scroll area.
      AssistantBoardView: {
        position: 'absolute',
        top: '0',
        left: '0',
        right: '0',
        zIndex: '2'
      },

      // THE MAIN THREAD (AI_ASSISTANT_SPEC v1 §8, the default placement;
      // components/AssistantMainThread.js): the same kind of view over this
      // grid area — the conversation in a reading column, its wide blocks
      // across the board. It gates and crossfades itself; ONE view holds the
      // area at a time (the board steps aside while the thread is up). Chat's
      // screen, and Build's while a thread opened in Apps mode holds the home
      // (functions/homeView.js): a view part — while it holds the home the
      // board stylesheet puts it in the flow.
      AssistantMainThread: {
        dataHomeViewPart: true,
        position: 'absolute',
        top: '0',
        left: '0',
        right: '0',
        zIndex: '2'
      },

      // THE LIVE DECK (AI_ASSISTANT_SPEC §12.1, components/AssistantDeckView.js):
      // Presentation mode's view over this grid area — it gates and crossfades
      // itself; ONE view holds the area at a time. Present's screen.
      AssistantDeckView: {
        dataHomeViewPart: true,
        position: 'absolute',
        top: '0',
        left: '0',
        right: '0',
        zIndex: '2'
      },

      // THE FLY CANVAS (docs/FLY_MODE.md, components/AssistantFlyView.js):
      // Fly mode's session of composed pages over this grid area — it gates
      // and crossfades itself; ONE view holds the area at a time. Main's
      // screen.
      AssistantFlyView: {
        dataHomeViewPart: true,
        position: 'absolute',
        top: '0',
        left: '0',
        right: '0',
        zIndex: '2'
      },

      // THE TABS' OWN SCREENS (components/HomeTabStart.js, functions/
      // homeTabStart.js; Nika 2026-10-23: "draw related experience for each
      // tab aligning AI mode"): Present's before a deck. It gates itself;
      // while it shows it is the view part in the flow (it has no box
      // otherwise).
      HomeTabStart_present: {
        tab: 'present',
        dataHomeViewPart: true
      },

      // THE BUILD TAB IS THE APP BUILDER (components/HomeBuildView.js,
      // functions/homeBuild.js; Nika 2026-10-23: "build tab should reuse
      // ui/ux of /add-apps"): the /add-app page itself in this area — the
      // build conversation, the canvas, the ready-made and the person's apps.
      // It gates and crossfades itself, in place (Nika 2026-10-23: "transit
      // page to canvas without switching the page or layout"): closed it lies
      // over this area out of the flow, like the views above; while it shows
      // the board stylesheet puts it in the flow.
      HomeBuildView: {
        dataHomeViewPart: true,
        position: 'absolute',
        top: '0',
        left: '0',
        right: '0',
        zIndex: '2'
      }
    },

    // THE END SENTINEL (DESIGN-WL-HOME-SKY-TOP-GROUND-1): the last in-flow
    // child of the home scroller, zero tall, its negative top margin equal to
    // the column's gap, so it adds no layout at all (the scroll height is
    // unchanged). Only the fallback reads it: a browser without CSS
    // scroll-driven animations watches it (ambientGroundWatch) and raises the
    // ambient ground at the end of the scroll; everywhere else the observer
    // is never made.
    HomeGroundMark: {
      aria: { hidden: 'true' },
      height: '0',
      marginTop: '-B',
      pointerEvents: 'none',
      flexShrink: '0',
      '@mobileL': { marginTop: '-B' },
      onRender: (el) => el.call('ambientGroundWatch', el),
      onRemove: (el) => el.call('ambientGroundUnwatch', el)
    },

    // Context-menu tip — a dismissible CanvasHelpCard floating bottom-right of
    // the dashboard, surfacing the right-click menus (the empty-area
    // [Refresh, Customize] menu + per-widget menu added 2026-06-29). Persisted
    // dismiss via _homeCtxTipDismissed; hidden in customize mode so it only
    // shows on the plain widget dashboard.
    //
    // ONCE, NEVER OVER CONTENT, WITH THE KEYBOARD WAY (persona audit WH-9,
    // functions/homeCtxTip.js): it shows only on the plain board (no window,
    // assistant, main thread, welcome or customize mode over it), it is
    // dismissed for good after a few seconds SEEN, and its words name Shift+F10
    // — which the board answers (MainColumn onKeydown).
    CtxMenuTip: {
      extends: 'CanvasHelpCard',
      dismissKey: '_homeCtxTipDismissed',
      position: 'fixed',
      // Its own fixed bottom replaces CanvasHelpCard's: the same call-dock
      // clearance (the shell's --calldock-clear) over its own C.
      bottom: 'max(var(--spacing-C), calc(var(--calldock-clear, 0%) + var(--spacing-A)))',
      // Above the EdgeFadeBottom scrim (zIndex 55) so the tip isn't dimmed by
      // the bottom shadow band; still under the z60 composer. (2026-07-03 per
      // Nika "CtxMenuTip should be above shadow".)
      zIndex: '56',
      // Touch now has the long-press → contextmenu shim armed on MainColumn,
      // so the tip is worth showing there too — with touch wording (below)
      // instead of being hidden as it was when the menus were mouse-only.
      //
      // Mounted only while it can show: `if:`, not `hide:` (2026-10-31). A
      // `hide:` tip is still rendered, and so are its children: the shared
      // card's Lottie loads lottie-web and swipe-right.json in its onRender,
      // so every home boot paid ~1.5 s of loading for a tip that was not on
      // screen (dismissed long ago, or under a window). The tip is fixed, out
      // of the flow, so its mount moves nothing (Rule 66). A re-show
      // re-attaches the built node: the Lottie loads once, and the tip's own
      // onRender / onRemove below still drive the once-timer.
      if: (el, s) => el.call('homeCtxTipVisible', s.root),
      // The once-timer follows what the person can SEE (hidden time is not
      // counted): started at mount when visible, paused and resumed as layers
      // open and close over the board, dropped with the tip.
      stateDeps: [(el, s) => el.call('homeCtxTipVisible', s.root)],
      onStateUpdate: (el, s, ctx, { next }) => el.call('homeCtxTipTick', el, !!next[0]),
      onRender: (el, s) => el.call('homeCtxTipTick', el, el.call('homeCtxTipVisible', s.root)),
      onRemove: (el) => el.call('homeCtxTipTick', el, false),
      // CanvasHelpCard's swipe-right Lottie is baked white — invisible on this
      // card's light-theme surface (CanvasHelpCard always renders on a dark
      // canvas elsewhere, so the asset never needed to survive a light card
      // before). `difference` blend reads the mark against whatever the card
      // background actually is instead of hardcoding a per-theme color, so it
      // stays legible in both themes without touching the shared asset or
      // component (same technique already used for surface-adaptive marks in
      // ComponentThumbnailUI / Widgets).
      Lottie: { mixBlendMode: 'difference' },
      TipTitle: {
        tag: 'span',
        // Adaptive ink, NOT a fixed literal. This was `color: 'black'`, which
        // measured 1.08:1 in dark theme — black rgb(0,0,0) on the card's
        // rgb(14,14,15) glass, i.e. invisible (reported by Nika 2026-08-10,
        // "some ui that shows dark on dark"). CanvasHelpCard carries
        // `theme: 'canvas-card-glass'`, so its surface follows the theme; the
        // sibling TipLine1/TipLine2 never broke precisely because they set no
        // colour and inherit the theme's adaptive ink. `title` is the same
        // adaptive token the house menus use (ContextDropdown rows).
        color: 'title',
        fontWeight: '700',
        // Same affordance, per-input wording: long-press drives the synthetic
        // contextmenu on touch (armLongPress on MainColumn), right-click on mouse.
        text: (el) =>
          el.call(
            'polyglot',
            el.call('isCoarsePointer') ? 'workspace.home.tipTouch' : 'workspace.home.tipMouse'
          )
      },
      TipLine1: { tag: 'span', text: '{{ workspace.home.tip1 | polyglot }}' },
      TipLine2: { tag: 'span', text: '{{ workspace.home.tip2 | polyglot }}' }
    },

    // The widget context menu's "Add color" palette (2026-09-02). Mounted on
    // the HOME page, not in the shell — it is a home-board surface and the
    // board owns both its trigger (`_homeColorPicker`) and its write path
    // (`_homeOps.setWidgetColor`). Self-hiding on `root._homeColorPicker`, so
    // a bare mount costs one hidden node.
    HomeWidgetColorPicker: {},

    // INTERACT-1: backdrop for the drawer below — clicking anywhere outside
    // the panel closes it, same transparent pointer-catching sibling shape
    // as the house ContextDropdown's own Backdrop (shared/components/
    // ContextDropdown.js: position fixed/absolute + inset:0 + onPointerdown
    // clears the open flag). Declared BEFORE CustomizeDrawer (paints under
    // it in source order) and pinned one below its zIndex so there's no
    // ambiguity either way.
    CustomizeDrawerBackdrop: {
      hide: (el, s) => !s.root?._homeCustomizeMode,
      position: 'fixed',
      inset: '0',
      zIndex: '1199',
      // `pointerEvents: none` is load-bearing, NOT a style tweak. This layer is
      // `inset: 0` — it covers the WHOLE viewport, and that includes the very
      // dashboard customize mode exists to edit. While it ate pointer events it
      // caused both halves of one bug (Nika 2026-08-15/16): the resize handles
      // and each card's × were unreachable because the backdrop sat on top of
      // them, AND any single click anywhere on the board hit this element's
      // onPointerdown and switched customize mode straight back off. Verified
      // live before the fix: in customize mode `document.elementFromPoint` over
      // a widget's centre AND over its south-east resize corner both returned
      // this node (`_pos-fixed _z-1199`), not the widget.
      //
      // The dismissal itself is still correct and still wanted, so it moves to
      // a document listener that can see the REAL target and skip the two
      // regions that must stay interactive: the grid, and the drawer above.
      pointerEvents: 'none',
      // Outside-press dismissal for customize mode. A DOCUMENT-level listener:
      // this layer is `pointerEvents: none` on purpose (see above), so the
      // press never reaches its own node and only the document can see the
      // REAL target.
      //
      // It used to be a raw `document.addEventListener('pointerdown', …)`
      // fired from `onInit` behind a `ctx.__customizeBackdropBound` app-scope
      // flag, with an ANONYMOUS handler — no reference was kept anywhere, so
      // nothing could remove it even in principle (FA503). Declared, DOMQL
      // registers it once and disposes it; the context flag is gone with it.
      //
      // The backdrop uses `hide:` (display:none, the node stays CONNECTED),
      // so the declared listener stays armed exactly as the raw one did.
      onDocumentPointerdown: (e, el) => {
        const root = el.state?.root
        if (!root || !root.update || !root._homeCustomizeMode) return
        // The decision — which regions are NOT "outside" — lives in
        // functions/homeCustomizeDismiss.js as a pure function, so it is
        // unit-testable by BEHAVIOUR (a press on the peek keeps the mode)
        // rather than by a regex over this file's source text. The three
        // excluded regions are:
        //   .home-grid                      the board customize mode edits
        //   [data-home-customize-drawer]    the drawer itself
        //   [data-home-customize-peek]      the tucked-drawer recall handle
        //
        // The peek handle (UI-CUSTOMIZE-SIDEBAR-REOPEN-1 layer 3) mounts
        // OUTSIDE the drawer BY DESIGN (the drawer translates off-viewport
        // and would carry it along), which put it outside both other
        // exclusions — so pointerdown here dismissed customize mode BEFORE
        // the peek's own click could untuck (pointerdown precedes click;
        // qa: 0-of-3 clicks reopened, mode flipped true→false). The one
        // affordance that exists to undo the tuck must never itself be read
        // as "clicked outside".
        // ⚠️ The list is REGRESSION-PINNED twice: as a SET plus the
        // peek's marker in __tests__/zeroSizeShipped.test.js, and by
        // OUTCOME in __tests__/homeCustomizePeekReopen.test.js. An entry
        // silently dropping out is how this ticket returns.
        const { dismiss } = customizeDismissDecision(e.target, root._homeCustomizeMode)
        if (!dismiss) return
        // S7: the exit patch clears the slide-tuck too, so a later re-open
        // never starts invisibly off-canvas from a state left over from
        // last time.
        root.update({ ...CUSTOMIZE_EXIT_PATCH })
      }
    },

    // Right-side customize drawer — slides in when the FloatingCustomizeBtn
    // (above) toggles `_homeCustomizeMode`. Lists every widget the user
    // has hidden (× from each card's RemoveBtn), click any entry to
    // restore it. Reset link at the bottom keeps the previous "Reset to
    // defaults" affordance reachable.
    // UI-CUSTOMIZE-SIDEBAR-REOPEN-1 (Nika: "when customize sidebar
    // dissapears it should leave button to toggle it back"; qa triage
    // 2026-08-18): the tuck translates CustomizeDrawer +120% off-viewport
    // and DrawerSlideToggle rides INSIDE the translating container —
    // customize mode stayed on with the way back rendered off-screen. The
    // untuck affordance must live OUTSIDE the translated element: a peek
    // handle pinned to the right edge, visible ONLY while tucked, same
    // z-band as the drawer it recalls. ae7a26e58 (the tuck ANIMATION fix)
    // is untouched — this is the next problem, not a regression of it.
    CustomizeDrawerPeek: {
      extends: 'IconButton',
      class: 'gs-no-drag',
      // Layer-3 marker (UI-CUSTOMIZE-SIDEBAR-REOPEN-1): the document-level
      // customize dismisser excludes this region by attribute — without it,
      // pointerdown on the peek dismissed customize mode before the click
      // could untuck (see the dismisser's own comment in
      // CustomizeDrawerBackdrop.onInit above). Pinned as a PAIR with that
      // exclusion list in __tests__/zeroSizeShipped.test.js.
      attr: { 'data-home-customize-peek': '' },
      // ZERO-SIZE-SHIPPED-1 #1: subscribeTo is REQUIRED, and listing only one
      // key would not be enough — this hide SHORT-CIRCUITS (`!mode ||
      // !tucked`), so at mount, with customize mode off, the factory returns
      // before `_homeCustomizeDrawerTucked` is ever read and the dep tracker
      // never learns about it (factory deps are control-flow dependent). The
      // tuck flip then never re-evaluated hide, the peek stayed display:none
      // forever, and qa measured it "mounted, 0×0" — present to code, absent
      // to the user. Same idiom as the studios AppSidebar: name
      // every gate key explicitly.
      subscribeTo: ['_homeCustomizeMode', '_homeCustomizeDrawerTucked'],
      hide: (el, s) => !s.root?._homeCustomizeMode || !s.root?._homeCustomizeDrawerTucked,
      position: 'fixed',
      right: 'X2',
      // Vertically centered in the drawer's own band (--shell-top .. D1),
      // not the raw viewport, so it reads as the drawer peeking back in.
      top: '50%',
      transform: 'translateY(-50%)',
      zIndex: '1200',
      // The phone sheet tucks to its own bar (handle + header) and comes back
      // from there — no side peek on a phone.
      '@mobileL': { hide: true },
      title: (el) => el.call('polyglot', 'workspace.customize.showPanel'),
      aria: { label: (el) => el.call('polyglot', 'workspace.customize.showPanel') },
      Icon: { name: 'chevronLeft', boxSize: 'Z' },
      onClick: (e, el, s) => {
        e.stopPropagation()
        s.root.update({ _homeCustomizeDrawerTucked: false })
      }
    },

    CustomizeDrawer: {
      // Built on the FIRST customize open, not in the boot task: its
      // HomePersonalize tree cost 0.44-0.5 s of every cold home mount while
      // hidden (mount profile, dev deploy 31). One-way latch - once built,
      // `hide:` below owns visibility as before (no re-create on close, no
      // lost scroll). Its components stay registered eagerly, so the latch
      // defers only the BUILD (same pattern as app.js _latchOnce).
      if: (el, s) => {
        if (el.__customizeDrawerBuilt) return true
        if (!s.root?._homeCustomizeMode) return false
        el.__customizeDrawerBuilt = true
        return true
      },
      hide: (el, s) => !s.root?._homeCustomizeMode,
      // Marks the drawer for CustomizeDrawerBackdrop's outside-pointerdown
      // test above — a pointerdown in here restores a widget or resets the
      // layout, and must not be read as "clicked outside, close".
      // `data-tucked` carries the slide-away state to the CSS (the side drawer
      // slides off to the right, the phone sheet collapses to its bar) — see
      // the tuck rules below.
      attr: {
        'data-home-customize-drawer': '',
        'data-tucked': (el, s) => (s.root?._homeCustomizeDrawerTucked ? 'true' : null)
      },
      // INTERACT-1: Esc-to-close. A document-level listener, not a
      // declarative onKeydown — the drawer opens from a context-menu click
      // (or the navbar dropdown), neither of which parks DOM focus inside
      // this panel, so an element-scoped keydown handler would never see
      // the Escape keystroke (the exact bug class this ticket also found on
      // the Home right-click ContextDropdown — that one lives in
      // shared/components/ContextDropdown.js, a shared-library file this
      // lane may not edit; documented separately, not worked around here).
      // It used to be a raw `document.addEventListener('keydown', …)` fired
      // from `onInit` behind a `ctx.__customizeDrawerEscBound` app-scope flag,
      // with an ANONYMOUS handler — nothing could ever remove it (FA503).
      // Declared, DOMQL registers it once and disposes it; the flag is gone.
      //
      // The drawer uses `hide:` (display:none, the node stays CONNECTED), so
      // the listener stays armed exactly as the raw one did — and the handler
      // still self-guards on LIVE state rather than assuming it is open.
      onDocumentKeydown: (e, el) => {
        // A handled Escape closed something on top (a menu, a palette) — the
        // board stays in edit mode (HOME_AMBIENT_SKIN_SPEC §5: top overlay first).
        if (e.key !== 'Escape' || e.defaultPrevented) return
        const root = el.state?.root
        if (!root || !root.update || !root._homeCustomizeMode) return
        // A dialog or a dropdown ABOVE the drawer owns this Escape (the Reset
        // layout confirm is the everyday case): the drawer does nothing and
        // leaves the key unspent for it — one Escape, one layer.
        if (typeof el.call === 'function' && el.call('shellOverlayOpen')) return
        e.preventDefault()
        root.update({ _homeCustomizeMode: false, _homeCustomizeDrawerTucked: false })
      },
      // Exact chat-drawer chrome (2026-06-10 per Nika "CustomizeDrawer should
      // have exact styles, position and theming as chatdrawer"): the same
      // floating glass card the MessengerOverlay list uses (canvas-card-glass,
      // X2 edge inset, --shell-top top / D1 bottom, G1 width, round A) — mirrored
      // to the RIGHT edge. Top tracks the shared --shell-top baseline so it lines
      // up with the messenger + every canvas rail. (2026-07-08 per Nika "sidebar
      // positions consistent on all pages".)
      position: 'fixed',
      right: 'X2',
      top: '--shell-top',
      bottom: 'D1',
      width: 'G1',
      minWidth: 'G1',
      maxWidth: 'G1',
      zIndex: '1200',
      theme: 'canvas-card-glass',
      borderWidth: '0',
      round: 'radiusCard',
      padding: 'A',
      overflow: 'auto',
      // Drawer scroller: edge-bounce must not chain to the dashboard behind.
      overscrollBehavior: 'contain',
      flow: 'y',
      gap: 'B',
      shadow: 'float',
      animation: 'fadeInUpShort',
      animationDuration: 'C',
      // BF-CUSTOMIZE-DRAWER-TOGGLE-1 (Nika 2026-08-16 "hiding toggle does not
      // work"): the framework's `animation` prop defaults
      // `animation-fill-mode` to `both` (css-in-props props/animation.js), so
      // once fadeInUpShort finished, its `to` keyframe
      // (`transform: translate3d(0,0,0)`) stayed applied FOREVER — and a
      // running-or-filled animation outranks every normal declaration in the
      // cascade, class or inline. DrawerSlideToggle then flipped the flag and
      // the chevron on every click while the `transform:` factory below wrote
      // `translateX(120%)` into a declaration the animation kept overriding:
      // flag true, computed transform still `matrix(1,0,0,1,0,0)`. `backwards`
      // keeps the entrance exactly as it was (the `from` state before the first
      // frame) and RELEASES the element to its own transform the moment the
      // animation ends — the `to` keyframe equals the declared resting state,
      // so nothing visibly changes at that hand-off.
      animationFillMode: 'backwards',
      // S7 (NIKA BATCH 2026-08-16) — slide-away toggle, separate from
      // `_homeCustomizeMode` (which mounts/unmounts this whole panel via
      // `hide:` above): `_homeCustomizeDrawerTucked` stays true only while
      // still IN customize mode, so DrawerSlideToggle below can push the
      // panel fully clear of the viewport and pull it back without exiting
      // customize mode. 120% (not 100%) clears the panel past the edge
      // regardless of its own resolved width — no token needed, unlike
      // `right: X2` above, which this doesn't need to know the value of.
      // A STATIC rule on `data-tucked` (attr above), not a transform factory:
      // a factory writes the value INLINE, and an inline transform outranks
      // every `@mobileL` rule — the phone sheet below must not slide sideways.
      '&[data-tucked]': { transform: 'translateX(120%)' },
      transition: '160ms defaultBezier transform',
      // THE PHONE SHEET (DESIGN-SHELL-RESPONSIVE-IOS-SAFARI-1; orchestrator
      // decision 2026-10-06). The phone posture used to be the side drawer
      // stretched over the whole screen (inset A on all four sides): edit mode
      // opened over the very board it edits — measured on dev 586122071 at
      // 393, the drawer 16..377 x 16..836 and 0 of 20 widget size segments
      // reachable until the drawer was tucked. On a phone it is a BOTTOM SHEET
      // now: about half the viewport, docked on the bottom edge, top corners
      // round, a grab handle on top (SheetHandle) — the board stays visible
      // and scrollable above it (the board's own bottom room in edit mode:
      // workspace/app.js, `[data-customizing="open"]`). Its last rows clear
      // the composer and the home indicator: `--slot-bottom` is the composer's
      // live top plus the gap and already folds in env(safe-area-inset-bottom);
      // the max() keeps the safe area when no composer is on screen.
      // Tucked, the sheet collapses to its handle and header (the header's
      // toggle and the handle bring it back) instead of sliding sideways.
      // iPad (above 768px) and desktop keep the side drawer.
      '@mobileL': {
        left: '0',
        right: '0',
        top: 'auto',
        bottom: '0',
        width: 'auto',
        minWidth: '0',
        maxWidth: 'none',
        height: '50dvh',
        borderBottomLeftRadius: '0',
        borderBottomRightRadius: '0',
        padding: 'Z1',
        paddingTop: '0',
        paddingBottom:
          'max(calc(var(--spacing-Z1) + env(safe-area-inset-bottom)), var(--slot-bottom, var(--spacing-Z1)))',
        gap: 'A',
        '& > [data-key="SheetHandle"]': { display: 'flex' },
        '&[data-tucked]': { transform: 'none', height: 'auto' },
        '&[data-tucked] > :not([data-key="SheetHandle"]):not([data-key="DrawerHeader"])': {
          display: 'none'
        }
      },

      // The phone sheet's grab handle: the whole 44pt-tall top band answers a
      // tap — tuck the sheet down to its bar, or bring it back up. Pointer
      // only (aria-hidden): DrawerSlideToggle in the header is the same
      // control for the keyboard and for assistive tech. Shown only in the
      // phone posture (the `@mobileL` rule above).
      SheetHandle: {
        class: 'gs-no-drag',
        display: 'none',
        flexShrink: '0',
        alignItems: 'center',
        justifyContent: 'center',
        width: '100%',
        minHeight: 'controlH',
        cursor: 'pointer',
        'aria-hidden': 'true',
        onClick: (e, el, s) => {
          e.stopPropagation()
          s.root.update({ _homeCustomizeDrawerTucked: !s.root?._homeCustomizeDrawerTucked })
        },
        Grip: {
          tag: 'span',
          display: 'block',
          width: 'B1',
          height: 'X',
          round: 'radiusPill',
          background: 'caption',
          opacity: '.4'
        }
      },

      DrawerHeader: {
        flow: 'x',
        align: 'center space-between',

        DrawerTitle: {
          tag: 'h3',
          margin: '0',
          fontSize: 'A',
          fontWeight: '700',
          // Adaptive ink, NOT a fixed literal — the geex craft pass swept this
          // to 'black' along with the postered widget bodies, but the drawer
          // sits on adaptive canvas-card-glass, so dark theme rendered black
          // on black (same class of bug as TipTitle above).
          color: 'title',
          text: '{{ workspace.customize.title | polyglot }}'
        },
        // S7 + house close grouped together on the right, Title left, via
        // the same `align: 'center space-between'` DrawerHeader already
        // declares — a bare 3rd child would space-between across all three
        // instead of grouping the two buttons.
        HeaderActions: {
          flow: 'x',
          align: 'center',
          gap: 'V',

          // Slide the panel out of the way and back, staying IN customize
          // mode (unlike DrawerClose below, which exits it). Same
          // directional chevron-flip convention as canvas/components/
          // PageSlot.js's Back/Forward header buttons.
          DrawerSlideToggle: {
            extends: 'IconButton',
            class: 'gs-no-drag',
            title: (el, s) =>
              el.call(
                'polyglot',
                s.root?._homeCustomizeDrawerTucked
                  ? 'workspace.customize.showPanel'
                  : 'workspace.customize.slideAway'
              ),
            aria: {
              label: (el, s) =>
                el.call(
                  'polyglot',
                  s.root?._homeCustomizeDrawerTucked
                    ? 'workspace.customize.showPanel'
                    : 'workspace.customize.slideAway'
                )
            },
            Icon: {
              name: (el, s) =>
                s.root?._homeCustomizeDrawerTucked ? 'chevronLeft' : 'chevronRight',
              boxSize: 'Z',
              // The phone sheet tucks DOWN and comes back UP: a quarter turn
              // makes the same two chevrons read down (open) and up (tucked).
              '@mobileL': { transform: 'rotate(90deg)' }
            },
            onClick: (e, el, s) => {
              e.stopPropagation()
              s.root.update({ _homeCustomizeDrawerTucked: !s.root?._homeCustomizeDrawerTucked })
            }
          },

          // House drawer-header close — THE brand CloseButton (F2
          // 2026-08-16, one X for every sidebar, drawer and modal): 36px
          // circle, 16px glyph, hover wash. INTERACT-1's tap-target concern
          // is met by the IconButton base's coarse-pointer 44pt tap slop,
          // not by a bigger visible box.
          DrawerClose: {
            extends: 'CloseButton',
            class: 'gs-no-drag',
            title: (el) => el.call('polyglot', 'workspace.customize.close'),
            aria: { label: (el) => el.call('polyglot', 'workspace.customize.close') },
            onClick: (e, el, s) => {
              e.stopPropagation()
              s.root.update({ _homeCustomizeMode: false, _homeCustomizeDrawerTucked: false })
            }
          }
        }
      },

      // PERSONALISE (DESIGN-WL-HOME-TOP-STRIP-SUMMARY-1, Nika's pick row 4 A:
      // one panel, grouped) — the App strip, the theme, the background and the
      // summary style, above this drawer's own widget list
      // (components/HomePersonalize.js). Every row is a live decision: the
      // board behind the drawer is the preview.
      HomePersonalize: {
        HomePersonalizeGroup_strip: {
          AppsList: { children: (el, s) => _personalizeAppRows(s.root) }
        }
      },

      // The widgets' group: its caption, then the hint and the hidden list.
      CaptionTitle_widgets: {
        fontSize: 'Y2',
        margin: '0',
        align: 'center flex-start',
        Text: { text: '{{ workspace.personalize.widgets | polyglot }}' }
      },

      DrawerHint: {
        tag: 'p',
        margin: '0',
        fontSize: 'Y',
        color: 'caption',
        lineHeight: '1.5',
        text: (el, s) =>
          el.call(
            'polyglot',
            _drawerHiddenRows(s.root).length === 0
              ? 'workspace.customize.hintNoneHidden'
              : 'workspace.customize.hintSomeHidden'
          )
      },

      HiddenSection: {
        hide: (el, s) => _drawerHiddenRows(s.root).length === 0,
        flow: 'y',
        gap: 'V',

        // Shared CaptionTitle (2026-06-10 per Nika "CaptionTitle component for
        // caption titles; also match Customize sidebar from / page") — the
        // FilesSidebar / TicketsSidebar / chat rails already render their
        // section captions through this built-in; the home Customize drawer
        // was the last holdout still hand-rolling an inline uppercase span.
        // Same usage shape as those overlays (fontSize Y2 + a Text child).
        CaptionTitle: {
          fontSize: 'Y2',
          Text: { text: '{{ workspace.customize.hidden | polyglot }}' }
        },

        HiddenList: {
          flow: 'y',
          gap: 'V',
          childrenAs: 'state',
          children: (el, s) => _drawerHiddenRows(s.root),
          childExtends: _customizeDrawerItem
        }
      },

      // Reset action stays available from the drawer — same behaviour
      // as the old bottom CustomizeBar: clear workspace-scoped local
      // cache + drop `homeGridLayout` from server prefs, then reload.
      ResetBtn: {
        tag: 'button',
        type: 'button',
        class: 'gs-no-drag',
        marginTop: 'A',
        minHeight: 'controlH',
        padding: '0 A',
        round: 'radiusPill',
        // Fill-contrast separation (borderless law) — the old white.12 stroke
        // + white.06 hover were raw literals that vanished in light theme.
        background: 'overlay',
        border: 'none',
        color: 'caption',
        fontSize: 'Z2',
        cursor: 'pointer',
        textAlign: 'center',
        transition: '120ms ease background',
        ':hover': {
          background: 'hover'
        },
        text: '{{ workspace.customize.reset | polyglot }}',
        onClick: (e, el, s) => {
          e.preventDefault()
          // RESET-LAYOUT-1: this reloads the page and discards every manual
          // resize/drag with no way back — confirm before doing it (it was a
          // silent, unconfirmed destructive action behind the reload).
          el.call('confirmDialog', {
            title: el.call('polyglot', 'workspace.customize.resetConfirmTitle'),
            message: el.call('polyglot', 'workspace.customize.resetConfirmMessage'),
            confirmText: el.call('polyglot', 'common.action.reset'),
            cancelText: el.call('polyglot', 'common.action.cancel'),
            // The house confirm (CRUD standard D1): the primary button, no icon.
            confirmTheme: 'primary',
            icon: null,
            onConfirm: async () => {
              // Cancel any drag/resize save still debouncing (see
              // _cancelPendingPrefsSave) BEFORE anything else, so a stale
              // pre-reset patch can never flush over this reset's own write.
              _cancelPendingPrefsSave()
              try {
                // Clear every home-scoped key — the layout, the outbox AND the
                // hidden-widget set — so reset truly restores defaults. (The
                // hiddenWidgets key was surviving, silently keeping the user's
                // last hidden set across a "reset".)
                Object.keys(localStorage)
                  .filter((k) => k.startsWith('symbols.home.'))
                  .forEach((k) => localStorage.removeItem(k))
              } catch (_) {
                /* ignore */
              }
              const root = s.root
              if (root?.currentUserId && el.sdk?.execute) {
                // Clear the layout AND every widget choice on the server
                // profile, stamping the current version: `grid_layout: null`
                // and `hidden_widgets: {}` say "no choices", and the board
                // composes the workspace's own defaults over that — its panel
                // set, app mode (the installed module's widgets alone, Nika
                // 2026-09-10) and the installed apps' default widgets
                // (`_resolveBootHidden`). The same answer the next boot reads,
                // on this device and every other.
                //
                // NEVER a stamped copy of today's defaults
                // (DESIGN-WL-HOME-DEFAULTS-ALL-WORKSPACES-1). This payload used
                // to write an explicit boolean for EVERY catalog id — the
                // global default of that day. A stored default outranks every
                // later default for that member, so the next default change
                // would never have reached anybody who ever pressed Reset; and
                // it ignored the workspace's panel set, so a Reset on a
                // configured workspace hid the panels its admin had turned on.
                // The two reasons it was dense are gone: the read composes
                // `{ ...base, ...saved }`, so `{}` IS the defaults (it no
                // longer un-hides the catalog), and the server REPLACES
                // `hidden_widgets` whole (PrefsService, since 14079239), so no
                // stale per-id key survives an empty write. The module
                // console's reset (mcResetPersonalization) already wrote `{}`.
                const next = {
                  grid_layout: null,
                  hidden_widgets: {},
                  dashboard_v: CURRENT_DASHBOARD_V
                }
                // RESET-LAYOUT-1: this session's own gestures are forgotten
                // first (_forgetHomeGestures), or a pre-reset drag or size
                // pick would outrank this block's own `grid_layout: null` and
                // silently undo the reset.
                // WS-HOME-RESET-LAYOUT-INPLACE-1: the OPTIMISTIC local row must
                // be SCOPE-SHAPED, not a bare copy of the server payload.
                // `_prefsRowForScope` accepts a prefs object only when its
                // `workspace_id` matches the live `_wsScope(root)`, and BOTH
                // hidden-widget resolvers read the saved map through it. A bare
                // `next` (no workspace_id) reads as "no row for this
                // workspace", so the board's reconcile re-derives the generic
                // workspace default and UN-HIDES exactly the widgets this reset
                // just hid — measured live on system/hq: the reset's own
                // 34-key map landed on the server, the board kept all 33
                // widgets on screen, and only a reload agreed with the write.
                // The server payload stays `next`: workspace_id / user_id are
                // the server's to derive, never the client's to send.
                const prevPrefs = root.userDashboardPrefs || {}
                const nextLocal = {
                  ...next,
                  workspace_id: prevPrefs.workspace_id || _wsScope(root),
                  user_id: prevPrefs.user_id
                }
                // The defaults win over this session's own gestures from here on.
                _forgetHomeGestures()
                root.update({ userDashboardPrefs: nextLocal, _homeCustomizeMode: false })
                // The board from here: the ONE hidden-map resolver over the
                // seeded row — the workspace's defaults composed over no
                // choices, exactly what the next boot reads.
                root.update({ _homeHiddenWidgets: _resolveBootHidden(root).hidden })
                try {
                  await el.sdk.execute('workspaceProject.homeDashboardPrefs', 'update', {
                    payload: next
                  })
                } catch (err) {
                  console.warn('[home] reset dashboard prefs failed:', err && err.message)
                  // RESET-LAYOUT-2: a failed write used to fall straight through
                  // to the reload below — which re-read the SERVER's UNTOUCHED
                  // prefs and restored the exact arrangement the user had just
                  // asked to discard. So a FAILED reset was indistinguishable
                  // from a successful one: the page churned through a full boot
                  // and everything came back, with the only trace a console
                  // warning nobody sees. That is the reported "net effect of
                  // clicking Reset: nothing is reset".
                  //
                  // Never reload over a failed write. Say so instead, and keep
                  // the optimistic local reset on screen so the user can see
                  // what WOULD have been applied and retry — a reload here
                  // would silently revert that too.
                  el.call('alertDialog', {
                    title: el.call('polyglot', 'workspace.customize.resetFailed'),
                    message: el.call('polyglot', 'workspace.customize.resetFailedMessage')
                  })
                  return
                }
              }
              // WS-HOME-RESET-LAYOUT-INPLACE-1: restore in place. This used
              // to be `window.location.reload()` — and the reload was not
              // laziness, it WAS the restore mechanism: nothing at runtime
              // knew a widget's default cell. `_SPEC_GEOMETRY` knows them now,
              // and the CSS grid reflows from the re-resolved order at once,
              // so a reset no longer costs the whole JS heap, the scroll
              // position and every open panel.
              if (!_resetLayoutInPlace(root)) {
                // No root to re-resolve on (the page is gone). The server
                // prefs are already reset; the next board mount resolves them.
                console.warn('[home] reset: no board root — prefs reset, board not re-laid')
              }
            }
          })
        }
      }
    }

    // (Removed: SearchOverlay scaffolding. The placeholder was never
    // wired to any caller — no surface set `_searchOverlayPath`, so it
    // could never become visible — AND its inner `Content` used a
    // `content: (el, s) => ({ extends: path })` factory that DOMQL v3.14
    // doesn't actually instantiate on state change. When the search
    // affordance is wired up, re-add it with the canonical pre-mount
    // pattern used by AppWindow below: list the page child statically
    // with a `hide:` rule. State field `_searchOverlayPath` removed
    // from state-app.js as part of the same cleanup.)

    // (BottomPrompt moved to the shell root — workspace/app.js — on
    // 2026-06-06 per Nika so the AI prompt shows on EVERY page, not
    // just home.)
  }

  // (Moved 2026-09-26: the CreateAgentModal page-mount went to the shell
  // root — workspace/app.js — as the single instance. The "Your agents"
  // create tile and the header + keep just SETTING `_createAgentOpen`. A page
  // mount here doubled it whenever /agents opened as a window over home.)

  // (Removed 2026-08-06: the TicketsDetailModal page-mount moved to the
  // shell root — workspace/app.js — as the single instance for EVERY
  // route. The dashboard's Received/Given task rows keep just SETTING
  // `selectedTicket`; the shell instance flips visible reactively. The
  // 2026-06-08 perf history — windows' Slot.set re-preparing the
  // ~4,000-line def froze the thread 15-40s per open — lives with the
  // shell mount's comment; framework fix ticketed in tickets/smbls.md.
  //
  // The event modal (CalEventDetailModal) stays a windows-body component
  // (no Modal/stateKey gate, ~650 lines — the freeze is super-linear in
  // size, so small defs are fine on the windows path) opened via
  // openEventDetailModal.)
}
